import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PizzaSaleModeSelector from "../menu/PizzaSaleModeSelector";
import CatalogImage from "../common/CatalogImage";
import { getDefaultPizzaSaleMode, getPizzaStartingPrice, getPizzaSizePriceSummary, getPizzaPortionScale, validatePizzaCartItems } from "../../services/local/pizzaHalfAndHalf";
import { getLinkedOnlyProductIds, indexLinkedRulesByProduct, resolveLinkedRuleProducts, isIngredientLinkedRule } from "../../services/menu/linkedRules";
import { resolveBeverageSelection, withoutBeverageVarietyIngredients } from "../../services/menu/beverageRelations";
import { resolveOrderPromotionPayment, promotionPaymentNotice } from "../../services/supabase/edge-functions/_shared/promotionPayments";
import QRCode from "react-qr-code";
import { useAuth } from "../context/AuthContext";
import mozzoWaiterDebugApkUrl from "../../android/mozzo-kiosk/waiter-app/build/outputs/apk/debug/waiter-app-debug.apk";
import mozzoWaiterReleaseApkUrl from "../../android/mozzo-kiosk/waiter-app/build/outputs/apk/release/waiter-app-release.apk";
import { listBranchTablesWithQrs } from "../../services/supabase/qrService";
import { loadLocalMenuSnapshot } from "../../services/local/menuStorage";
import { getBranchMenuSnapshot } from "../../services/supabase/menuService";
import { listIngredientes } from "../../services/supabase/ingredientesService";
import { isSupabaseConfigured as isSupabaseRuntimeConfigured } from "../../services/supabase/config";
import {
  claimSharedOperationalOrderPayment,
  hydrateSharedOperationalStateToLocal,
  pushSharedOperationalState,
  pushSharedPaymentOperationsState,
  pushSharedWaiterOperationsState,
} from "../../services/supabase/operationalStateService";
import {
  buildPromotionPricing,
  getPromotionRecordType,
  isPromotionAvailableNow,
} from "../../services/promotions/promotionEngine";
import { hydrateBranchPromotionsToLocal } from "../../services/supabase/promotionsService";
import {
  buildPromotionsStorageKey,
  getPromotions,
  PROMOTIONS_UPDATED_EVENT,
} from "../../services/local/promotions";
import {
  buildOperationalOrdersStorageKey,
  getBlockingSalonReservationForTable,
  getOperationalOrdersState,
  getSalonReservationWindow,
  getSalonReservationsForTable,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
  saveOperationalOrdersState,
} from "../../services/local/operationalOrders";
import {
  getPaymentOperations,
  isPaymentOperationConfirmed,
  resolveOperationalOrderPaymentState,
  savePaymentOperations,
} from "../../services/local/paymentOperations";
import { resolveBranchCommissionRateByChannel } from "../../services/local/paymentSettings";
import {
  buildPaymentRailSnapshot,
  isPaymentRailEnabled,
  PAYMENT_RAILS,
} from "../../services/payments/paymentRails";
import { createBranchDigitalPaymentIntent } from "../../services/payments/paymentIntents";
import {
  cancelInPersonQrPaymentIntent,
  getDigitalPaymentIntent,
} from "../../services/supabase/kioskDigitalPaymentsService";
import {
  finalizeApprovedDigitalCommission,
  syncManualCommissionDebtForOperation,
} from "../../services/supabase/commissionLedgerService";
import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "../../services/local/menuIngredientModel";
import {
  buildOperationalItemLabel,
  buildOperationalConsumptions,
  createOperationalOrderItem,
  normalizeOperationalOrderItems,
  scaleOperationalConsumptions,
} from "../../services/local/orderModel";
import {
  buildPizzaHalfAndHalfOptions,
  buildPizzaHalfAndHalfSelectionDetails,
  createPizzaHalfAndHalfSelection,
  createPizzaSizeSelectionDetail,
  findPizzaSizeOption,
  getPizzaHalfAndHalfSelection,
  getPizzaHalfAndHalfExtraUnitPrice,
  getPizzaHalfAndHalfPricing,
  getPizzaProductName,
  getPizzaSizeOptions,
  isPizzaHalfAndHalfSelection,
  normalizePizzaSizeOptions,
  productHasPizzaSizeOptions,
  resolvePizzaProductPrice,
} from "../../services/local/pizzaHalfAndHalf";
import {
  createEmpanadaCookingSelectionDetail,
  createEmpanadaSaleSelectionDetail,
  findEmpanadaCookingOption,
  findEmpanadaSaleOption,
  getEmpanadaCookingOptions,
  getEmpanadaSaleOptions,
  normalizeEmpanadaCookingOptions,
  normalizeEmpanadaSaleOptions,
  productHasEmpanadaSaleOptions,
  resolveEmpanadaProductPrice,
} from "../../services/local/empanadaSales";
import {
  assignWaiterToTable,
  buildWaiterOperationsStorageKey,
  completeDeliveryAlert,
  completeWaiterServiceRequest,
  doesWaiterServiceRequestMatchTable,
  getPendingWaiterServiceRequests,
  getWaiterOperationsState,
  getVisibleWaiterServiceRequests,
  isWaiterServiceRequestEscalated,
  markDeliveryAlertSeen,
  releaseWaiterTableAssignment,
  WAITER_OPERATIONS_REMOTE_SYNC_SOURCE,
  WAITER_OPERATIONS_UPDATED_EVENT,
} from "../../services/local/waiterOperations";
import {
  buildRestaurantProfileStorageKey,
  getRestaurantProfile,
  mergeRemoteBranchIntoProfilePreservingLocalPreferences,
  RESTAURANT_PROFILE_UPDATED_EVENT,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  buildRestaurantBrandTheme,
} from "../../services/local/brandTheme";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import {
  getIngredientStockValidationMessage,
  restoreIngredientStock,
  validateIngredientStock,
} from "../../services/local/ingredientStock";
import {
  BRANCH_TABLES_UPDATED_EVENT,
  buildBranchTablesStorageKey,
  getBranchTablesSnapshot,
  saveBranchTablesSnapshot,
} from "../../services/local/branchTablesSnapshot";
import { getBranchPublicProfile } from "../../services/supabase/companyAdminService";
import { startWaiterPresence } from "../../services/supabase/waiterPresenceService";
import { assertAcceptingNewOrders, hasAdditionalOrderItems } from "../../services/supabase/commissionDebtTermsService";
import { setDocumentTitle } from "../../services/local/pageTitle";

const WAITER_DEMO_WORKSPACE_KEY = "mozzoapp:waiter-demo-workspace";
const WAITER_DEMO_EMAIL = "mozo.demo@local.dev";
const WAITER_DEMO_COMPANY = {
  id: "waiter-demo-company",
  name: "MozzoApp Demo",
  workspaceRole: "mozo",
  role: "admin_limitado",
};
const WAITER_DEMO_BRANCH = {
  id: "waiter-demo-branch",
  companyId: WAITER_DEMO_COMPANY.id,
  name: "Salon Demo",
  description: "Modo mozo demo",
  role: "admin_limitado",
  workspaceRole: "mozo",
};
const EMPTY_OPERATIONAL_STATE = {
  mesasActivas: [],
  pedidosHistoricos: [],
  salonTables: [],
  salonLayout: {},
  salonReservations: {},
  salonRooms: [],
  salonTableRooms: {},
  salonFixtures: {},
};
const SHARED_OPERATIONAL_SYNC_INTERVAL_MS = 4000;
const DEFAULT_SALON_ROOM_ID = "salon-principal";
const DEFAULT_SALON_ROOM = {
  id: DEFAULT_SALON_ROOM_ID,
  name: "Salon principal",
};
const DEFAULT_TABLE_GEOMETRY = {
  shape: "circle",
  width: 88,
  height: 88,
};
const ANDROID_WAITER_APK_FILE_NAMES = {
  beta: "MozzoMozo-Beta.apk",
  production: "MozzoMozo.apk",
};
const SALON_FIXTURE_LABELS = {
  bar: "Barra",
  counter: "Mostrador",
  entrance: "Entrada",
  exit: "Salida",
  kitchen: "Cocina",
};

const loadingLogoKeyframes = `
  @keyframes mozzoapp-loading-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes mozzoapp-loading-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.82; }
    50% { transform: scale(1.04); opacity: 1; }
  }

  @keyframes mozzoapp-loading-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const createDemoTables = (branchId) =>
  Array.from({ length: 12 }, (_, index) => ({
    id: `${branchId}-mesa-${index + 1}`,
    branchId,
    tableNumber: index + 1,
    label: `Mesa ${index + 1}`,
    active: true,
    qr: {
      token: `demo-${index + 1}`,
    },
  }));

const normalizeTableLabel = (value) =>
  String(value || "").trim().toLowerCase();

const clampPercentage = (value, fallback) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(94, Math.max(6, parsedValue));
};

const normalizeSalonRooms = (rooms = []) => {
  const catalog = new Map();

  (Array.isArray(rooms) ? rooms : []).forEach((room) => {
    const id = String(room?.id || "").trim();

    if (!id || catalog.has(id)) {
      return;
    }

    catalog.set(id, {
      ...room,
      id,
      name: String(room?.name || room?.nombre || "Ambiente").trim() || "Ambiente",
    });
  });

  if (!catalog.size) {
    catalog.set(DEFAULT_SALON_ROOM.id, DEFAULT_SALON_ROOM);
  }

  return Array.from(catalog.values());
};

const resolveTableGeometry = (layout = {}) => {
  const shape = ["circle", "square", "rectangle"].includes(layout?.shape)
    ? layout.shape
    : DEFAULT_TABLE_GEOMETRY.shape;
  const requestedWidth = Number(layout?.width);
  const requestedHeight = Number(layout?.height);
  const requestedRotation = Math.round(Number(layout?.rotation || 0));
  const rotation = requestedRotation === 90 || requestedRotation === 270 ? 90 : 0;
  const width = Number.isFinite(requestedWidth) && requestedWidth > 0
    ? requestedWidth
    : DEFAULT_TABLE_GEOMETRY.width;
  const height = Number.isFinite(requestedHeight) && requestedHeight > 0
    ? requestedHeight
    : DEFAULT_TABLE_GEOMETRY.height;

  if (shape === "circle" || shape === "square") {
    const side = Math.max(64, Math.min(120, Math.max(width, height)));
    return { shape, width: side, height: side };
  }

  const resolvedWidth = Math.max(92, Math.min(170, width));
  const resolvedHeight = Math.max(62, Math.min(110, height));

  return {
    shape,
    width: rotation === 90 && resolvedWidth > resolvedHeight
      ? resolvedHeight
      : resolvedWidth,
    height: rotation === 90 && resolvedWidth > resolvedHeight
      ? resolvedWidth
      : resolvedHeight,
    rotation,
  };
};

const getSalonReservationForTable = (reservations, table, now = Date.now()) =>
  getBlockingSalonReservationForTable(reservations, table, now);

const buildDefaultSalonPosition = (index, total) => {
  const safeTotal = Math.max(1, Number(total || 1));
  const columns = Math.min(5, Math.max(2, Math.ceil(Math.sqrt(safeTotal))));
  const rows = Math.max(1, Math.ceil(safeTotal / columns));

  return {
    x: clampPercentage(((index % columns) + 0.5) * (100 / columns), 50),
    y: clampPercentage(
      (Math.floor(index / columns) + 0.5) * (100 / rows),
      50
    ),
  };
};

const resolveWaiterTableLayout = (entry, index, total) => {
  const fallback = buildDefaultSalonPosition(index, total);

  return {
    x: clampPercentage(entry?.x, fallback.x),
    y: clampPercentage(entry?.y, fallback.y),
    ...resolveTableGeometry(entry),
  };
};

const getTableBorderRadius = (shape) => {
  if (shape === "circle") {
    return "999px";
  }

  return shape === "rectangle" ? "24px" : "22px";
};

const resolveWaiterFixturesForRoom = (fixturesByRoom, roomId) => {
  if (Object.prototype.hasOwnProperty.call(fixturesByRoom || {}, roomId)) {
    return Array.isArray(fixturesByRoom[roomId]) ? fixturesByRoom[roomId] : [];
  }

  return [
    {
      id: `${roomId}-entrance-default`,
      type: "entrance",
      label: "Entrada",
      x: 12,
      y: 92,
      width: 16,
      height: 7,
    },
    {
      id: `${roomId}-kitchen-default`,
      type: "kitchen",
      label: "Cocina / servicio",
      x: 84,
      y: 9,
      width: 24,
      height: 10,
    },
  ];
};

const parseTableNumber = (value) => {
  const numericMatch = String(value || "").match(/(\d+)/);
  return Number(numericMatch?.[1] || 0);
};

const isOpenOperationalOrder = (order) =>
  order?.phase !== "closed" && order?.phase !== "cancelled";

const getOperationalOrdersForTable = (orders, table) => {
  if (!table) {
    return [];
  }

  const tableId = table.id || "";
  const tableLabel = normalizeTableLabel(
    table.label || `Mesa ${table.tableNumber || ""}`
  );
  const tableNumber = Number(table.tableNumber || 0);

  return (Array.isArray(orders) ? orders : []).filter((order) => {
      if (!isOpenOperationalOrder(order)) {
        return false;
      }

      if (order.tableId && order.tableId === tableId) {
        return true;
      }

      if (
        tableNumber > 0 &&
        parseTableNumber(order.mesa || order.tableLabel || "") === tableNumber
      ) {
        return true;
      }

      return normalizeTableLabel(order.mesa || order.tableLabel || "") === tableLabel;
    });
};

const findOperationalOrderForTable = (orders, table) =>
  getOperationalOrdersForTable(orders, table)[0] || null;

const buildTablesFromOperationalOrders = (branchId, mesasActivas = []) =>
  (Array.isArray(mesasActivas) ? mesasActivas : [])
    .filter((mesa) => mesa?.tableId && isOpenOperationalOrder(mesa))
    .map((mesa, index) => {
      const parsedTableNumber = parseTableNumber(mesa.mesa);

      return {
        id: mesa.tableId,
        branchId,
        tableNumber: parsedTableNumber > 0 ? parsedTableNumber : index + 1,
        label: mesa.mesa || `Mesa ${index + 1}`,
        active: true,
        qr: null,
      };
    });

const normalizeWaiterCategory = (category) => ({
  ...category,
  id: category?.id || "",
  nombre: category?.nombre || category?.name || "",
});

const normalizeWaiterSubcategory = (subcategory) => ({
  ...subcategory,
  id:
    subcategory?.id ||
    subcategory?.subcategoryId ||
    subcategory?.subcategoriaId ||
    "",
  name: subcategory?.name || subcategory?.nombre || "",
  nombre: subcategory?.nombre || subcategory?.name || "",
});

const loadLocalIngredientCatalog = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  try {
    const rawValue = window.localStorage.getItem("mozzoapp:ingredientes");
    const parsed = rawValue ? JSON.parse(rawValue) : [];

    return new Map(
      (Array.isArray(parsed) ? parsed : [])
        .filter((ingredient) => ingredient?.id)
        .map((ingredient) => [ingredient.id, ingredient])
    );
  } catch (error) {
    return new Map();
  }
};

const normalizeWaiterIngredient = (ingredient, fallbackIngredient = null) => {
  const baseQuantity = getIngredientMenuBaseQuantity(ingredient);
  const consumptionQuantity = getIngredientConsumptionQuantity(ingredient);

  return {
    ...ingredient,
    id: ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id || "",
    ingredientId: ingredient?.ingredientId || ingredient?.ingredient_id || ingredient?.id || "",
    name:
      ingredient?.name ||
      ingredient?.nombre ||
      fallbackIngredient?.nombre ||
      fallbackIngredient?.name ||
      "",
    nombre:
      ingredient?.nombre ||
      ingredient?.name ||
      fallbackIngredient?.nombre ||
      fallbackIngredient?.name ||
      "",
    imageUrl: ingredient?.imageUrl || ingredient?.fotoUrl || "",
    fotoUrl: ingredient?.fotoUrl || ingredient?.imageUrl || "",
    price:
      ingredient?.price !== undefined && ingredient?.price !== null
        ? Number(ingredient.price || 0)
        : Number(ingredient?.precio ?? fallbackIngredient?.precio ?? fallbackIngredient?.price ?? 0),
    precio:
      ingredient?.precio !== undefined && ingredient?.precio !== null
        ? Number(ingredient.precio || 0)
        : Number(ingredient?.price ?? fallbackIngredient?.precio ?? fallbackIngredient?.price ?? 0),
    type:
      ingredient?.type ||
      ingredient?.tipo ||
      (fallbackIngredient?.tipo === "obligatorio" ? "required" : null) ||
      "optional",
    tipo:
      ingredient?.tipo ||
      ingredient?.type ||
      fallbackIngredient?.tipo ||
      "opcional",
    selectable:
      ingredient?.selectable ?? ingredient?.seleccionable ?? fallbackIngredient?.seleccionable ?? true,
    cantidad: baseQuantity,
    quantity: baseQuantity,
    baseQuantity,
    menuBaseQuantity: baseQuantity,
    menuQuantity: baseQuantity,
    consumptionQuantity,
    stockQuantity: consumptionQuantity,
    canRemove: ingredient?.canRemove === true || ingredient?.can_remove === true,
    canAddMore: ingredient?.canAddMore === true || ingredient?.can_add_more === true,
    maxExtraQuantity: Number(
      ingredient?.maxExtraQuantity ?? ingredient?.max_extra_quantity ?? 0
    ),
    unit: ingredient?.consumptionUnit || ingredient?.unit || ingredient?.unidad || "un",
    unidad: ingredient?.unidad || ingredient?.unit || ingredient?.consumptionUnit || "un",
    consumptionUnit: ingredient?.consumptionUnit || ingredient?.unit || ingredient?.unidad || "un",
  };
};

const normalizeWaiterProduct = (product) => {
  const ingredientCatalog = loadLocalIngredientCatalog();
  const sourceIngredients = Array.isArray(product?.ingredients)
    ? product.ingredients
    : Array.isArray(product?.ingredientes)
      ? product.ingredientes
      : [];
  const normalizedIngredients = sourceIngredients.map((ingredient) =>
    normalizeWaiterIngredient(
      ingredient,
      ingredientCatalog.get(
        ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id || ""
      ) || null
    )
  );
  const imageUrl =
    product?.imageUrl ||
    product?.image_url ||
    product?.fotoUrl ||
    product?.foto_url ||
    product?.imagen ||
    product?.image ||
    "";
  const pizzaSizeOptions = normalizePizzaSizeOptions(
    product?.pizzaSizeOptions ||
      product?.pizza_size_options ||
      product?.pizzaSizes ||
      product?.pizza_sizes ||
      []
  );
  const empanadaSaleOptions = normalizeEmpanadaSaleOptions(
    product?.empanadaSaleOptions ||
      product?.empanada_sale_options ||
      product?.empanadaOptions ||
      product?.empanada_options ||
      []
  );
  const empanadaCookingOptions = normalizeEmpanadaCookingOptions(
    product?.empanadaCookingOptions ||
      product?.empanada_cooking_options ||
      product?.empanadaCookOptions ||
      product?.empanada_cook_options ||
      []
  );

  return {
    ...product,
    id: product?.id || "",
    nombre: product?.nombre || product?.name || "",
    name: product?.name || product?.nombre || "",
    imageUrl,
    fotoUrl: imageUrl,
    categoria:
      product?.categoria || product?.categoryId || product?.category_id || "",
    subcategoria:
      product?.subcategoria ||
      product?.subcategoryName ||
      product?.subcategory ||
      "",
    subcategoriaId:
      product?.subcategoriaId ||
      product?.subcategoryId ||
      product?.subcategory_id ||
      "",
    precio:
      product?.precio !== undefined && product?.precio !== null
        ? Number(product.precio || 0)
        : Number(product?.price || 0),
    precioSugerido:
      product?.precioSugerido !== undefined && product?.precioSugerido !== null
        ? Number(product.precioSugerido || 0)
        : Number(product?.suggestedPrice || product?.suggested_price || 0),
    ingredients: normalizedIngredients,
    ingredientes: normalizedIngredients.map((ingredient) => ({ ...ingredient })),
    pizzaSizeOptions,
    pizza_size_options: pizzaSizeOptions,
    empanadaSaleOptions,
    empanada_sale_options: empanadaSaleOptions,
    empanadaCookingOptions,
    empanada_cooking_options: empanadaCookingOptions,
  };
};

const normalizeWaiterMenuSnapshot = (snapshot) => {
  const products = Array.isArray(snapshot?.products)
    ? snapshot.products
        .filter((product) => product?.active !== false)
        .map(normalizeWaiterProduct)
    : [];
  const explicitSubcategories = Array.isArray(snapshot?.subcategories)
    ? snapshot.subcategories
    : Array.isArray(snapshot?.subcategorias)
      ? snapshot.subcategorias
      : [];
  const derivedSubcategories = new Map();

  products.forEach((product) => {
    const subcategoryId = product.subcategoriaId || product.subcategoryId || "";
    const subcategoryName = product.subcategoria || product.subcategoryName || "";

    if (!subcategoryId && !subcategoryName) {
      return;
    }

    derivedSubcategories.set(subcategoryId || subcategoryName, {
      id: subcategoryId,
      name: subcategoryName,
      nombre: subcategoryName,
    });
  });

  return {
    categories: Array.isArray(snapshot?.categories)
      ? snapshot.categories.map(normalizeWaiterCategory)
      : [],
    products,
    subcategories: explicitSubcategories.length
      ? explicitSubcategories.map(normalizeWaiterSubcategory)
      : Array.from(derivedSubcategories.values()),
    linkedSubcategoryRules: Array.isArray(snapshot?.linkedSubcategoryRules)
      ? snapshot.linkedSubcategoryRules
      : [],
  };
};

const enrichSnapshotWithIngredientsCatalog = (snapshot, ingredientsCatalog = []) => {
  const ingredientMap = new Map(
    (Array.isArray(ingredientsCatalog) ? ingredientsCatalog : [])
      .filter((ingredient) => ingredient?.id)
      .map((ingredient) => [ingredient.id, ingredient])
  );

  if (!ingredientMap.size) {
    return snapshot;
  }

  const enrichIngredient = (ingredient) => {
    const fallbackIngredient =
      ingredientMap.get(
        ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id || ""
      ) || null;

    if (!fallbackIngredient) {
      return ingredient;
    }

    return {
      ...ingredient,
      name:
        ingredient?.name || ingredient?.nombre || fallbackIngredient?.nombre || "",
      nombre:
        ingredient?.nombre || ingredient?.name || fallbackIngredient?.nombre || "",
      price:
        ingredient?.price !== undefined && ingredient?.price !== null
          ? Number(ingredient.price || 0)
          : Number(fallbackIngredient?.precio || 0),
      precio:
        ingredient?.precio !== undefined && ingredient?.precio !== null
          ? Number(ingredient.precio || 0)
          : Number(fallbackIngredient?.precio || 0),
      type:
        ingredient?.type ||
        (fallbackIngredient?.tipo === "obligatorio" ? "required" : "optional"),
      tipo: ingredient?.tipo || fallbackIngredient?.tipo || "opcional",
      selectable:
        ingredient?.selectable ?? ingredient?.seleccionable ?? fallbackIngredient?.seleccionable ?? true,
      seleccionable:
        ingredient?.seleccionable ?? ingredient?.selectable ?? fallbackIngredient?.seleccionable ?? true,
    };
  };

  return {
    ...snapshot,
    products: Array.isArray(snapshot?.products)
      ? snapshot.products
          .filter((product) => product?.active !== false)
          .map((product) => ({
          ...product,
          ingredients: Array.isArray(product?.ingredients)
            ? product.ingredients.map(enrichIngredient)
            : Array.isArray(product?.ingredientes)
              ? product.ingredientes.map(enrichIngredient)
              : [],
          ingredientes: Array.isArray(product?.ingredientes)
            ? product.ingredientes.map(enrichIngredient)
            : Array.isArray(product?.ingredients)
              ? product.ingredients.map(enrichIngredient)
              : [],
        }))
      : [],
  };
};

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString("es-AR")}`;

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new Date(value).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "";
  }
};

const formatReservationTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return "";
  }
};

const buildWaiterAlertSoundKey = (alert) =>
  [
    String(alert?.id || "").trim(),
    String(alert?.requestedAt || "").trim(),
    String(alert?.status || "").trim(),
    String(alert?.type || "").trim(),
    Array.isArray(alert?.targetWaiterIds) ? alert.targetWaiterIds.join(",") : "",
    alert?.broadcast ? "broadcast" : "targeted",
  ].join("|");

const scheduleWaiterRingtoneTone = (
  context,
  destination,
  startAt,
  frequency,
  duration,
  volume = 0.09
) => {
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  const attackAt = startAt + 0.02;
  const releaseAt = startAt + duration;

  oscillator.type = "triangle";
  oscillator.frequency.setValueAtTime(frequency, startAt);
  oscillator.connect(gainNode);
  gainNode.connect(destination);

  gainNode.gain.setValueAtTime(0.0001, startAt);
  gainNode.gain.linearRampToValueAtTime(volume, attackAt);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, releaseAt);

  oscillator.start(startAt);
  oscillator.stop(releaseAt + 0.02);
};

const scheduleWaiterRingtone = (context) => {
  const now = context.currentTime + 0.02;
  const masterGain = context.createGain();
  masterGain.gain.setValueAtTime(0.9, now);
  masterGain.connect(context.destination);

  [0, 0.92, 1.84].forEach((offset) => {
    scheduleWaiterRingtoneTone(context, masterGain, now + offset, 880, 0.16, 0.08);
    scheduleWaiterRingtoneTone(context, masterGain, now + offset + 0.18, 698, 0.22, 0.11);
    scheduleWaiterRingtoneTone(context, masterGain, now + offset + 0.44, 988, 0.16, 0.08);
  });

  window.setTimeout(() => {
    masterGain.disconnect();
  }, 3200);
};

const ORDER_PHASE_LABELS = {
  new: "Nuevo",
  preparation: "En preparacion",
  preparing: "En preparacion",
  ready: "Listo para entregar",
  ready_for_delivery: "Listo para entregar",
  delivered: "Entregado",
  closed: "Cerrado",
  cancelled: "Cancelado",
};

const READY_FOR_DELIVERY_PHASES = new Set(["ready", "ready_for_delivery"]);

const isOrderReadyForDelivery = (order) =>
  READY_FOR_DELIVERY_PHASES.has(String(order?.phase || "").trim().toLowerCase());

const isDeliveryReadyAlert = (alert) => {
  const type = String(alert?.type || "delivery").trim().toLowerCase();
  return !["service", "service_request", "call_waiter", "waiter_call", "need_help"].includes(type);
};

const ORDER_SOURCE_LABELS = {
  app: "QR cliente",
  waiter: "Mozo",
  qr: "QR cliente",
  manual: "Caja",
  ia: "IA",
  assistant: "IA",
};

const getOperationalCustomerName = (order) =>
  String(
    order?.customerName ||
      order?.customerAlias ||
      order?.clientName ||
      order?.customer_name ||
      ""
  ).trim();

const getInteroperableQrData = (source) => {
  const qr =
    source?.qr && typeof source.qr === "object"
      ? source.qr
      : source?.qrPayload && typeof source.qrPayload === "object"
        ? source.qrPayload
        : {};
  const candidates = [
    qr?.qrData,
    qr?.qr_data,
    qr?.copyCode,
    qr?.copy_code,
    source?.qrData,
    source?.qr_data,
    source?.copyCode,
    source?.copy_code,
  ];

  return candidates
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";
};

const NEW_ORDER_ACCOUNT_ID = "__new__";

const getOperationalOrderTotal = (order) => {
  const storedTotal = Number(order?.totalAmount ?? order?.total ?? order?.amount);
  if (Number.isFinite(storedTotal) && storedTotal >= 0) return storedTotal;

  return normalizeOperationalOrderItems(order?.items || []).reduce(
    (total, item) =>
      total + Number(item?.quantity || 1) * Number(item?.unitPrice ?? item?.price ?? 0),
    0
  );
};

const isOperationalOrderDigital = (order) => {
  const method = String(
    order?.paymentMethod ||
      order?.selectedPaymentMethod ||
      order?.requestedPaymentMethod ||
      order?.paymentProvider ||
      ""
  ).toLowerCase();
  return ["deeplink", "mercado", "digital", "pay_by_phone", "qr", "point"].some(
    (token) => method.includes(token)
  );
};

const DEFAULT_MAX_EXTRA_QUANTITY = 2;

const resolveMenuProductPrice = (product) => {
  if (!product) {
    return 0;
  }

  if (productHasPizzaSizeOptions(product)) {
    const prices = getPizzaSizeOptions(product)
      .map(getPizzaStartingPrice)
      .filter((price) => price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  if (productHasEmpanadaSaleOptions(product)) {
    const prices = getEmpanadaSaleOptions(product)
      .map((option) => Number(option.price || 0))
      .filter((price) => price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  if (product.precio !== undefined && product.precio !== null && product.precio !== "") {
    return Number(product.precio || 0);
  }

  return Number(product.precioSugerido || 0);
};

const normalizeEntityId = (value) => String(value || "").trim();

const createWaiterDraftSelection = (product, existingSelection = null) =>
  (Array.isArray(product?.ingredients) ? product.ingredients : []).reduce((acc, ingredient) => {
    acc[ingredient.id] = Number(
      existingSelection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
    );
    return acc;
  }, {});

const getWaiterCustomizationSummary = (product, selection = null) => {
  if (!product?.ingredients?.length || !selection) {
    return null;
  }

  let removedUnits = 0;
  let extraUnits = 0;
  let extraCost = 0;

  product.ingredients.forEach((ingredient) => {
    const baseQuantity = Number(ingredient.baseQuantity || 0);
    const selectedQuantity = Number(selection?.[ingredient.id] ?? baseQuantity);

    if (selectedQuantity < baseQuantity) {
      removedUnits += baseQuantity - selectedQuantity;
    }

    if (selectedQuantity > baseQuantity) {
      const extra = selectedQuantity - baseQuantity;
      extraUnits += extra;
      extraCost += extra * Number(ingredient.price || 0);
    }
  });

  if (!removedUnits && !extraUnits) {
    return null;
  }

  return {
    removedUnits,
    extraUnits,
    extraCost,
    changes: removedUnits + extraUnits,
  };
};

const getWaiterIngredientStateLabel = (ingredient, quantity) => {
  const baseQuantity = Number(ingredient.baseQuantity || 0);

  if (baseQuantity <= 0 && quantity <= 0) {
    return "Sin agregar";
  }

  if (quantity <= 0) {
    return "Quitado";
  }

  if (quantity === baseQuantity) {
    return "Normal";
  }

  if (quantity > baseQuantity) {
    return `+${quantity - baseQuantity} extra`;
  }

  return "Ajustado";
};

const buildWaiterLinkedSelectionDetail = (linkedProduct) => {
  if (!linkedProduct) {
    return "";
  }

  const groupLabel = linkedProduct.subcategoryName || linkedProduct.subcategoria || "Opcion";
  return `${groupLabel}: ${linkedProduct.nombre || linkedProduct.name}`;
};

const buildWaiterSelectionDetails = (
  product,
  selection = {},
  linkedProduct = null,
  pizzaHalfSelection = null,
  pizzaSizeOption = null,
  empanadaSaleOption = null,
  empanadaCookingOption = null
) => {
  const details = (Array.isArray(product?.ingredients) ? product.ingredients : []).reduce(
    (acc, ingredient) => {
      const baseQuantity = Number(ingredient.baseQuantity || 0);
      const selectedQuantity = Number(
        selection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
      );

      if (selectedQuantity === baseQuantity) {
        return acc;
      }

      if (selectedQuantity <= 0 && baseQuantity > 0) {
        acc.push(`Sin ${ingredient.name}`);
        return acc;
      }

      if (selectedQuantity < baseQuantity) {
        acc.push(`-${baseQuantity - selectedQuantity} ${ingredient.name}`);
        return acc;
      }

      if (selectedQuantity > baseQuantity) {
        acc.push(`+${selectedQuantity - baseQuantity} ${ingredient.name}`);
      }

      return acc;
    },
    []
  );

  if (pizzaHalfSelection) {
    details.push(...buildPizzaHalfAndHalfSelectionDetails(product, pizzaHalfSelection));
  } else if (linkedProduct) {
    details.push(buildWaiterLinkedSelectionDetail(linkedProduct));
  }

  if (!pizzaHalfSelection) {
    const sizeDetail = createPizzaSizeSelectionDetail(pizzaSizeOption);

    if (sizeDetail) {
      details.unshift(sizeDetail);
    }
  }

  const empanadaCookingDetail =
    createEmpanadaCookingSelectionDetail(empanadaCookingOption);

  if (empanadaCookingDetail) {
    details.unshift(empanadaCookingDetail);
  }

  const empanadaSaleDetail =
    createEmpanadaSaleSelectionDetail(empanadaSaleOption);

  if (empanadaSaleDetail) {
    details.unshift(empanadaSaleDetail);
  }

  return details;
};

const buildWaiterOrderConsumptions = (
  product,
  {
    selection = {},
    quantity = 1,
    linkedProduct = null,
    pizzaHalfSelection = null,
    pizzaSizeOption = null,
    pizzaPortionMode = "full",
    empanadaSaleOption = null,
  } = {}
) => {
  const basePizzaRecipeScale = Number(pizzaSizeOption?.recipeScale || 1);
  const baseEmpanadaUnitScale = Number(empanadaSaleOption?.unitCount || 1);
  const baseConsumptions = buildOperationalConsumptions(product, {
    selection,
    quantity,
    quantityScale:
      getPizzaPortionScale(pizzaSizeOption, pizzaHalfSelection ? "half_and_half" : pizzaPortionMode) *
      basePizzaRecipeScale *
      baseEmpanadaUnitScale,
  });

  if (!linkedProduct) {
    return baseConsumptions;
  }

  const linkedPizzaSizeOption = pizzaHalfSelection
    ? findPizzaSizeOption(
        linkedProduct,
        pizzaHalfSelection.pizzaSizeOptionId || pizzaSizeOption?.id || ""
      )
    : null;

  return [
    ...baseConsumptions,
    ...buildOperationalConsumptions(linkedProduct, {
      quantity,
      quantityScale:
        (pizzaHalfSelection ? 0.5 : 1) *
        Number(linkedPizzaSizeOption?.recipeScale || 1),
    }),
  ];
};

const createWaiterOrderItem = (
  product,
  quantity,
  selection = {},
  linkedProduct = null,
  pizzaHalfSelection = null,
  pizzaSizeOption = null,
  empanadaSaleOption = null,
  empanadaCookingOption = null,
  beverageOption = null,
  pizzaPortionMode = "full"
) => {
  const resolvedPizzaSizeOption =
    pizzaSizeOption || (productHasPizzaSizeOptions(product) ? findPizzaSizeOption(product) : null);
  const resolvedEmpanadaSaleOption =
    empanadaSaleOption ||
    (productHasEmpanadaSaleOptions(product) ? findEmpanadaSaleOption(product) : null);
  const resolvedEmpanadaCookingOption =
    empanadaCookingOption || findEmpanadaCookingOption(product);
  const linkedUnitPrice = pizzaHalfSelection
    ? getPizzaHalfAndHalfExtraUnitPrice(product, linkedProduct, {
        sizeOptionId:
          resolvedPizzaSizeOption?.id || pizzaHalfSelection.pizzaSizeOptionId || "",
      })
    : Number(linkedProduct ? resolveMenuProductPrice(linkedProduct) : 0);
  const baseUnitPrice = resolvedPizzaSizeOption
    ? resolvePizzaProductPrice(product, { sizeOption: resolvedPizzaSizeOption, portionMode: pizzaHalfSelection ? "half_and_half" : pizzaPortionMode })
    : resolvedEmpanadaSaleOption
      ? resolveEmpanadaProductPrice(product, {
          saleOption: resolvedEmpanadaSaleOption,
        })
    : resolveMenuProductPrice(product);
  const extraUnitCost = Number(
    getWaiterCustomizationSummary(product, selection)?.extraCost || 0
  );

  return createOperationalOrderItem({
    id: `waiter-order-item-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    productId: product.id,
    productName: product.nombre,
    delivered: false,
    linkedProductId: linkedProduct?.id || "",
    linkedProductName: linkedProduct?.nombre || linkedProduct?.name || "",
    linkedSelections: beverageOption ? [{ productId: beverageOption.id, productName: beverageOption.name,
      targetIngredientId: beverageOption.targetIngredientId, consumptionMl: beverageOption.consumptionMl,
      ruleKey: `${product.id}::beverage-variety`, unitPrice: 0 }] : pizzaHalfSelection ? [pizzaHalfSelection] : [],
    linkedUnitPrice,
    pizzaSizeOptionId: resolvedPizzaSizeOption?.id || "",
    pizzaPortionMode: pizzaHalfSelection ? "half_and_half" : pizzaPortionMode,
    pizzaSizeLabel: resolvedPizzaSizeOption?.label || "",
    pizzaSizeFullUnitPrice: Number(resolvedPizzaSizeOption?.fullPrice || 0),
    pizzaSizeHalfUnitPrice: Number(resolvedPizzaSizeOption?.halfPrice || 0),
    pizzaSizeSliceUnitPrice: Number(resolvedPizzaSizeOption?.slicePrice || 0),
    pizzaSlices: Number(resolvedPizzaSizeOption?.slices || 0),
    pizzaSizeRecipeScale: Number(resolvedPizzaSizeOption?.recipeScale || 1),
    empanadaSaleOptionId: resolvedEmpanadaSaleOption?.id || "",
    empanadaSaleLabel: resolvedEmpanadaSaleOption?.label || "",
    empanadaUnitCount: Number(resolvedEmpanadaSaleOption?.unitCount || 1),
    empanadaSaleUnitPrice: Number(resolvedEmpanadaSaleOption?.price || 0),
    empanadaCookingOptionId: resolvedEmpanadaCookingOption?.id || "",
    empanadaCookingLabel: resolvedEmpanadaCookingOption?.label || "",
    splitMode: pizzaHalfSelection?.splitMode || "",
    selection,
    selectionDetails: buildWaiterSelectionDetails(
      product,
      selection,
      linkedProduct,
      pizzaHalfSelection,
      resolvedPizzaSizeOption,
      resolvedEmpanadaSaleOption,
      resolvedEmpanadaCookingOption
    ).concat(pizzaPortionMode === "half" ? ["Formato: Media pizza"] : pizzaPortionMode === "slice" ? ["Formato: Porcion"] : [])
      .concat(beverageOption ? [`Variedad: ${beverageOption.name} (${beverageOption.consumptionMl} ml)`] : []),
    consumos: buildWaiterOrderConsumptions(product, {
      selection,
      quantity,
      linkedProduct,
      pizzaHalfSelection,
      pizzaSizeOption: resolvedPizzaSizeOption,
      pizzaPortionMode,
      empanadaSaleOption: resolvedEmpanadaSaleOption,
    }).concat(beverageOption ? buildOperationalConsumptions(beverageOption, { quantity }) : []),
    quantity,
    baseUnitPrice,
    extraUnitCost,
    unitPrice: baseUnitPrice + linkedUnitPrice + extraUnitCost,
    categoryName: product.categoryName || product.categoriaNombre || "",
    subcategoryName: product.subcategoryName || product.subcategoria || "",
  });
};

const findAnySavedMenuSnapshot = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const snapshotKeys = Object.keys(window.localStorage).filter((key) =>
      key.startsWith("mozzoapp:menu:")
    );

    for (const key of snapshotKeys) {
      const rawValue = window.localStorage.getItem(key);

      if (!rawValue) {
        continue;
      }

      const parsed = JSON.parse(rawValue);

      if (Array.isArray(parsed?.products) && parsed.products.length > 0) {
        return {
          categories: Array.isArray(parsed.categories) ? parsed.categories : [],
          linkedSubcategoryRules: Array.isArray(parsed.linkedSubcategoryRules)
            ? parsed.linkedSubcategoryRules
            : [],
          products: parsed.products,
        };
      }
    }
  } catch (error) {
    return null;
  }

  return null;
};

const readStandaloneWaiterDemoWorkspace = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(WAITER_DEMO_WORKSPACE_KEY);

    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);

    if (!parsed?.company?.id || !parsed?.branch?.id) {
      return null;
    }

    return {
      company: parsed.company,
      branch: parsed.branch,
    };
  } catch (error) {
    return null;
  }
};

const buildStandaloneWaiterDemoAuth = () => {
  const storedWorkspace = readStandaloneWaiterDemoWorkspace();
  const company = storedWorkspace?.company?.id
    ? {
        ...storedWorkspace.company,
        role: "admin_limitado",
        workspaceRole: "mozo",
      }
    : WAITER_DEMO_COMPANY;
  const branch = storedWorkspace?.branch?.id
    ? {
        ...storedWorkspace.branch,
        role: "admin_limitado",
        workspaceRole: "mozo",
        companyId: storedWorkspace.branch.companyId || company.id,
      }
    : WAITER_DEMO_BRANCH;

  return {
    currentCompany: company,
    currentBranch: branch,
    currentRestaurant: branch,
    currentUserProfile: {
      fullName: "Mozo demo",
      documentId: "",
      phone: "",
      email: WAITER_DEMO_EMAIL,
    },
    user: {
      id: "waiter-demo-user",
      email: WAITER_DEMO_EMAIL,
    },
    signOut: () => {
      if (typeof window !== "undefined") {
        window.location.assign("/login");
      }
    },
    isSupabaseConfigured: isSupabaseRuntimeConfigured,
    isWaiterDemoMode: true,
    hasWaiterPermission: () => true,
  };
};

const newWaiterPaymentAttemptId = () => `waiter-payment-${
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
}`;

function PanelMozo({ authOverride = null }) {
  const auth = useAuth();
  const {
    currentCompany,
    currentBranch,
    currentUserProfile,
    currentRestaurant,
    user,
    signOut,
    isCreatorMode,
    isLocalPersonnelTestMode,
    isSupabaseConfigured,
    isWaiterDemoMode,
    hasWaiterPermission,
  } = authOverride || auth;
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [tablesError, setTablesError] = useState("");
  const [waiterOps, setWaiterOps] = useState(() =>
    getWaiterOperationsState(currentBranch || currentRestaurant)
  );
  const [operationalOrders, setOperationalOrders] = useState(() =>
    getOperationalOrdersState(currentBranch || currentRestaurant)
  );
  const [menuCatalog, setMenuCatalog] = useState({
    categories: [],
    products: [],
    linkedSubcategoryRules: [],
  });
  const [promotions, setPromotions] = useState(() => getPromotions(currentBranch || currentRestaurant));
  const [panelMessage, setPanelMessage] = useState("");
  const [serviceRequestClock, setServiceRequestClock] = useState(() => Date.now());
  const [selectedTableId, setSelectedTableId] = useState("");
  const [selectedSalonRoomId, setSelectedSalonRoomId] = useState("");
  const [orderTargetMode, setOrderTargetMode] = useState("table");
  const [orderWorkspaceTab, setOrderWorkspaceTab] = useState("order");
  const [selectedExistingOrderId, setSelectedExistingOrderId] = useState("");
  const [orderCustomerName, setOrderCustomerName] = useState("");
  const [tableOrderItems, setTableOrderItems] = useState([]);
  const [tableOrderSearch, setTableOrderSearch] = useState("");
  const [tableOrderCategoryId, setTableOrderCategoryId] = useState("");
  const [tableOrderMessage, setTableOrderMessage] = useState("");
  const [waiterChargeDialog, setWaiterChargeDialog] = useState(null);
  const [waiterChargeIntent, setWaiterChargeIntent] = useState(null);
  const [waiterChargeMessage, setWaiterChargeMessage] = useState("");
  const [waiterChargeBusy, setWaiterChargeBusy] = useState(false);
  const [waiterChargeRefreshing, setWaiterChargeRefreshing] = useState(false);
  const [waiterQrCancelConfirmation, setWaiterQrCancelConfirmation] = useState(false);
  const [selectedMenuProductId, setSelectedMenuProductId] = useState("");
  const [editingTableOrderItemId, setEditingTableOrderItemId] = useState("");
  const [draftSelection, setDraftSelection] = useState({});
  const [draftLinkedProductId, setDraftLinkedProductId] = useState("");
  const [draftBeverageIngredientId, setDraftBeverageIngredientId] = useState("");
  const [draftPizzaSizeOptionId, setDraftPizzaSizeOptionId] = useState("");
  const [draftPizzaPortionMode, setDraftPizzaPortionMode] = useState("full");
  const [draftPizzaHalfProductId, setDraftPizzaHalfProductId] = useState("");
  const [draftEmpanadaSaleOptionId, setDraftEmpanadaSaleOptionId] = useState("");
  const [draftEmpanadaCookingOptionId, setDraftEmpanadaCookingOptionId] = useState("");
  const [draftProductQuantity, setDraftProductQuantity] = useState(1);
  const [launcherInstallTarget, setLauncherInstallTarget] = useState("");
  const [restaurantProfile, setRestaurantProfile] = useState(() =>
    getRestaurantProfile(currentBranch || currentRestaurant)
  );
  const activeBranchScope = currentBranch || currentRestaurant;
  const isLocalOnlyWaiterSession =
    isCreatorMode || isLocalPersonnelTestMode || isWaiterDemoMode;
  const waiterPresenceRef = useRef(null);
  const newOrderCheckRef = useRef(false);
  const orderEditorScope = `${activeBranchScope?.id}:${user?.id}:${orderTargetMode}:${selectedTableId}:${selectedExistingOrderId}`;
  const orderEditorScopeRef = useRef(orderEditorScope);
  orderEditorScopeRef.current = orderEditorScope;
  useEffect(() => {
    if (!currentBranch?.id || !user?.id || isLocalOnlyWaiterSession || !isSupabaseConfigured) return;
    const presenceError = "No se pudo confirmar tu conexion para recibir entregas. Estamos reintentando.";
    const presence = startWaiterPresence({
      branchId: currentBranch.id,
      onError: () => setPanelMessage((message) => message || presenceError),
      onConnected: () => setPanelMessage((message) => message === presenceError ? "" : message),
    });
    waiterPresenceRef.current = presence;
    return () => {
      if (waiterPresenceRef.current === presence) waiterPresenceRef.current = null;
      void presence.stop();
    };
  }, [currentBranch?.id, user?.id, isLocalOnlyWaiterSession, isSupabaseConfigured]);
  const handleWaiterSignOut = async () => {
    await waiterPresenceRef.current?.stop();
    await signOut();
  };
  const isAndroidWaiterLauncher = useMemo(
    () =>
      typeof navigator !== "undefined" &&
      /MozzoWaiter\/[\d.]+/i.test(String(navigator.userAgent || "")),
    []
  );
  const isProductionWaiterDownload = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const hostname = String(window.location.hostname || "").toLowerCase();
    return hostname === "mozzoapp.com" || hostname === "www.mozzoapp.com";
  }, []);
  const androidWaiterApkUrl = useMemo(() => {
    const selectedApkUrl = isProductionWaiterDownload
      ? mozzoWaiterReleaseApkUrl
      : mozzoWaiterDebugApkUrl;

    if (!selectedApkUrl || typeof window === "undefined") {
      return selectedApkUrl || "";
    }

    return new URL(selectedApkUrl, window.location.origin).toString();
  }, [isProductionWaiterDownload]);
  const androidWaiterApkFileName = isProductionWaiterDownload
    ? ANDROID_WAITER_APK_FILE_NAMES.production
    : ANDROID_WAITER_APK_FILE_NAMES.beta;
  const waiterLauncherUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return new URL("/mozo", window.location.origin).toString();
  }, []);
  const waiterAlertSoundHydratedRef = useRef(false);
  const previousVisibleAlertKeysRef = useRef(new Set());
  const waiterAudioContextRef = useRef(null);
  const waiterLastRingAtRef = useRef(0);
  const waiterChargeBusyRef = useRef(false);
  const waiterChargeFinalizingIntentRef = useRef("");
  const waiterChargeRequestVersionRef = useRef(0);
  const waiterChargeRefreshRequestRef = useRef(null);
  const refreshWaiterQrChargeRef = useRef(null);
  useEffect(() => () => {
    waiterChargeRequestVersionRef.current += 1;
    waiterChargeRefreshRequestRef.current = null;
  }, [activeBranchScope?.id]);
  const brandTheme = useMemo(
    () => buildRestaurantBrandTheme(restaurantProfile || {}),
    [restaurantProfile]
  );
  const restaurantFaviconUrl =
    brandTheme.effectiveIconLogoUrl ||
    brandTheme.effectiveLogoCompactUrl ||
    "";
  const loadingLogoUrl = brandTheme.effectiveLoadingLogoUrl || "";
  const loadingLogoAnimation =
    brandTheme.loadingAnimation === "pulse"
      ? { animation: "mozzoapp-loading-pulse 1.45s ease-in-out infinite" }
      : brandTheme.loadingAnimation === "float"
        ? { animation: "mozzoapp-loading-float 1.9s ease-in-out infinite" }
        : { animation: "mozzoapp-loading-spin 1.8s linear infinite" };

  useEffect(() => {
    setDocumentTitle("Mozo", currentBranch?.name || activeBranchScope?.name || "");
  }, [activeBranchScope?.name, currentBranch?.name]);

  useEffect(() => {
    setRestaurantProfile(getRestaurantProfile(activeBranchScope));

    const handleProfileUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = activeBranchScope?.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      setRestaurantProfile(
        event.detail?.profile || getRestaurantProfile(activeBranchScope)
      );
    };

    const handleStorage = (event) => {
      const expectedKey = buildRestaurantProfileStorageKey(activeBranchScope?.id);

      if (event.key !== expectedKey) {
        return;
      }

      setRestaurantProfile(getRestaurantProfile(activeBranchScope));
    };

    window.addEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope]);

  useEffect(() => {
    let active = true;

    const syncRemoteProfile = async () => {
      if (!activeBranchScope?.id || isLocalOnlyWaiterSession || !isSupabaseConfigured) {
        return;
      }

      try {
        const localProfile = getRestaurantProfile(activeBranchScope);
        const remoteProfile = await getBranchPublicProfile(activeBranchScope.id);
        const mergedProfile = saveRestaurantProfile(
          activeBranchScope,
          mergeRemoteBranchIntoProfilePreservingLocalPreferences(
            localProfile,
            remoteProfile
          )
        );

        if (!active) {
          return;
        }

        setRestaurantProfile(mergedProfile);
      } catch (error) {
        if (!active) {
          return;
        }

        console.error("No pudimos sincronizar la marca remota de la sucursal:", error);
      }
    };

    syncRemoteProfile();

    return () => {
      active = false;
    };
  }, [activeBranchScope, isLocalOnlyWaiterSession, isSupabaseConfigured]);

  useEffect(() => {
    let active = true;
    let retryTimer = null;

    const loadTables = async (attempt = 0) => {
      if (!currentBranch?.id) {
        if (active) {
          setTables([]);
          setLoadingTables(false);
        }
        return;
      }

      const savedTables = getBranchTablesSnapshot(currentBranch.id);

      if (attempt === 0 && savedTables.length > 0 && active) {
        setTables(savedTables);
        setLoadingTables(false);
      }

      if (!isSupabaseConfigured || isLocalOnlyWaiterSession) {
        if (active) {
          setTables(
            savedTables.length > 0
              ? savedTables
              : isLocalOnlyWaiterSession
                ? createDemoTables(currentBranch.id)
                : []
          );
          setTablesError("");
          setLoadingTables(false);
        }
        return;
      }

      setLoadingTables(true);
      setTablesError("");

      try {
        const nextTables = await listBranchTablesWithQrs(currentBranch.id);

        if (!active) {
          return;
        }

        const activeTables = nextTables.filter((table) => table.active !== false);
        saveBranchTablesSnapshot(currentBranch.id, activeTables);
        setTables(activeTables);
      } catch (error) {
        if (!active) {
          return;
        }

        if (isLocalOnlyWaiterSession) {
          setTables(
            savedTables.length > 0
              ? savedTables
              : createDemoTables(currentBranch.id)
          );
          setTablesError("");
        } else {
          if (savedTables.length > 0) {
            setTables(savedTables);
          }
          setTablesError(error.message || "No pudimos cargar las mesas registradas.");

          if (attempt < 2) {
            retryTimer = window.setTimeout(
              () => void loadTables(attempt + 1),
              1200 * (attempt + 1)
            );
          }
        }
      } finally {
        if (active) {
          setLoadingTables(false);
        }
      }
    };

    void loadTables();

    return () => {
      active = false;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [currentBranch?.id, isLocalOnlyWaiterSession, isSupabaseConfigured]);

  useEffect(() => {
    const branchId = currentBranch?.id;

    if (!branchId) {
      return undefined;
    }

    const syncBranchTables = () => {
      const nextTables = getBranchTablesSnapshot(branchId);
      if (nextTables.length > 0) {
        setTables(nextTables);
      }
    };

    const handleTablesUpdated = (event) => {
      const updatedBranchId = event.detail?.branchId || "default";
      if (updatedBranchId !== branchId) {
        return;
      }

      setTables(Array.isArray(event.detail?.tables) ? event.detail.tables : []);
    };

    const handleStorage = (event) => {
      if (event.key !== buildBranchTablesStorageKey(branchId)) {
        return;
      }

      syncBranchTables();
    };

    window.addEventListener(BRANCH_TABLES_UPDATED_EVENT, handleTablesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(BRANCH_TABLES_UPDATED_EVENT, handleTablesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentBranch?.id]);

  useEffect(() => {
    setWaiterOps(getWaiterOperationsState(activeBranchScope));
    setOperationalOrders(getOperationalOrdersState(activeBranchScope));
    setPromotions(getPromotions(activeBranchScope));
  }, [activeBranchScope]);

  const syncSharedOperationalState = useCallback(async () => {
    if (!activeBranchScope?.id || isLocalOnlyWaiterSession) {
      return;
    }

    try {
      await hydrateSharedOperationalStateToLocal(activeBranchScope, () =>
        EMPTY_OPERATIONAL_STATE
      );
    } catch (error) {
      console.error(
        "No pudimos sincronizar el estado operativo compartido del mozo:",
        error
      );
    }
  }, [activeBranchScope, isLocalOnlyWaiterSession]);

  useEffect(() => {
    if (!activeBranchScope?.id) {
      return undefined;
    }

    syncSharedOperationalState();

    const intervalId = window.setInterval(
      syncSharedOperationalState,
      SHARED_OPERATIONAL_SYNC_INTERVAL_MS
    );
    const handleFocus = () => {
      syncSharedOperationalState();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [activeBranchScope?.id, syncSharedOperationalState]);

  useEffect(() => {
    const restaurantId = activeBranchScope?.id || "default";

    const syncWaiterOps = () => {
      setWaiterOps(getWaiterOperationsState(activeBranchScope));
    };

    const handleWaiterUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      const nextWaiterOps =
        event.detail?.payload || getWaiterOperationsState(activeBranchScope);

      setWaiterOps(nextWaiterOps);

      if (event.detail?.source === WAITER_OPERATIONS_REMOTE_SYNC_SOURCE) {
        return;
      }

      if (!isLocalOnlyWaiterSession) {
        void pushSharedWaiterOperationsState(activeBranchScope, nextWaiterOps).catch(
          (error) => {
            console.error(
              "No pudimos sincronizar la operacion compartida de mozos:",
              error
            );
          }
        );
      }
    };

    const handleStorage = (event) => {
      if (event.key !== buildWaiterOperationsStorageKey(activeBranchScope?.id)) {
        return;
      }

      syncWaiterOps();
    };

    window.addEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope, isLocalOnlyWaiterSession]);

  const persistOperationalOrders = (nextState) => {
    const savedState = saveOperationalOrdersState(activeBranchScope, nextState);
    if (!isLocalOnlyWaiterSession) {
      void pushSharedOperationalState(activeBranchScope, savedState).catch((error) => {
        console.error(
          "No pudimos sincronizar el estado operativo compartido del mozo:",
          error
        );
      });
    }
    return savedState;
  };

  const persistPaymentOperations = (nextOperations) => {
    const savedOperations = savePaymentOperations(activeBranchScope, nextOperations);
    if (!isLocalOnlyWaiterSession) {
      void pushSharedPaymentOperationsState(activeBranchScope, savedOperations).catch(
        (error) => {
          console.error(
            "No pudimos sincronizar los cobros compartidos del mozo:",
            error
          );
        }
      );
    }
    return savedOperations;
  };

  const applySharedPaymentClaim = (payload) => {
    if (!payload || typeof payload !== "object") {
      return false;
    }

    const savedOrders = saveOperationalOrdersState(activeBranchScope, payload);
    const savedPayments = savePaymentOperations(
      activeBranchScope,
      payload.paymentOperations || []
    );

    setOperationalOrders(savedOrders);
    return {
      orders: savedOrders,
      payments: savedPayments,
    };
  };

  useEffect(() => {
    const restaurantId = activeBranchScope?.id || "default";

    const syncOperationalOrders = () => {
      setOperationalOrders(getOperationalOrdersState(activeBranchScope));
    };

    const handleOrdersUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      setOperationalOrders(event.detail?.payload || getOperationalOrdersState(activeBranchScope));
    };

    const handleStorage = (event) => {
      if (event.key !== buildOperationalOrdersStorageKey(activeBranchScope?.id)) {
        return;
      }

      syncOperationalOrders();
    };

    window.addEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope]);

  useEffect(() => {
    const restaurantId = activeBranchScope?.id || "default";
    let active = true;

    const syncPromotions = () => {
      setPromotions(getPromotions(activeBranchScope));
    };

    const syncRemotePromotions = async () => {
      if (!currentBranch?.id || isLocalOnlyWaiterSession || !isSupabaseConfigured) {
        syncPromotions();
        return;
      }

      try {
        const remotePromotions = await hydrateBranchPromotionsToLocal(
          activeBranchScope,
          currentBranch.id
        );

        if (!active) {
          return;
        }

        setPromotions(remotePromotions);
      } catch (error) {
        if (!active) {
          return;
        }

        syncPromotions();
      }
    };

    const handlePromotionsUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      if (Array.isArray(event.detail?.promotions)) {
        setPromotions(event.detail.promotions);
        return;
      }

      void syncRemotePromotions();
    };

    const handleStorage = (event) => {
      if (event.key !== buildPromotionsStorageKey(activeBranchScope?.id)) {
        return;
      }

      void syncRemotePromotions();
    };

    void syncRemotePromotions();
    window.addEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      active = false;
      window.removeEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [
    activeBranchScope,
    currentBranch?.id,
    isLocalOnlyWaiterSession,
    isSupabaseConfigured,
  ]);

  useEffect(() => {
    let active = true;

    const loadMenu = async () => {
      if (!currentBranch?.id) {
        if (active) {
          setMenuCatalog({ categories: [], products: [], linkedSubcategoryRules: [] });
        }
        return;
      }

      try {
        let snapshot = null;

        if (isSupabaseConfigured && !isLocalOnlyWaiterSession) {
          snapshot = await getBranchMenuSnapshot(currentBranch.id);
        } else {
          snapshot = loadLocalMenuSnapshot(currentBranch.id);
        }

        if (!snapshot?.products || snapshot.products.length === 0) {
          snapshot = findAnySavedMenuSnapshot() || snapshot;
        }

        const hasMissingIngredientNames = Array.isArray(snapshot?.products)
          ? snapshot.products.some((product) => {
              const ingredientsSource = Array.isArray(product?.ingredients)
                ? product.ingredients
                : Array.isArray(product?.ingredientes)
                  ? product.ingredientes
                  : [];

              return ingredientsSource.some(
                (ingredient) =>
                  (ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id) &&
                  !String(ingredient?.name || ingredient?.nombre || "").trim()
              );
            })
          : false;

        if (
          hasMissingIngredientNames &&
          isSupabaseConfigured &&
          !isLocalOnlyWaiterSession &&
          currentCompany?.id
        ) {
          try {
            const ingredientCatalog = await listIngredientes(currentCompany.id);
            snapshot = enrichSnapshotWithIngredientsCatalog(snapshot, ingredientCatalog);
          } catch (error) {
            // If the ingredient catalog cannot be loaded, keep the snapshot we already have.
          }
        }

        if (!active) {
          return;
        }

        const normalizedSnapshot = normalizeWaiterMenuSnapshot(snapshot);

        setMenuCatalog(normalizedSnapshot);
      } catch (error) {
        if (active) {
          const fallbackSnapshot =
            findAnySavedMenuSnapshot() || loadLocalMenuSnapshot(currentBranch.id);
          setMenuCatalog(normalizeWaiterMenuSnapshot(fallbackSnapshot));
        }
      }
    };

    loadMenu();

    return () => {
      active = false;
    };
  }, [
    currentBranch?.id,
    currentCompany?.id,
    isLocalOnlyWaiterSession,
    isSupabaseConfigured,
  ]);

  useEffect(() => {
    if (restaurantFaviconUrl) {
      applyHeadBrandingAssets(restaurantFaviconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [restaurantFaviconUrl]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setServiceRequestClock(Date.now());
    }, 10000);

    return () => window.clearInterval(timer);
  }, []);

  const assignedTables = useMemo(
    () =>
      (waiterOps.tableAssignments || []).filter(
        (assignment) => assignment.waiterUserId === user?.id
      ),
    [user?.id, waiterOps.tableAssignments]
  );

  const assignedTableIds = useMemo(
    () => new Set(assignedTables.map((assignment) => assignment.tableId)),
    [assignedTables]
  );
  const pendingServiceRequests = useMemo(
    () =>
      getPendingWaiterServiceRequests(
        waiterOps,
        currentBranch?.id || activeBranchScope?.id || ""
      ),
    [activeBranchScope?.id, currentBranch?.id, waiterOps]
  );
  const visibleServiceRequests = useMemo(
    () =>
      getVisibleWaiterServiceRequests(pendingServiceRequests, {
        branchId: currentBranch?.id || activeBranchScope?.id || "",
        waiterUserId: user?.id || "",
        assignedTableIds,
        now: serviceRequestClock,
      }),
    [
      activeBranchScope?.id,
      assignedTableIds,
      currentBranch?.id,
      serviceRequestClock,
      user?.id,
      pendingServiceRequests,
    ]
  );
  const visibleServiceRequestIds = useMemo(
    () => new Set(visibleServiceRequests.map((request) => request.id)),
    [visibleServiceRequests]
  );
  const tableAssignmentsById = useMemo(
    () =>
      new Map(
        (waiterOps.tableAssignments || [])
          .filter((assignment) => assignment?.tableId)
          .map((assignment) => [assignment.tableId, assignment])
      ),
    [waiterOps.tableAssignments]
  );

  const visibleAlerts = useMemo(
    () =>
      (waiterOps.deliveryAlerts || []).filter((alert) => {
        if (alert.status !== "pending") {
          return false;
        }

        if (alert.seenByUserIds?.includes(user?.id)) {
          return false;
        }

        if (alert.type === "service_request") {
          return visibleServiceRequestIds.has(alert.orderId);
        }

        if (alert.broadcast) {
          return true;
        }

        if (alert.targetWaiterIds?.includes(user?.id)) {
          return true;
        }

        return assignedTableIds.has(alert.tableId);
      }),
    [assignedTableIds, user?.id, visibleServiceRequestIds, waiterOps.deliveryAlerts]
  );
  const visibleAlertSoundKeys = useMemo(
    () => visibleAlerts.map((alert) => buildWaiterAlertSoundKey(alert)),
    [visibleAlerts]
  );
  const visibleDeliveryReadyAlerts = useMemo(
    () => visibleAlerts.filter(isDeliveryReadyAlert),
    [visibleAlerts]
  );
  const visibleTables = useMemo(() => {
    const catalog = new Map();
    const usedTableNumbers = new Set();
    const usedLabels = new Set();

    tables.forEach((table) => {
      if (table?.id) {
        catalog.set(table.id, table);
        if (Number(table.tableNumber || 0) > 0) {
          usedTableNumbers.add(Number(table.tableNumber));
        }
        if (table.label) {
          usedLabels.add(String(table.label).trim().toLowerCase());
        }
      }
    });

    (Array.isArray(operationalOrders.salonTables)
      ? operationalOrders.salonTables
      : []
    ).forEach((table) => {
      const normalizedTable = {
        ...table,
        id: String(table?.id || "").trim(),
        branchId: table?.branchId || currentBranch?.id || "",
        tableNumber: Number(table?.tableNumber || 0),
        label:
          table?.label ||
          (Number(table?.tableNumber || 0) > 0
            ? `Mesa ${table.tableNumber}`
            : "Mesa"),
        active: table?.active !== false,
      };
      const normalizedLabel = String(normalizedTable.label || "").trim().toLowerCase();
      const normalizedNumber = Number(normalizedTable.tableNumber || 0);

      if (
        normalizedTable.id &&
        normalizedTable.active &&
        !catalog.has(normalizedTable.id) &&
        !usedLabels.has(normalizedLabel) &&
        !(normalizedNumber > 0 && usedTableNumbers.has(normalizedNumber))
      ) {
        catalog.set(normalizedTable.id, normalizedTable);
        if (normalizedNumber > 0) {
          usedTableNumbers.add(normalizedNumber);
        }
        if (normalizedLabel) {
          usedLabels.add(normalizedLabel);
        }
      }
    });

    buildTablesFromOperationalOrders(
      currentBranch?.id,
      operationalOrders.mesasActivas || []
    ).forEach((table) => {
      const normalizedLabel = String(table?.label || "").trim().toLowerCase();
      const normalizedNumber = Number(table?.tableNumber || 0);

      if (
        table?.id &&
        !catalog.has(table.id) &&
        !usedLabels.has(normalizedLabel) &&
        !(normalizedNumber > 0 && usedTableNumbers.has(normalizedNumber))
      ) {
        catalog.set(table.id, table);
      }
    });

    if (catalog.size === 0 && operationalOrders.salonLayout) {
      Object.keys(operationalOrders.salonLayout).forEach((tableId, index) => {
        const id = String(tableId || "").trim();

        if (!id) {
          return;
        }

        const tableNumber = index + 1;
        catalog.set(id, {
          id,
          branchId: currentBranch?.id || "",
          tableNumber,
          label: `Mesa ${tableNumber}`,
          active: true,
        });
      });
    }

    return Array.from(catalog.values()).sort(
      (left, right) => Number(left.tableNumber || 0) - Number(right.tableNumber || 0)
    );
  }, [
    currentBranch?.id,
    operationalOrders.mesasActivas,
    operationalOrders.salonLayout,
    operationalOrders.salonTables,
    tables,
  ]);
  const visibleServiceRequestsByTableId = useMemo(() => {
    const requestsByTableId = new Map();

    visibleServiceRequests.forEach((request) => {
      const matchedTable = visibleTables.find((table) =>
        doesWaiterServiceRequestMatchTable(request, table)
      );

      if (!matchedTable) {
        return;
      }

      requestsByTableId.set(matchedTable.id, [
        ...(requestsByTableId.get(matchedTable.id) || []),
        request,
      ]);
    });

    return requestsByTableId;
  }, [visibleServiceRequests, visibleTables]);
  const salonRooms = useMemo(
    () => normalizeSalonRooms(operationalOrders.salonRooms),
    [operationalOrders.salonRooms]
  );

  useEffect(() => {
    if (!salonRooms.length) {
      return;
    }

    const roomExists = salonRooms.some((room) => room.id === selectedSalonRoomId);

    if (!roomExists) {
      setSelectedSalonRoomId(salonRooms[0].id);
    }
  }, [salonRooms, selectedSalonRoomId]);

  const resolvedSalonRoomId =
    selectedSalonRoomId || salonRooms[0]?.id || DEFAULT_SALON_ROOM_ID;
  const activeSalonRoom =
    salonRooms.find((room) => room.id === resolvedSalonRoomId) ||
    salonRooms[0] ||
    DEFAULT_SALON_ROOM;
  const selectedSalonTables = useMemo(() => {
    const defaultRoomId = salonRooms[0]?.id || DEFAULT_SALON_ROOM_ID;
    const tableRooms = operationalOrders.salonTableRooms || {};

    return visibleTables.filter(
      (table) => (tableRooms[table.id] || defaultRoomId) === resolvedSalonRoomId
    );
  }, [operationalOrders.salonTableRooms, resolvedSalonRoomId, salonRooms, visibleTables]);
  useEffect(() => {
    if (!visibleServiceRequestsByTableId.size) {
      return;
    }

    const currentRoomHasCall = selectedSalonTables.some((table) =>
      visibleServiceRequestsByTableId.has(table.id)
    );

    if (currentRoomHasCall) {
      return;
    }

    const firstCallingTableId = visibleServiceRequestsByTableId.keys().next().value;
    const targetRoomId =
      operationalOrders.salonTableRooms?.[firstCallingTableId] ||
      salonRooms[0]?.id ||
      DEFAULT_SALON_ROOM_ID;

    if (targetRoomId !== resolvedSalonRoomId) {
      setSelectedSalonRoomId(targetRoomId);
    }
  }, [
    operationalOrders.salonTableRooms,
    resolvedSalonRoomId,
    salonRooms,
    selectedSalonTables,
    visibleServiceRequestsByTableId,
  ]);
  const activeSalonFixtures = useMemo(
    () =>
      resolveWaiterFixturesForRoom(
        operationalOrders.salonFixtures,
        resolvedSalonRoomId
      ),
    [operationalOrders.salonFixtures, resolvedSalonRoomId]
  );
  const selectedTable = useMemo(
    () => visibleTables.find((table) => table.id === selectedTableId) || null,
    [selectedTableId, visibleTables]
  );
  const selectedTableServiceRequests = selectedTable
    ? visibleServiceRequestsByTableId.get(selectedTable.id) || []
    : [];
  const selectedTableAssignment = useMemo(
    () => (selectedTableId ? tableAssignmentsById.get(selectedTableId) || null : null),
    [selectedTableId, tableAssignmentsById]
  );
  const isSelectedTableAssignedToCurrentWaiter = Boolean(
    selectedTableAssignment?.waiterUserId &&
      selectedTableAssignment.waiterUserId === user?.id
  );
  const isSelectedTableAssignedToAnotherWaiter = Boolean(
    selectedTableAssignment?.waiterUserId &&
      selectedTableAssignment.waiterUserId !== user?.id
  );
  const selectedTableOrders = useMemo(
    () => getOperationalOrdersForTable(operationalOrders.mesasActivas, selectedTable),
    [operationalOrders.mesasActivas, selectedTable]
  );
  const activeOrderForSelectedTable = useMemo(() => {
    if (selectedExistingOrderId === NEW_ORDER_ACCOUNT_ID) return null;
    return (
      selectedTableOrders.find((order) => order.id === selectedExistingOrderId) ||
      selectedTableOrders[0] ||
      null
    );
  }, [selectedExistingOrderId, selectedTableOrders]);
  const isOrderEditorOpen = Boolean(
    (orderTargetMode === "table" && selectedTable) || orderTargetMode === "takeaway"
  );
  const selectedTableReservations = useMemo(
    () =>
      getSalonReservationsForTable(
        operationalOrders.salonReservations || {},
        selectedTable,
        serviceRequestClock
      ),
    [operationalOrders.salonReservations, selectedTable, serviceRequestClock]
  );
  const selectedTableReservation = useMemo(
    () =>
      getSalonReservationForTable(
        operationalOrders.salonReservations || {},
        selectedTable,
        serviceRequestClock
      ),
    [operationalOrders.salonReservations, selectedTable, serviceRequestClock]
  );
  const selectedTableNextReservation = selectedTableReservations[0] || null;
  const activeSalonOccupiedCount = useMemo(
    () =>
      selectedSalonTables.filter(
        (table) =>
          findOperationalOrderForTable(operationalOrders.mesasActivas, table) ||
          tableAssignmentsById.has(table.id)
      ).length,
    [operationalOrders.mesasActivas, selectedSalonTables, tableAssignmentsById]
  );
  const canTakeOrders =
    typeof hasWaiterPermission === "function"
      ? hasWaiterPermission("takeOrders")
      : true;
  const canModifyOrders =
    typeof hasWaiterPermission === "function"
      ? hasWaiterPermission("modifyOrders")
      : true;
  const canCancelOrders =
    typeof hasWaiterPermission === "function"
      ? hasWaiterPermission("cancelOrders")
      : true;
  const canChargeOrders =
    typeof hasWaiterPermission === "function"
      ? hasWaiterPermission("chargeOrders")
      : true;
  const chargePermissionMessage =
    "Cobro no habilitado para este mozo. Un administrador debe activar el permiso Cobrar desde Mozos > Editar permisos.";
  const ensureWaiterAudioContext = useCallback(async () => {
    if (typeof window === "undefined") {
      return null;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      return null;
    }

    if (!waiterAudioContextRef.current) {
      waiterAudioContextRef.current = new AudioContextClass();
    }

    const context = waiterAudioContextRef.current;

    if (context.state === "suspended") {
      try {
        await context.resume();
      } catch (error) {
        return null;
      }
    }

    return context;
  }, []);

  const playWaiterAlertRingtone = useCallback(async () => {
    const now = Date.now();

    if (now - waiterLastRingAtRef.current < 1200) {
      return;
    }

    const context = await ensureWaiterAudioContext();

    if (!context) {
      return;
    }

    waiterLastRingAtRef.current = now;
    scheduleWaiterRingtone(context);
  }, [ensureWaiterAudioContext]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const unlockAudio = () => {
      void ensureWaiterAudioContext();
    };

    window.addEventListener("pointerdown", unlockAudio, { passive: true });
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, [ensureWaiterAudioContext]);

  useEffect(() => {
    const nextKeys = new Set(visibleAlertSoundKeys);

    if (!waiterAlertSoundHydratedRef.current) {
      previousVisibleAlertKeysRef.current = nextKeys;
      waiterAlertSoundHydratedRef.current = true;
      return;
    }

    const hasNewVisibleAlert = visibleAlertSoundKeys.some(
      (key) => !previousVisibleAlertKeysRef.current.has(key)
    );

    previousVisibleAlertKeysRef.current = nextKeys;

    if (hasNewVisibleAlert) {
      void playWaiterAlertRingtone();
    }
  }, [playWaiterAlertRingtone, visibleAlertSoundKeys]);
  const menuCategories = useMemo(
    () => normalizeWaiterMenuSnapshot(menuCatalog).categories,
    [menuCatalog]
  );
  const menuSubcategories = useMemo(
    () => normalizeWaiterMenuSnapshot(menuCatalog).subcategories,
    [menuCatalog]
  );
  const normalizedMenuProducts = useMemo(
    () => normalizeWaiterMenuSnapshot(menuCatalog).products.map((product) =>
      withoutBeverageVarietyIngredients(product, menuCatalog.linkedSubcategoryRules || [])),
    [menuCatalog]
  );
  const linkedSubcategoryRules = useMemo(
    () =>
      Array.isArray(menuCatalog?.linkedSubcategoryRules)
        ? menuCatalog.linkedSubcategoryRules
        : [],
    [menuCatalog]
  );
  const linkedRulesByProductId = useMemo(
    () => indexLinkedRulesByProduct(linkedSubcategoryRules, normalizedMenuProducts),
    [linkedSubcategoryRules, normalizedMenuProducts]
  );
  const hiddenStandaloneProductIds = useMemo(
    () => getLinkedOnlyProductIds(linkedSubcategoryRules, normalizedMenuProducts),
    [linkedSubcategoryRules, normalizedMenuProducts]
  );
  const productsById = useMemo(
    () => new Map(normalizedMenuProducts.map((product) => [product.id, product])),
    [normalizedMenuProducts]
  );
  const selectedMenuProduct = selectedMenuProductId
    ? productsById.get(selectedMenuProductId) || null
    : null;
  const selectedMenuProductLinkedRules = useMemo(
    () =>
      linkedRulesByProductId.get(selectedMenuProduct?.id) || [],
    [linkedRulesByProductId, selectedMenuProduct]
  );
  const selectedMenuProductLinkedRule = selectedMenuProductLinkedRules.find((rule) => !isIngredientLinkedRule(rule)) || null;
  const beverageRules = selectedMenuProductLinkedRules.filter(isIngredientLinkedRule);
  const beverageOptions = beverageRules.flatMap((rule) => resolveLinkedRuleProducts(rule));
  const selectedBeverageOption = beverageOptions.find((option) => option.targetIngredientId === draftBeverageIngredientId) || null;
  const selectedMenuProductLinkedTitle = useMemo(() => {
    const titles = Array.from(
      new Set(
        selectedMenuProductLinkedRules
          .map((rule) => String(rule?.title || "").trim())
          .filter(Boolean)
      )
    );

    return titles.length === 1 ? titles[0] : "Elegi una combinacion";
  }, [selectedMenuProductLinkedRules]);
  const selectedLinkedOptions = useMemo(() => {
    const optionsById = new Map();

    selectedMenuProductLinkedRules.forEach((rule) => {
      if (isIngredientLinkedRule(rule)) return;
      resolveLinkedRuleProducts(rule, normalizedMenuProducts, selectedMenuProductId)
        .forEach((product) => optionsById.set(product.id, product));
    });

    return Array.from(optionsById.values()).sort((left, right) =>
      String(left?.nombre || left?.name || "").localeCompare(
        String(right?.nombre || right?.name || ""),
        "es"
      )
    );
  }, [normalizedMenuProducts, selectedMenuProductId, selectedMenuProductLinkedRules]);
  const selectedLinkedProduct = draftLinkedProductId
    ? productsById.get(draftLinkedProductId) || null
    : null;
  const selectedPizzaSizeOptions = useMemo(
    () => getPizzaSizeOptions(selectedMenuProduct),
    [selectedMenuProduct]
  );
  const selectedEmpanadaSaleOptions = useMemo(
    () => getEmpanadaSaleOptions(selectedMenuProduct),
    [selectedMenuProduct]
  );
  const selectedEmpanadaCookingOptions = useMemo(
    () => getEmpanadaCookingOptions(selectedMenuProduct),
    [selectedMenuProduct]
  );
  const selectedPizzaSizeOption = useMemo(
    () =>
      selectedPizzaSizeOptions.find(
        (option) => option.id === draftPizzaSizeOptionId
      ) ||
      selectedPizzaSizeOptions[0] ||
      null,
    [draftPizzaSizeOptionId, selectedPizzaSizeOptions]
  );
  const selectedEmpanadaSaleOption = useMemo(
    () =>
      selectedEmpanadaSaleOptions.find(
        (option) => option.id === draftEmpanadaSaleOptionId
      ) ||
      selectedEmpanadaSaleOptions[0] ||
      null,
    [draftEmpanadaSaleOptionId, selectedEmpanadaSaleOptions]
  );
  const selectedEmpanadaCookingOption = useMemo(
    () =>
      selectedEmpanadaCookingOptions.find(
        (option) => option.id === draftEmpanadaCookingOptionId
      ) ||
      selectedEmpanadaCookingOptions[0] ||
      null,
    [draftEmpanadaCookingOptionId, selectedEmpanadaCookingOptions]
  );
  const selectedPizzaHalfOptions = useMemo(
    () =>
      buildPizzaHalfAndHalfOptions(
        selectedMenuProduct,
        normalizedMenuProducts,
        menuCategories,
        {
          sizeOptionId: selectedPizzaSizeOption?.id || "",
          subcategories: menuSubcategories,
        }
      ),
    [
      menuCategories,
      menuSubcategories,
      normalizedMenuProducts,
      selectedMenuProduct,
      selectedPizzaSizeOption?.id,
    ]
  );
  const selectedPizzaHalfProduct = useMemo(
    () =>
      selectedPizzaHalfOptions.find(
        (option) => option.id === draftPizzaHalfProductId
      ) || null,
    [draftPizzaHalfProductId, selectedPizzaHalfOptions]
  );
  const selectedPizzaHalfSelection = useMemo(
    () =>
      createPizzaHalfAndHalfSelection(selectedMenuProduct, selectedPizzaHalfProduct, {
        sizeOptionId: selectedPizzaSizeOption?.id || "",
      }),
    [selectedMenuProduct, selectedPizzaHalfProduct, selectedPizzaSizeOption?.id]
  );
  const waiterMenuProducts = useMemo(() => {
    const baseProducts = normalizedMenuProducts.filter(
      (product) => !hiddenStandaloneProductIds.has(product.id)
    );

    const byCategory = tableOrderCategoryId
      ? baseProducts.filter((product) => product.categoria === tableOrderCategoryId)
      : baseProducts;
    const query = tableOrderSearch.trim().toLowerCase();

    if (!query) {
      return byCategory;
    }

    return byCategory.filter((product) =>
      `${product.nombre || ""} ${product.subcategoria || ""}`.toLowerCase().includes(query)
    );
  }, [hiddenStandaloneProductIds, normalizedMenuProducts, tableOrderCategoryId, tableOrderSearch]);
  const waiterAvailableCombos = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false &&
          getPromotionRecordType(promotion) === "combo" &&
          isPromotionAvailableNow(promotion, new Date()) &&
          Array.isArray(promotion.items) &&
          promotion.items.length > 0
      ),
    [promotions]
  );
  const waiterAvailablePromotions = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false &&
          getPromotionRecordType(promotion) === "promotion" &&
          isPromotionAvailableNow(promotion, new Date())
      ),
    [promotions]
  );
  const tableOrderPricing = useMemo(
    () => buildPromotionPricing(tableOrderItems, promotions, normalizedMenuProducts),
    [normalizedMenuProducts, promotions, tableOrderItems]
  );
  const selectedMenuProductSummary = useMemo(
    () => getWaiterCustomizationSummary(selectedMenuProduct, draftSelection),
    [draftSelection, selectedMenuProduct]
  );
  const selectedMenuProductUnitPrice = useMemo(() => {
    if (!selectedMenuProduct) {
      return 0;
    }

    return (
      (selectedPizzaSizeOption
        ? resolvePizzaProductPrice(selectedMenuProduct, {
            sizeOption: selectedPizzaSizeOption,
            portionMode: selectedPizzaHalfSelection ? "half_and_half" : draftPizzaPortionMode,
          })
        : selectedEmpanadaSaleOption
          ? resolveEmpanadaProductPrice(selectedMenuProduct, {
              saleOption: selectedEmpanadaSaleOption,
            })
        : resolveMenuProductPrice(selectedMenuProduct)) +
      Number(
        selectedPizzaHalfSelection
          ? selectedPizzaHalfSelection.unitPrice
          : selectedLinkedProduct
            ? resolveMenuProductPrice(selectedLinkedProduct)
            : 0
      ) +
      Number(selectedMenuProductSummary?.extraCost || 0)
    );
  }, [
    selectedLinkedProduct,
    selectedMenuProduct,
    selectedMenuProductSummary?.extraCost,
    selectedEmpanadaSaleOption,
    selectedPizzaHalfSelection,
    selectedPizzaSizeOption,
    draftPizzaPortionMode,
  ]);
  const activeOrderSummary = useMemo(() => {
    if (!activeOrderForSelectedTable) {
      return null;
    }

    return {
      phaseLabel:
        ORDER_PHASE_LABELS[activeOrderForSelectedTable.phase] ||
        activeOrderForSelectedTable.phase ||
        "Pedido activo",
      sourceLabel:
        ORDER_SOURCE_LABELS[activeOrderForSelectedTable.source] ||
        activeOrderForSelectedTable.source ||
        "Pedido",
      updatedAt:
        formatDateTime(
          activeOrderForSelectedTable.updatedAt || activeOrderForSelectedTable.orderedAt
        ) || "",
      customerName: getOperationalCustomerName(activeOrderForSelectedTable),
    };
  }, [activeOrderForSelectedTable]);
  const canEditSelectedTableOrder =
    orderTargetMode === "takeaway"
      ? canTakeOrders
      : !isSelectedTableAssignedToAnotherWaiter &&
        (activeOrderForSelectedTable
          ? canModifyOrders && activeOrderForSelectedTable.phase === "new"
          : canTakeOrders && !selectedTableReservation);
  const tableOrderPermissionMessage =
    orderTargetMode === "takeaway"
      ? "Tu acceso no tiene permiso para cargar pedidos para retirar."
      : isSelectedTableAssignedToAnotherWaiter
        ? `Esta mesa la atiende ${selectedTableAssignment?.waiterName || "otro mozo"}. Puedes ver el detalle, pero no modificarlo.`
        : selectedTableReservation && !activeOrderForSelectedTable
          ? `Esta mesa esta bloqueada por la reserva de ${selectedTableReservation.customerName || "un cliente"} para ${formatReservationTime(selectedTableReservation.reservedFor)}.`
        : activeOrderForSelectedTable
          ? activeOrderForSelectedTable.phase !== "new"
            ? canChargeOrders
              ? "Pedido en cocina o entrega: no se pueden cambiar los productos ni cancelar el pedido. El cobro sigue habilitado."
              : "Pedido en cocina o entrega: no se pueden cambiar los productos ni cancelar el pedido."
            : "Tu acceso no tiene permiso para modificar pedidos desde el panel del mozo."
          : "Tu acceso no tiene permiso para tomar pedidos o abrir mesas nuevas.";

  useEffect(() => {
    if (!isOrderEditorOpen) {
      setTableOrderItems([]);
      setTableOrderCategoryId("");
      setTableOrderSearch("");
      setTableOrderMessage("");
      setOrderCustomerName("");
      return;
    }

    setTableOrderItems(
      normalizeOperationalOrderItems(activeOrderForSelectedTable?.items || []).map((item) => {
        if (Array.isArray(item.consumos) && item.consumos.length > 0) {
          return item;
        }

        const product = productsById.get(item.sourceProductId || item.productId);

        if (!product) {
          return item;
        }

        const linkedProduct = item.linkedProductId
          ? productsById.get(item.linkedProductId) || null
          : null;
        const pizzaHalfSelection =
          getPizzaHalfAndHalfSelection(item.linkedSelections || []) ||
          (isPizzaHalfAndHalfSelection(item) ? item : null);

        return {
          ...item,
          consumos: buildWaiterOrderConsumptions(product, {
            selection: item.selection || {},
            quantity: item.quantity || 1,
            linkedProduct,
            pizzaHalfSelection,
            pizzaSizeOption: findPizzaSizeOption(product, item.pizzaSizeOptionId),
            pizzaPortionMode: item.pizzaPortionMode || "full",
          }),
        };
      })
    );
    setTableOrderCategoryId("");
    setTableOrderSearch("");
    setTableOrderMessage("");
    setOrderCustomerName(getOperationalCustomerName(activeOrderForSelectedTable));
    setSelectedMenuProductId("");
    setEditingTableOrderItemId("");
    setDraftSelection({});
    setDraftLinkedProductId("");
    setDraftPizzaSizeOptionId("");
    setDraftPizzaHalfProductId("");
    setDraftProductQuantity(1);
  }, [activeOrderForSelectedTable, isOrderEditorOpen, productsById]);

  useEffect(() => {
    if (!isOrderEditorOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeTableModal();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOrderEditorOpen]);

  const handleSelectTable = (table) => {
    if (!table?.id) {
      return;
    }

    const tableOrders = getOperationalOrdersForTable(
      operationalOrders.mesasActivas,
      table
    );
    const blockingReservation = getSalonReservationForTable(
      operationalOrders.salonReservations || {},
      table,
      Date.now()
    );

    if (!tableOrders.length && blockingReservation) {
      setPanelMessage(
        `${table.label || `Mesa ${table.tableNumber}`} esta bloqueada por la reserva de ${
          blockingReservation.customerName || "un cliente"
        } para ${formatReservationTime(blockingReservation.reservedFor)}.`
      );
      return;
    }

    setOrderTargetMode("table");
    setSelectedTableId(table.id);
    setSelectedExistingOrderId(tableOrders[0]?.id || NEW_ORDER_ACCOUNT_ID);
    setOrderWorkspaceTab(tableOrders.length > 0 ? "order" : "menu");
    setPanelMessage("");
  };
  const openTakeawayOrder = () => {
    if (!canTakeOrders) {
      setPanelMessage("Tu permiso actual no permite tomar pedidos para retirar.");
      return;
    }

    setOrderTargetMode("takeaway");
    setSelectedTableId("");
    setSelectedExistingOrderId(NEW_ORDER_ACCOUNT_ID);
    setOrderCustomerName("");
    setOrderWorkspaceTab("menu");
    setPanelMessage("");
  };
  const closeTableModal = () => {
    setOrderTargetMode("table");
    setSelectedTableId("");
    setSelectedExistingOrderId("");
    setOrderWorkspaceTab("order");
    setOrderCustomerName("");
    setTableOrderItems([]);
    setTableOrderCategoryId("");
    setTableOrderSearch("");
    setTableOrderMessage("");
    setSelectedMenuProductId("");
    setEditingTableOrderItemId("");
    setDraftSelection({});
    setDraftLinkedProductId("");
    setDraftPizzaSizeOptionId("");
    setDraftPizzaHalfProductId("");
    setDraftProductQuantity(1);
  };
  const releaseSelectedTable = () => {
    if (!selectedTable || !activeBranchScope?.id || !user?.id) {
      return;
    }

    releaseWaiterTableAssignment(activeBranchScope, selectedTable.id, user.id);
    setPanelMessage(
      `${selectedTable.label || `Mesa ${selectedTable.tableNumber}`} quedo liberada para que la tome otro mozo.`
    );
    closeTableModal();
  };
  const openWaiterProductDetail = (product, existingItem = null) => {
    if (!product) {
      return;
    }

    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    const linkedRules = linkedRulesByProductId.get(product.id) || [];
    const linkedOptionsById = new Map();
    linkedRules.forEach((rule) => {
      resolveLinkedRuleProducts(rule, normalizedMenuProducts, product.id)
        .forEach((candidate) => linkedOptionsById.set(candidate.id, candidate));
    });
    const linkedOptions = Array.from(linkedOptionsById.values());
    const previousPizzaHalfSelection =
      getPizzaHalfAndHalfSelection(existingItem?.linkedSelections || []) ||
      (isPizzaHalfAndHalfSelection(existingItem) ? existingItem : null);
    const pizzaSizeOptions = getPizzaSizeOptions(product);
    const nextPizzaSizeOption =
      pizzaSizeOptions.find(
        (option) =>
          option.id ===
          normalizeEntityId(
            existingItem?.pizzaSizeOptionId ||
              previousPizzaHalfSelection?.pizzaSizeOptionId ||
              ""
          )
      ) ||
      pizzaSizeOptions[0] ||
      null;
    const pizzaHalfOptions = buildPizzaHalfAndHalfOptions(
      product,
      normalizedMenuProducts,
      menuCategories,
      {
        sizeOptionId: nextPizzaSizeOption?.id || "",
        subcategories: menuSubcategories,
      }
    );
    const nextPizzaHalfProductId = pizzaHalfOptions.some(
      (candidate) => candidate.id === previousPizzaHalfSelection?.productId
    )
      ? previousPizzaHalfSelection.productId
      : "";
    const previousLinkedProductId = nextPizzaHalfProductId
      ? ""
      : existingItem?.linkedProductId || "";
    const nextLinkedProductId = linkedOptions.some(
      (candidate) => candidate.id === previousLinkedProductId
    )
      ? previousLinkedProductId
      : "";
    const empanadaSaleOptions = getEmpanadaSaleOptions(product);
    const nextEmpanadaSaleOption =
      empanadaSaleOptions.find(
        (option) => option.id === normalizeEntityId(existingItem?.empanadaSaleOptionId || "")
      ) ||
      empanadaSaleOptions[0] ||
      null;
    const empanadaCookingOptions = getEmpanadaCookingOptions(product);
    const nextEmpanadaCookingOption =
      empanadaCookingOptions.find(
        (option) => option.id === normalizeEntityId(existingItem?.empanadaCookingOptionId || "")
      ) ||
      empanadaCookingOptions[0] ||
      null;

    setSelectedMenuProductId(product.id);
    setDraftBeverageIngredientId(existingItem?.linkedSelections?.find((entry) => entry.targetIngredientId)?.targetIngredientId || "");
    setEditingTableOrderItemId(existingItem?.id || "");
    setDraftSelection(createWaiterDraftSelection(product, existingItem?.selection || null));
    setDraftLinkedProductId(nextLinkedProductId);
    setDraftPizzaSizeOptionId(nextPizzaSizeOption?.id || "");
    setDraftPizzaPortionMode(nextPizzaHalfProductId ? "half_and_half" : existingItem?.pizzaPortionMode || getDefaultPizzaSaleMode(nextPizzaSizeOption));
    setDraftPizzaHalfProductId(nextPizzaHalfProductId);
    setDraftEmpanadaSaleOptionId(nextEmpanadaSaleOption?.id || "");
    setDraftEmpanadaCookingOptionId(nextEmpanadaCookingOption?.id || "");
    setDraftProductQuantity(Math.max(1, Number(existingItem?.quantity || 1)));
  };
  const closeWaiterProductDetail = () => {
    setSelectedMenuProductId("");
    setEditingTableOrderItemId("");
    setDraftSelection({});
    setDraftLinkedProductId("");
    setDraftPizzaSizeOptionId("");
    setDraftPizzaHalfProductId("");
    setDraftEmpanadaSaleOptionId("");
    setDraftEmpanadaCookingOptionId("");
    setDraftProductQuantity(1);
  };
  const updateWaiterIngredientQuantity = (ingredient, change) => {
    setDraftSelection((prev) => {
      const baseQuantity = Number(ingredient.baseQuantity || 0);
      const current = Number(prev?.[ingredient.id] ?? baseQuantity);
      const minValue = ingredient.canRemove ? 0 : baseQuantity;
      const maxValue = ingredient.canAddMore
        ? baseQuantity +
          Math.max(1, Number(ingredient.maxExtraQuantity || DEFAULT_MAX_EXTRA_QUANTITY))
        : baseQuantity;
      const nextValue = Math.min(maxValue, Math.max(minValue, current + change));

      return {
        ...prev,
        [ingredient.id]: nextValue,
      };
    });
  };
  const resetWaiterProductCustomization = () => {
    if (!selectedMenuProduct) {
      return;
    }

    setDraftSelection(createWaiterDraftSelection(selectedMenuProduct));
    setDraftBeverageIngredientId("");
    setDraftLinkedProductId("");
    setDraftPizzaSizeOptionId(selectedPizzaSizeOptions[0]?.id || "");
    setDraftPizzaPortionMode(getDefaultPizzaSaleMode(selectedPizzaSizeOptions[0]));
    setDraftPizzaHalfProductId("");
    setDraftEmpanadaSaleOptionId(selectedEmpanadaSaleOptions[0]?.id || "");
    setDraftEmpanadaCookingOptionId(
      selectedEmpanadaCookingOptions[0]?.id || ""
    );
  };
  const validateTableOrderStock = (items) => {
    const pizzaValidation = validatePizzaCartItems(items, [...productsById.values()]);
    if (!pizzaValidation.ok) return { ok: false, selectionError: pizzaValidation.message };
    const invalid = (items || []).find((item) => !resolveBeverageSelection(
      productsById.get(item.productId || item.sourceProductId), item.linkedSelections || [], linkedSubcategoryRules
    ).valid);
    if (invalid) return { ok: false, selectionError: `Elegi una variedad para ${invalid.productName || "la bebida"}.` };
    return validateIngredientStock(
      activeBranchScope,
      (Array.isArray(items) ? items : []).reduce(
        (acc, item) => acc.concat(Array.isArray(item?.consumos) ? item.consumos : []),
        []
      )
    );
  };
  const addSelectedProductToTableOrder = () => {
    if (!selectedMenuProduct) {
      return;
    }

    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    const linkedProduct = selectedPizzaHalfProduct || (draftLinkedProductId
      ? productsById.get(draftLinkedProductId) || null
      : null);
    const nextItem = createWaiterOrderItem(
      selectedMenuProduct,
      draftProductQuantity,
      selectedMenuProduct.ingredients?.length > 0 ? { ...draftSelection } : {},
      linkedProduct,
      selectedPizzaHalfSelection,
      selectedPizzaSizeOption,
      selectedEmpanadaSaleOption,
      selectedEmpanadaCookingOption,
      selectedBeverageOption,
      draftPizzaPortionMode
    );
    const finalItem = editingTableOrderItemId
      ? {
          ...nextItem,
          id: editingTableOrderItemId,
        }
      : nextItem;
    const nextItems = editingTableOrderItemId
      ? tableOrderItems.map((item) =>
          item.id === editingTableOrderItemId ? finalItem : item
        )
      : [...tableOrderItems, finalItem];
    const stockValidation = validateTableOrderStock(nextItems);

    if (!stockValidation.ok) {
      setTableOrderMessage(
        getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para sumar ese plato al pedido de la mesa.",
        })
      );
      return;
    }

    setTableOrderItems(nextItems);
    setTableOrderMessage(
      editingTableOrderItemId
        ? `${selectedMenuProduct.nombre} se actualizo en el pedido de la mesa.`
        : `${selectedMenuProduct.nombre} se sumo al pedido de la mesa.`
    );
    closeWaiterProductDetail();
  };
  const buildPendingTableOrderItems = () => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return null;
    }

    if (!selectedMenuProduct) {
      return tableOrderItems;
    }

    const linkedProduct = selectedPizzaHalfProduct || (draftLinkedProductId
      ? productsById.get(draftLinkedProductId) || null
      : null);
    const draftItem = createWaiterOrderItem(
      selectedMenuProduct,
      draftProductQuantity,
      selectedMenuProduct.ingredients?.length > 0 ? { ...draftSelection } : {},
      linkedProduct,
      selectedPizzaHalfSelection,
      selectedPizzaSizeOption,
      selectedEmpanadaSaleOption,
      selectedEmpanadaCookingOption,
      selectedBeverageOption,
      draftPizzaPortionMode
    );
    const finalDraftItem = editingTableOrderItemId
      ? {
          ...draftItem,
          id: editingTableOrderItemId,
        }
      : draftItem;

    return editingTableOrderItemId
      ? tableOrderItems.map((item) =>
          item.id === editingTableOrderItemId ? finalDraftItem : item
        )
      : [...tableOrderItems, finalDraftItem];
  };
  const addComboToTableOrder = (combo) => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    const comboItems = Array.isArray(combo?.items) ? combo.items : [];

    if (!comboItems.length) {
      setTableOrderMessage("Ese combo todavia no tiene productos configurados.");
      return;
    }

    const nextItems = comboItems
      .map((comboItem) => {
        const product = productsById.get(comboItem.productId);

        if (!product) {
          return null;
        }

        const quantity = Math.max(1, Number(comboItem.quantity || 1));
        const nextItem = createWaiterOrderItem(product, quantity);

        return {
          ...nextItem,
          selectionDetails: [`Combo: ${combo.title}`],
          baseUnitPrice:
            comboItem.unitPrice !== undefined
              ? Number(comboItem.unitPrice || 0)
              : nextItem.baseUnitPrice,
          unitPrice:
            comboItem.unitPrice !== undefined
              ? Number(comboItem.unitPrice || 0)
              : nextItem.unitPrice,
          consumos: buildOperationalConsumptions(product, {
            quantity,
          }),
        };
      })
      .filter(Boolean);

    if (!nextItems.length) {
      setTableOrderMessage("No encontramos los productos reales de ese combo en el menu actual.");
      return;
    }

    const nextOrderItems = [...tableOrderItems, ...nextItems];
    const stockValidation = validateTableOrderStock(nextOrderItems);

    if (!stockValidation.ok) {
      setTableOrderMessage(
        getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para sumar ese combo al pedido de la mesa.",
        })
      );
      return;
    }

    setTableOrderItems(nextOrderItems);
    setTableOrderMessage(`Se agrego el combo "${combo.title}" al pedido de la mesa.`);
  };
  const applyPromotionFilter = (promotion) => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    setTableOrderCategoryId(promotion?.categoryId || "");
    setTableOrderSearch("");
    setTableOrderMessage(
      promotion?.title
        ? `Filtramos la carta para trabajar con la promo "${promotion.title}".`
        : "Filtramos la carta segun la promo elegida."
    );
  };
  const removeTableOrderItem = (itemId) => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    setTableOrderItems((prev) => prev.filter((item) => item.id !== itemId));
  };
  const editTableOrderItem = (item) => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    const product = productsById.get(item.sourceProductId || item.productId);

    if (!product) {
      setTableOrderMessage("No pudimos reabrir ese item porque ya no existe en la carta actual.");
      return;
    }

    openWaiterProductDetail(product, item);
  };
  const updateDraftItemQuantity = (itemId, change) => {
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    setTableOrderItems((prev) =>
      prev.flatMap((item) => {
        if (item.id !== itemId) {
          return [item];
        }

        if (!item.sourceProductId) {
          return [item];
        }

        const nextQuantity = Math.max(1, Number(item.quantity || 1) + change);
        const product = productsById.get(item.sourceProductId || item.productId);
        const linkedProduct = item.linkedProductId
          ? productsById.get(item.linkedProductId) || null
          : null;
        const pizzaHalfSelection =
          getPizzaHalfAndHalfSelection(item.linkedSelections || []) ||
          (isPizzaHalfAndHalfSelection(item) ? item : null);

        return [
          {
            ...item,
            quantity: nextQuantity,
            nombre: buildOperationalItemLabel(
              nextQuantity,
              item.sourceProductName || item.productName || item.nombre,
              item.selectionDetails || []
            ),
            consumos: product && !item.linkedSelections?.some((entry) => entry.targetIngredientId)
              ? buildWaiterOrderConsumptions(product, {
                  selection: item.selection || {},
                  quantity: nextQuantity,
                  linkedProduct,
                  pizzaHalfSelection,
                  pizzaSizeOption: findPizzaSizeOption(product, item.pizzaSizeOptionId),
                  pizzaPortionMode: item.pizzaPortionMode || "full",
                })
              : scaleOperationalConsumptions(
                  item.consumos || [],
                  item.quantity || 1,
                  nextQuantity
                ),
          },
        ];
      })
    );
  };
  const saveTableOrderChanges = async () => {
    if (newOrderCheckRef.current) return;
    if (!canEditSelectedTableOrder) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    const isTakeawayOrder = orderTargetMode === "takeaway";

    if (!isTakeawayOrder && !selectedTable) {
      return;
    }

    if (!isTakeawayOrder && !activeOrderForSelectedTable) {
      const blockingReservation = getSalonReservationForTable(
        operationalOrders.salonReservations || {},
        selectedTable,
        Date.now()
      );

      if (blockingReservation) {
        setTableOrderMessage(
          `La mesa esta bloqueada por la reserva de ${
            blockingReservation.customerName || "un cliente"
          } para ${formatReservationTime(blockingReservation.reservedFor)}.`
        );
        return;
      }
    }

    const pendingItems = buildPendingTableOrderItems();

    if (!pendingItems) {
      return;
    }

    if (!pendingItems.length) {
      setTableOrderMessage("Agrega al menos un plato para guardar el pedido de esta mesa.");
      return;
    }

    const stockValidation = validateTableOrderStock(pendingItems);

    if (!stockValidation.ok) {
      setTableOrderMessage(
        getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para guardar este pedido de la mesa.",
        })
      );
      return;
    }

    if ((!activeOrderForSelectedTable || hasAdditionalOrderItems(activeOrderForSelectedTable.items || [], pendingItems))
      && !isLocalOnlyWaiterSession && isSupabaseConfigured) {
      newOrderCheckRef.current = true;
      try { await assertAcceptingNewOrders(activeBranchScope.id); }
      catch (error) { if (orderEditorScopeRef.current === orderEditorScope) setTableOrderMessage(error.message); return; }
      finally { newOrderCheckRef.current = false; }
      if (orderEditorScopeRef.current !== orderEditorScope) return;
    }
    const now = new Date().toISOString();
    const pendingPricing = buildPromotionPricing(
      pendingItems,
      promotions,
      normalizedMenuProducts
    );
    const totalAmount = Number(pendingPricing.finalTotal || 0);
    const customerName =
      orderCustomerName.trim() ||
      (isTakeawayOrder
        ? "Retiro mostrador"
        : `Comensal ${Math.max(1, selectedTableOrders.length + 1)}`);
    const orderTableLabel = isTakeawayOrder
      ? "Para retirar"
      : selectedTable.label || `Mesa ${selectedTable.tableNumber}`;

    const nextOrder = activeOrderForSelectedTable
      ? {
          ...activeOrderForSelectedTable,
          mesa: orderTableLabel,
          tableId: isTakeawayOrder ? "" : selectedTable.id,
          customerName,
          customerAlias: customerName,
          kitchenModeSnapshot:
            activeOrderForSelectedTable.kitchenModeSnapshot ||
            restaurantProfile?.kitchenMode ||
            "manual",
          items: normalizeOperationalOrderItems(pendingItems),
          pricing: {
            ...pendingPricing,
          },
          totalAmount,
          updatedAt: now,
        }
      : {
          id: `waiter-order-${Date.now()}`,
          mesa: orderTableLabel,
          tableId: isTakeawayOrder ? "" : selectedTable.id,
          comensales: 1,
          canal: isTakeawayOrder ? "Retiro" : "Salon",
          serviceType: isTakeawayOrder ? "takeaway" : "dine_in",
          phase: "new",
          source: "waiter",
          customerName,
          customerAlias: customerName,
          cashPaymentPending: true,
          requestedPaymentMethod: "cash",
          kitchenModeSnapshot: restaurantProfile?.kitchenMode || "manual",
          stockDiscounted: false,
          orderedAt: now,
          items: normalizeOperationalOrderItems(pendingItems),
          pricing: {
            ...pendingPricing,
          },
          notes: "",
          totalAmount,
        };

    const nextState = activeOrderForSelectedTable
      ? {
          ...operationalOrders,
          mesasActivas: (operationalOrders.mesasActivas || []).map((mesa) =>
            mesa.id === activeOrderForSelectedTable.id ? nextOrder : mesa
          ),
        }
      : {
          ...operationalOrders,
          mesasActivas: [nextOrder, ...(operationalOrders.mesasActivas || [])],
        };

    if (
      !isTakeawayOrder &&
      !activeOrderForSelectedTable &&
      activeBranchScope?.id &&
      user?.id
    ) {
      assignWaiterToTable(activeBranchScope, {
        tableId: selectedTable.id,
        tableLabel: selectedTable.label || `Mesa ${selectedTable.tableNumber}`,
        branchId: currentBranch?.id || "",
        waiterUserId: user.id,
        waiterName:
          currentUserProfile?.fullName ||
          currentUserProfile?.email ||
          "Mozo activo",
      });
    }

    persistOperationalOrders(nextState);
    setPanelMessage(
      isTakeawayOrder
        ? `El pedido para retirar de ${customerName} quedo cargado.`
        : activeOrderForSelectedTable
          ? `${orderTableLabel} actualizo el pedido de ${customerName}.`
          : `${orderTableLabel} ya tiene una cuenta para ${customerName}.`
    );
    closeTableModal();
  };
  const closeWaiterChargeDialog = () => {
    if (waiterChargeBusyRef.current) {
      return;
    }

    waiterChargeRequestVersionRef.current += 1;
    waiterChargeRefreshRequestRef.current = null;
    setWaiterChargeRefreshing(false);
    setWaiterChargeDialog(null);
    setWaiterChargeIntent(null);
    setWaiterChargeMessage("");
    setWaiterQrCancelConfirmation(false);
    waiterChargeFinalizingIntentRef.current = "";
  };

  const chargeOperationalOrder = (order, { cancelQr = false } = {}) => {
    if (!canChargeOrders) {
      setTableOrderMessage(chargePermissionMessage);
      return;
    }

    const branchContext = activeBranchScope || currentBranch || currentCompany || {};
    const currentPayments = getPaymentOperations(branchContext);
    const paymentState = resolveOperationalOrderPaymentState(
      order,
      currentPayments,
      order?.paymentOperationId
    );
    const existingPayment = paymentState.paymentOperation;
    const paymentStatus = String(existingPayment?.status || "").toLowerCase();
    const pendingWaiterQrData = getInteroperableQrData(existingPayment);
    const hasPendingWaiterQrPayment =
      paymentState.hasPendingDigitalPayment &&
      existingPayment?.paymentRail === PAYMENT_RAILS.WAITER_QR &&
      ["created", "link_ready", "pending", "pending_confirmation"].includes(
        paymentStatus
      );
    const hasPendingWaiterQr =
      hasPendingWaiterQrPayment && Boolean(pendingWaiterQrData);

    if (paymentState.isPaid || isPaymentOperationConfirmed(existingPayment)) {
      setTableOrderMessage("Esta cuenta ya esta pagada.");
      return;
    }

    if (paymentState.hasPendingDigitalPayment && !hasPendingWaiterQrPayment) {
      setTableOrderMessage(
        "Esta cuenta ya tiene un cobro digital pendiente. Espera su confirmacion o cancelalo desde el panel que lo genero."
      );
      return;
    }

    const totalAmount = getOperationalOrderTotal(order);

    if (totalAmount <= 0) {
      setTableOrderMessage("Esta cuenta no tiene un importe pendiente para cobrar.");
      return;
    }

    waiterChargeRequestVersionRef.current += 1;
    waiterChargeRefreshRequestRef.current = null;
    waiterChargeFinalizingIntentRef.current = "";
    setWaiterChargeRefreshing(false);
    setWaiterQrCancelConfirmation(cancelQr && hasPendingWaiterQr && Boolean(existingPayment?.paymentIntentId));
    setWaiterChargeDialog({
      order,
      paymentOperationId:
        existingPayment?.cancellationScope === "local"
          ? newWaiterPaymentAttemptId()
          : existingPayment?.id || order.paymentOperationId || `waiter-payment-${order.id}`,
    });
    setWaiterChargeMessage(
      cancelQr && hasPendingWaiterQr
        ? ""
        : hasPendingWaiterQr
        ? "El QR sigue activo. Esperamos la confirmacion de Mercado Pago antes de cerrar la cuenta."
        : hasPendingWaiterQrPayment
          ? "El QR anterior sera reemplazado por el QR presencial interoperable."
        : "Elige el medio de cobro para esta cuenta."
    );
    setWaiterChargeIntent(
      hasPendingWaiterQr
        ? {
            id: existingPayment?.paymentIntentId || "",
            status: paymentStatus || "pending_confirmation",
            provider: existingPayment?.provider || existingPayment?.paymentRailProvider || "mercado_pago",
            externalReference: existingPayment?.externalReference || "",
            providerOperationId: existingPayment?.providerOperationId || "",
            grossAmount: Number(existingPayment?.grossAmount || totalAmount),
            currentCommissionAmount: Number(existingPayment?.currentCommissionAmount || 0),
            pendingDebtAppliedAmount: Number(existingPayment?.pendingDebtAppliedAmount || 0),
            merchantNetAmount: Number(existingPayment?.merchantNetAmount || 0),
            retainedAmount: Number(existingPayment?.retainedAmount || 0),
            qr: existingPayment?.qr || null,
          }
        : null
    );
  };

  const confirmWaiterLocalCharge = async (method) => {
    const sourceOrder = waiterChargeDialog?.order;

    if (!sourceOrder || !canChargeOrders || waiterChargeBusyRef.current) {
      return;
    }

    // NFC/Point remains disabled until Mercado Pago can confirm it. A card
    // approved on the restaurant's external terminal is recorded as a manual charge.
    if (
      method === "waiter_point" &&
      !isPaymentRailEnabled(PAYMENT_RAILS.WAITER_POINT)
    ) {
      setWaiterChargeMessage(
        "El cobro con tarjeta por NFC todavia no esta habilitado. Usa efectivo o QR interoperable."
      );
      return;
    }

    const isManualCardPayment = method === "card";
    const localPaymentMethod = isManualCardPayment ? "card" : "counter";
    const branchContext = {
      ...(activeBranchScope || currentBranch || currentCompany || {}),
      ...(restaurantProfile || {}),
    };
    const currentOperationalState = getOperationalOrdersState(activeBranchScope);
    const order = resolveOrderPromotionPayment(
      (currentOperationalState.mesasActivas || []).find(
        (candidate) => candidate.id === sourceOrder.id
      ) || sourceOrder, localPaymentMethod);
    const totalAmount = getOperationalOrderTotal(order);
    const customerName = getOperationalCustomerName(order) || "Comensal";
    const paymentOperationId =
      waiterChargeDialog?.paymentOperationId ||
      order.paymentOperationId ||
      `waiter-payment-${order.id}`;
    const currentPayments = getPaymentOperations(branchContext);
    const paymentState = resolveOperationalOrderPaymentState(
      order,
      currentPayments,
      paymentOperationId
    );

    if (paymentState.isPaid) {
      setWaiterChargeMessage("Esta cuenta ya tiene un cobro confirmado.");
      return;
    }

    if (paymentState.hasPendingDigitalPayment) {
      setWaiterChargeMessage(
        "Hay un QR activo para esta cuenta. Cancelalo antes de registrar otro medio de pago."
      );
      return;
    }

    if (totalAmount <= 0) {
      setWaiterChargeMessage("Esta cuenta no tiene un importe pendiente para cobrar.");
      return;
    }

    const promotionNotice = promotionPaymentNotice(order.pricing, formatCurrency);
    if (promotionNotice && !window.confirm(`${promotionNotice}\n\nConfirmar cobro por ${formatCurrency(totalAmount)}?`)) return;
    waiterChargeBusyRef.current = true;
    setWaiterChargeBusy(true);
    setWaiterChargeMessage(
      isManualCardPayment
        ? "Registrando el pago aprobado en la terminal del restaurante..."
        : "Registrando el efectivo recibido..."
    );

    try {
      const now = new Date().toISOString();
      const paymentRail = buildPaymentRailSnapshot({
        source: "waiter",
        checkoutMethod: localPaymentMethod,
        paymentProvider: isManualCardPayment ? "external_card_terminal" : "cash",
        splitMode: "manual_debt",
        isKioskExperience: false,
      });
      const paymentOperation = {
        id: paymentOperationId,
        orderId: order.id,
        tableId: order.tableId || "",
        mesa: order.mesa || "",
        customerName,
        canal: order.canal || "Salon",
        source: "waiter",
        requestedMethod: localPaymentMethod,
        selectedMethod: localPaymentMethod,
        paymentMethod: localPaymentMethod,
        paymentRail: paymentRail.rail,
        paymentRailProvider: paymentRail.provider,
        paymentRailLabel: isManualCardPayment
          ? "Cobro con tarjeta"
          : paymentRail.label,
        chargePresentation: isManualCardPayment
          ? "external_card_terminal"
          : paymentRail.chargePresentation,
        commissionCollectionMode: paymentRail.commissionCollectionMode,
        splitExecutionMode: paymentRail.splitExecutionMode,
        interoperabilityObjective: paymentRail.interoperabilityObjective,
        paymentSplitMode: paymentRail.splitMode,
        commissionRate: resolveBranchCommissionRateByChannel(branchContext, "manual"),
        status: "paid",
        grossAmount: totalAmount,
        amount: totalAmount,
        pricing: order.pricing || { finalTotal: totalAmount },
        itemsSummary: normalizeOperationalOrderItems(order.items || [])
          .map((item) => item.nombre || item.name || "Producto")
          .join(", "),
        orderedAt: order.orderedAt || now,
        paidAt: now,
        closedAt: null,
      };

      const orderPaymentPatch = {
        pricing: order.pricing,
        totalAmount,
        paymentStatus: "paid",
        paymentMethod: localPaymentMethod,
        selectedPaymentMethod: localPaymentMethod,
        requestedPaymentMethod: localPaymentMethod,
        cashPaymentPending: false,
        paymentOperationId,
        paidAt: now,
        paymentClaimSource: "waiter",
        paymentClaimedBy: user?.id || "",
        ...(isManualCardPayment
          ? {}
          : {
              cashCollectedAt: now,
              cashCollectedBy: user?.id || "",
            }),
      };
      const paymentClaim = await claimSharedOperationalOrderPayment({
        restaurant: activeBranchScope,
        orderId: order.id,
        paymentOperation,
        orderPatch: orderPaymentPatch,
        fallbackFactory: () => EMPTY_OPERATIONAL_STATE,
      });

      if (paymentClaim.supported) {
        applySharedPaymentClaim(paymentClaim.payload);

        if (!paymentClaim.claimed) {
          setWaiterChargeMessage(
            paymentClaim.reason === "payment_in_progress"
              ? "Hay otro cobro digital activo. Cancelalo antes de registrar otro medio de pago."
              : "Esta cuenta ya fue cobrada desde Caja o por otro mozo."
          );
          setTableOrderMessage("La cuenta ya tiene un cobro confirmado.");
          return;
        }
      } else {
        // Backward-compatible fallback while a branch has not applied the SQL patch yet.
        persistPaymentOperations([
          paymentOperation,
          ...currentPayments.filter((payment) => payment?.id !== paymentOperationId),
        ]);
        persistOperationalOrders({
          ...currentOperationalState,
          mesasActivas: (currentOperationalState.mesasActivas || []).map((candidate) =>
            candidate.id === order.id
              ? {
                  ...candidate,
                  ...orderPaymentPatch,
                  updatedAt: now,
                }
              : candidate
          ),
        });
      }

      try {
        await syncManualCommissionDebtForOperation({
          branch: branchContext,
          operation: paymentOperation,
          note: isManualCardPayment
            ? `Cobro con tarjeta externa registrado por mozo para ${customerName}.`
            : `Cobro en efectivo registrado por mozo para ${customerName}.`,
          metadata: {
            tableId: order.tableId || "",
            waiterUserId: user?.id || "",
            paymentChannel: isManualCardPayment ? "card" : "cash",
          },
        });
        setWaiterChargeMessage(
          `${isManualCardPayment ? "Cobro con tarjeta" : "Cobro en efectivo"} registrado para ${customerName}: ${formatCurrency(totalAmount)}.`
        );
      } catch (error) {
        setWaiterChargeMessage(
          `El cobro quedo registrado, pero no se pudo sincronizar la comision: ${
            error?.message || "error desconocido"
          }`
        );
      }

      setTableOrderMessage(`Cobro registrado para ${customerName}: ${formatCurrency(totalAmount)}.`);
      setPanelMessage(
        `${customerName} pago ${formatCurrency(totalAmount)}${
          isManualCardPayment ? " con tarjeta." : " en efectivo."
        }`
      );
    } finally {
      waiterChargeBusyRef.current = false;
      setWaiterChargeBusy(false);
    }
  };

  const createWaiterQrCharge = async () => {
    const sourceOrder = waiterChargeDialog?.order;

    if (!sourceOrder || !canChargeOrders || waiterChargeBusyRef.current) {
      return;
    }

    const branchContext = {
      ...(activeBranchScope || currentBranch || currentCompany || {}),
      ...(restaurantProfile || {}),
    };
    const currentOperationalState = getOperationalOrdersState(activeBranchScope);
    const order = resolveOrderPromotionPayment(
      (currentOperationalState.mesasActivas || []).find(
        (candidate) => candidate.id === sourceOrder.id
      ) || sourceOrder, "qr");
    const totalAmount = getOperationalOrderTotal(order);
    const customerName = getOperationalCustomerName(order) || "Comensal";
    const paymentOperationId =
      waiterChargeDialog?.paymentOperationId ||
      order.paymentOperationId ||
      `waiter-payment-${order.id}`;
    const currentPayments = getPaymentOperations(branchContext);
    const paymentState = resolveOperationalOrderPaymentState(
      order,
      currentPayments,
      paymentOperationId
    );
    const existingPayment = paymentState.paymentOperation;
    const existingQrData = getInteroperableQrData(existingPayment);

    if (paymentState.isPaid) {
      setWaiterChargeMessage("Esta cuenta ya fue cobrada desde Caja o por otro mozo.");
      return;
    }

    if (
      paymentState.hasPendingDigitalPayment &&
      existingPayment?.paymentRail !== PAYMENT_RAILS.WAITER_QR
    ) {
      setWaiterChargeMessage(
        "Esta cuenta ya tiene un cobro digital activo desde otro panel. Espera su confirmacion o cancelalo alli."
      );
      return;
    }

    if (
      paymentState.hasPendingDigitalPayment &&
      existingPayment?.paymentRail === PAYMENT_RAILS.WAITER_QR &&
      existingPayment?.paymentIntentId &&
      existingQrData
    ) {
      setWaiterChargeIntent({
        id: existingPayment.paymentIntentId,
        status: existingPayment.status || "pending_confirmation",
        provider: existingPayment.provider || existingPayment.paymentRailProvider || "mercado_pago",
        externalReference: existingPayment.externalReference || "",
        providerOperationId: existingPayment.providerOperationId || "",
        grossAmount: Number(existingPayment.grossAmount || totalAmount),
        qr: existingPayment.qr || null,
      });
      setWaiterChargeMessage("El QR ya esta listo. Esperamos la confirmacion del pago.");
      return;
    }

    if (totalAmount <= 0) {
      setWaiterChargeMessage("Esta cuenta no tiene un importe pendiente para cobrar.");
      return;
    }

    waiterChargeBusyRef.current = true;
    setWaiterChargeBusy(true);
    setWaiterChargeMessage("Generando el QR de Mercado Pago...");

    try {
      const promotionNotice = promotionPaymentNotice(order.pricing, formatCurrency);
      if (promotionNotice && !window.confirm(`${promotionNotice}\n\nGenerar QR por ${formatCurrency(totalAmount)}?`)) return;
      const checkoutOrder = {
        ...order,
        source: "waiter",
        selectedMethod: "waiter_qr",
        requestedMethod: "waiter_qr",
        paymentOperationId,
        totalAmount,
      };
      const paymentIntent = await createBranchDigitalPaymentIntent({
        branch: branchContext,
        order: checkoutOrder,
        customerName,
      });
      const qrData = getInteroperableQrData(paymentIntent);

      if (!paymentIntent?.id || !qrData) {
        throw new Error(
          "Mercado Pago no devolvio un QR presencial valido para esta cuenta."
        );
      }

      const paymentRail = buildPaymentRailSnapshot({
        source: "waiter",
        checkoutMethod: "waiter_qr",
        paymentProvider: paymentIntent.provider || branchContext.paymentProvider || "mercado_pago",
        splitMode: paymentIntent.paymentSplitMode || branchContext.paymentSplitMode || "",
        isKioskExperience: false,
      });
      const now = new Date().toISOString();
      const paymentOperation = {
        id: paymentOperationId,
        orderId: order.id,
        tableId: order.tableId || "",
        mesa: order.mesa || "",
        customerName,
        canal: order.canal || "Salon",
        source: "waiter",
        requestedMethod: "waiter_qr",
        selectedMethod: "waiter_qr",
        paymentMethod: "waiter_qr",
        paymentRail: paymentRail.rail,
        paymentRailProvider: paymentRail.provider,
        paymentRailLabel: paymentRail.label,
        chargePresentation: paymentRail.chargePresentation,
        commissionCollectionMode: paymentRail.commissionCollectionMode,
        splitExecutionMode: paymentRail.splitExecutionMode,
        interoperabilityObjective: paymentRail.interoperabilityObjective,
        paymentSplitMode: paymentRail.splitMode,
        commissionRate: resolveBranchCommissionRateByChannel(branchContext, "kiosk"),
        status: "pending_confirmation",
        grossAmount: totalAmount,
        amount: totalAmount,
        pricing: order.pricing || { finalTotal: totalAmount },
        itemsSummary: normalizeOperationalOrderItems(order.items || [])
          .map((item) => item.nombre || item.name || "Producto")
          .join(", "),
        orderedAt: order.orderedAt || now,
        paymentIntentId: paymentIntent.id,
        provider: paymentIntent.provider || "mercado_pago",
        providerOperationId: paymentIntent.providerOperationId || "",
        externalReference: paymentIntent.externalReference || "",
        currentCommissionAmount: Number(paymentIntent.currentCommissionAmount || 0),
        pendingDebtAppliedAmount: Number(paymentIntent.pendingDebtAppliedAmount || 0),
        retainedAmount: Number(paymentIntent.retainedAmount || 0),
        merchantNetAmount: Number(paymentIntent.merchantNetAmount || 0),
        deeplinkUrl: "",
        qr: paymentIntent.qr || {
          kind: "mercado_pago_qr_dynamic",
          qrData,
          qr_data: qrData,
          copyCode: qrData,
          copy_code: qrData,
          mode: "dynamic",
        },
      };

      persistPaymentOperations([
        paymentOperation,
        ...currentPayments.filter((payment) => payment?.id !== paymentOperationId),
      ]);
      persistOperationalOrders({
        ...currentOperationalState,
        mesasActivas: (currentOperationalState.mesasActivas || []).map((candidate) =>
          candidate.id === order.id
            ? {
                ...candidate,
                paymentStatus: "pending_confirmation",
                pricing: order.pricing,
                totalAmount,
                paymentMethod: "waiter_qr",
                selectedPaymentMethod: "waiter_qr",
                requestedPaymentMethod: "waiter_qr",
                cashPaymentPending: false,
                paymentOperationId,
                paymentIntentId: paymentIntent.id,
                updatedAt: now,
              }
            : candidate
        ),
      });

      setWaiterChargeIntent(paymentIntent);
      setWaiterChargeMessage(
        "Muestra este QR presencial al comensal. La cuenta se cerrara solo cuando Mercado Pago confirme el pago."
      );
    } catch (error) {
      setWaiterChargeMessage(
        error?.message || "No pudimos generar el QR para cobrar esta cuenta."
      );
    } finally {
      waiterChargeBusyRef.current = false;
      setWaiterChargeBusy(false);
    }
  };

  const requestWaiterQrCancellation = () => {
    if (!waiterChargeIntent?.id || waiterChargeBusyRef.current) {
      return;
    }

    setWaiterQrCancelConfirmation(true);
  };

  const releaseInactiveWaiterQrCharge = (paymentIntentId, sourceOrder, status = "cancelled") => {
    const inactiveStatus = status === "expired" ? "expired" : "cancelled";
    const inactiveMessage = inactiveStatus === "expired" ? "QR vencido" : "QR cancelado";
    const currentPayments = getPaymentOperations(activeBranchScope);
    const currentOperationalState = getOperationalOrdersState(activeBranchScope);
    const order = (currentOperationalState.mesasActivas || []).find(
      (candidate) => candidate.id === sourceOrder.id
    ) || sourceOrder;
    const paymentOperationId = waiterChargeDialog?.paymentOperationId ||
      sourceOrder.paymentOperationId || `waiter-payment-${sourceOrder.id}`;
    const paymentState = resolveOperationalOrderPaymentState(order, currentPayments, paymentOperationId);
    const now = new Date().toISOString();

    waiterChargeRequestVersionRef.current += 1;
    waiterChargeFinalizingIntentRef.current = paymentIntentId;
    setWaiterChargeIntent(null);
    setWaiterQrCancelConfirmation(false);

    if (paymentState.isPaid) {
      setWaiterChargeMessage("La cuenta ya tiene un cobro confirmado. Conservamos ese pago.");
      setTableOrderMessage("La cuenta ya fue cobrada; no se reabrio para evitar un doble cobro.");
      return;
    }

    // An operation id can be reused for a new QR: only retire this exact attempt.
    const matchesAttempt = (payment) => payment?.paymentIntentId
      ? payment.paymentIntentId === paymentIntentId
      : payment?.id === paymentOperationId && payment?.orderId === sourceOrder.id;
    const otherPendingPayment = currentPayments.some((payment) =>
      !matchesAttempt(payment) &&
      resolveOperationalOrderPaymentState(order, [payment]).hasPendingDigitalPayment
    );
    persistPaymentOperations(currentPayments.map((payment) =>
      matchesAttempt(payment) && !isPaymentOperationConfirmed(payment)
        ? { ...payment, status: inactiveStatus, qr: null, updatedAt: now }
        : payment
    ));

    if (otherPendingPayment || (order.paymentIntentId && order.paymentIntentId !== paymentIntentId)) {
      setWaiterChargeMessage(`${inactiveMessage}. La cuenta tiene otro cobro activo; vuelve a abrirla para revisarlo.`);
      return;
    }

    persistOperationalOrders({
      ...currentOperationalState,
      mesasActivas: (currentOperationalState.mesasActivas || []).map((candidate) =>
        candidate.id === order.id
          ? {
              ...candidate,
              paymentStatus: "unpaid",
              paymentProviderStatus: "",
              paymentMethod: "",
              selectedPaymentMethod: "",
              requestedPaymentMethod: "",
              cashPaymentPending: true,
              paymentOperationId: "",
              paymentIntentId: "",
              updatedAt: now,
            }
          : candidate
      ),
    });
    setWaiterChargeMessage(`${inactiveMessage}. Puedes cobrar en efectivo o generar un nuevo QR.`);
    setTableOrderMessage(`${inactiveMessage}. La cuenta quedo liberada para otro medio de pago.`);
    setPanelMessage(`${inactiveMessage}. La cuenta sigue pendiente.`);
  };

  const cancelWaiterQrCharge = async ({ releaseLocally = false } = {}) => {
    const paymentIntentId = waiterChargeIntent?.id;
    const sourceOrder = waiterChargeDialog?.order;
    const branchId = activeBranchScope?.id || currentBranch?.id || "";

    if (
      !paymentIntentId ||
      !sourceOrder ||
      !branchId ||
      !canChargeOrders ||
      waiterChargeBusyRef.current
    ) {
      return;
    }

    waiterChargeBusyRef.current = true;
    // Invalidate polls already in flight before awaiting the provider cancellation.
    waiterChargeRequestVersionRef.current += 1;
    waiterChargeRefreshRequestRef.current = null;
    setWaiterChargeRefreshing(false);
    setWaiterChargeBusy(true);
    setWaiterChargeMessage(releaseLocally ? "Liberando el cobro en MozzoApp..." : "Cancelando el QR de Mercado Pago...");

    try {
      const inactiveIntent = await cancelInPersonQrPaymentIntent({
        branchId,
        intentId: paymentIntentId,
        orderId: sourceOrder.id,
        sourceChannel: "waiter",
        ...(releaseLocally ? { releaseLocally: true, riskAcknowledged: true } : {}),
      });

      if (releaseLocally) {
        if (!inactiveIntent.locallyReleased || !inactiveIntent.state) {
          throw new Error("No se confirmo la liberacion del cobro. Actualiza la cuenta.");
        }
        applySharedPaymentClaim(inactiveIntent.state);
        setWaiterChargeIntent(null);
        setWaiterQrCancelConfirmation(false);
        waiterChargeFinalizingIntentRef.current = paymentIntentId;
        setWaiterChargeDialog((current) => current ? {
          ...current,
          order: inactiveIntent.state.mesasActivas.find((order) => order.id === sourceOrder.id) || current.order,
          paymentOperationId: newWaiterPaymentAttemptId(),
        } : current);
        setWaiterChargeMessage("Cobro liberado en MozzoApp. Puedes cobrar en efectivo o generar otro QR. El QR anterior podria acreditarse: no lo vuelvas a usar.");
        setTableOrderMessage("El pedido sigue en su estado actual. El cobro fue liberado para elegir otro medio.");
        return;
      }
      releaseInactiveWaiterQrCharge(paymentIntentId, sourceOrder, inactiveIntent.status);
    } catch (error) {
      setWaiterQrCancelConfirmation(false);
      setWaiterChargeMessage(
        error?.message ||
          "No pudimos cancelar el QR. Actualiza el estado antes de intentar otro cobro."
      );
    } finally {
      waiterChargeBusyRef.current = false;
      setWaiterChargeBusy(false);
    }
  };

  const refreshWaiterQrCharge = async ({ silent = false } = {}) => {
    const paymentIntentId = waiterChargeIntent?.id;
    const sourceOrder = waiterChargeDialog?.order;
    const branchId = activeBranchScope?.id || currentBranch?.id || "";

    if (!paymentIntentId || !sourceOrder || !branchId ||
        waiterChargeBusyRef.current || waiterChargeRefreshRequestRef.current) {
      return;
    }

    const requestToken = {};
    const requestVersion = waiterChargeRequestVersionRef.current;
    waiterChargeRefreshRequestRef.current = requestToken;
    setWaiterChargeRefreshing(true);
    let finalizingPayment = false;

    try {
      const refreshedIntent = await getDigitalPaymentIntent({
        intentId: paymentIntentId,
        orderId: sourceOrder.id,
        branchId,
        externalReference: waiterChargeIntent?.externalReference || "",
      });

      if (!refreshedIntent || requestVersion !== waiterChargeRequestVersionRef.current ||
          waiterChargeBusyRef.current) {
        return;
      }

      const paymentStatus = String(
        refreshedIntent.status || refreshedIntent.providerStatus || ""
      ).toLowerCase();
      if (refreshedIntent.locallyReleased || getPaymentOperations(activeBranchScope).some(
        (payment) => payment.paymentIntentId === paymentIntentId && payment.cancellationScope === "local"
      )) {
        setWaiterChargeIntent(null);
        setWaiterQrCancelConfirmation(false);
        waiterChargeRequestVersionRef.current += 1;
        // Refresh shared state; never finalize a locally released attempt, even
        // when an old device still has its QR open.
        closeWaiterChargeDialog();
        setTableOrderMessage("Este QR fue liberado desde otro dispositivo. Actualiza la cuenta antes de cobrar.");
        return;
      }
      if (["cancelled", "canceled", "expired"].includes(paymentStatus) && !refreshedIntent.approvedAt) {
        releaseInactiveWaiterQrCharge(paymentIntentId, sourceOrder, paymentStatus);
        return;
      }

      const mergedIntent = {
        ...waiterChargeIntent,
        ...refreshedIntent,
        deeplinkUrl: "",
        qr: refreshedIntent.qr || waiterChargeIntent?.qr || null,
      };
      setWaiterChargeIntent(mergedIntent);

      const isApproved =
        Boolean(refreshedIntent.approvedAt) ||
        ["approved", "paid", "accredited", "completed", "closed"].includes(paymentStatus);

      if (!isApproved) {
        if (!silent) {
          setWaiterChargeMessage("El pago todavia no fue confirmado. Manten el QR visible un momento mas.");
        }
        return;
      }

      if (waiterChargeFinalizingIntentRef.current === paymentIntentId) {
        return;
      }

      waiterChargeFinalizingIntentRef.current = paymentIntentId;
      finalizingPayment = true;
      waiterChargeBusyRef.current = true;
      setWaiterChargeBusy(true);
      const branchContext = {
        ...(activeBranchScope || currentBranch || currentCompany || {}),
        ...(restaurantProfile || {}),
      };
      const currentPayments = getPaymentOperations(branchContext);
      const currentOperationalState = getOperationalOrdersState(activeBranchScope);
      const order =
        (currentOperationalState.mesasActivas || []).find(
          (candidate) => candidate.id === sourceOrder.id
        ) || sourceOrder;
      const paymentOperationId =
        waiterChargeDialog?.paymentOperationId ||
        order.paymentOperationId ||
        `waiter-payment-${order.id}`;
      const currentPaymentState = resolveOperationalOrderPaymentState(
        order,
        currentPayments,
        paymentOperationId
      );
      const existingPayment = currentPaymentState.paymentOperation;

      if (
        currentPaymentState.isPaid &&
        currentPaymentState.confirmedOperation?.id !== paymentOperationId
      ) {
        setWaiterChargeMessage(
          "El QR se acredito, pero la cuenta ya fue cobrada por Caja u otro mozo. Revisa la conciliacion antes de devolver dinero."
        );
        setTableOrderMessage("La cuenta ya tenia un cobro confirmado.");
        return;
      }

      const now = refreshedIntent.approvedAt || new Date().toISOString();
      const settledPayment = {
        ...(existingPayment || {}),
        id: paymentOperationId,
        orderId: order.id,
        tableId: order.tableId || "",
        mesa: order.mesa || "",
        customerName: getOperationalCustomerName(order) || "Comensal",
        canal: order.canal || "Salon",
        source: "waiter",
        requestedMethod: "waiter_qr",
        selectedMethod: "waiter_qr",
        paymentMethod: "waiter_qr",
        paymentRail: PAYMENT_RAILS.WAITER_QR,
        provider: refreshedIntent.provider || existingPayment?.provider || "mercado_pago",
        paymentIntentId,
        providerOperationId:
          refreshedIntent.providerOperationId || existingPayment?.providerOperationId || "",
        externalReference:
          refreshedIntent.externalReference || existingPayment?.externalReference || "",
        currentCommissionAmount: Number(
          refreshedIntent.currentCommissionAmount ?? existingPayment?.currentCommissionAmount ?? 0
        ),
        pendingDebtAppliedAmount: Number(
          refreshedIntent.pendingDebtAppliedAmount ?? existingPayment?.pendingDebtAppliedAmount ?? 0
        ),
        retainedAmount: Number(
          refreshedIntent.retainedAmount ?? existingPayment?.retainedAmount ?? 0
        ),
        merchantNetAmount: Number(
          refreshedIntent.merchantNetAmount ?? existingPayment?.merchantNetAmount ?? 0
        ),
        status: "paid",
        paidAt: now,
        closedAt: now,
        updatedAt: now,
      };

      const orderPaymentPatch = {
        paymentStatus: "paid",
        paymentProviderStatus: "approved",
        paymentMethod: "waiter_qr",
        selectedPaymentMethod: "waiter_qr",
        requestedPaymentMethod: "waiter_qr",
        cashPaymentPending: false,
        paymentOperationId,
        paymentIntentId,
        paidAt: now,
        paymentClaimSource: "waiter_qr",
        paymentClaimedBy: user?.id || "",
      };
      const paymentClaim = await claimSharedOperationalOrderPayment({
        restaurant: activeBranchScope,
        orderId: order.id,
        paymentOperation: settledPayment,
        orderPatch: orderPaymentPatch,
        fallbackFactory: () => EMPTY_OPERATIONAL_STATE,
      });

      if (paymentClaim.supported) {
        applySharedPaymentClaim(paymentClaim.payload);

        if (!paymentClaim.claimed) {
          setWaiterChargeMessage(
            "El QR se acredito, pero esta cuenta ya fue cobrada desde otro panel. Revisa la conciliacion antes de devolver dinero."
          );
          setTableOrderMessage("La cuenta ya tenia un cobro confirmado.");
          return;
        }
      } else {
        // Keep the current flow working on branches that still need the SQL patch.
        persistPaymentOperations([
          settledPayment,
          ...currentPayments.filter((payment) => payment?.id !== paymentOperationId),
        ]);
        persistOperationalOrders({
          ...currentOperationalState,
          mesasActivas: (currentOperationalState.mesasActivas || []).map((candidate) =>
            candidate.id === order.id
              ? {
                  ...candidate,
                  ...orderPaymentPatch,
                  updatedAt: now,
                }
              : candidate
          ),
        });
      }

      try {
        await finalizeApprovedDigitalCommission({
          branchId,
          orderId: order.id,
          paymentOperationId,
          paymentIntentId,
          sourceChannel: "waiter",
          sourceMode: "waiter_qr",
          metadata: {
            tableId: order.tableId || "",
            waiterUserId: user?.id || "",
          },
        });
        setWaiterChargeMessage(
          `Pago QR confirmado para ${getOperationalCustomerName(order) || "el comensal"}.`
        );
      } catch (error) {
        setWaiterChargeMessage(
          `Pago QR confirmado, pero la conciliacion de comision quedo pendiente: ${
            error?.message || "error desconocido"
          }`
        );
      }

      setTableOrderMessage(
        `Cobro QR registrado: ${formatCurrency(getOperationalOrderTotal(order))}.`
      );
      setPanelMessage(
        `${getOperationalCustomerName(order) || "Comensal"} pago ${formatCurrency(
          getOperationalOrderTotal(order)
        )} por QR.`
      );
    } catch (error) {
      if (requestVersion !== waiterChargeRequestVersionRef.current) {
        return;
      }
      if (finalizingPayment && waiterChargeFinalizingIntentRef.current === paymentIntentId) {
        waiterChargeFinalizingIntentRef.current = "";
      }
      if (!silent) {
        setWaiterChargeMessage(
          error?.message || "No pudimos revisar el estado del pago QR."
        );
      }
    } finally {
      if (waiterChargeRefreshRequestRef.current === requestToken) {
        waiterChargeRefreshRequestRef.current = null;
        setWaiterChargeRefreshing(false);
      }
      if (finalizingPayment) {
        waiterChargeBusyRef.current = false;
        setWaiterChargeBusy(false);
      }
    }
  };

  refreshWaiterQrChargeRef.current = refreshWaiterQrCharge;

  useEffect(() => {
    const intentId = waiterChargeIntent?.id;
    const status = String(waiterChargeIntent?.status || "").toLowerCase();

    if (
      !intentId ||
      ["approved", "paid", "accredited", "completed", "closed"].includes(status)
    ) {
      return undefined;
    }

    void refreshWaiterQrChargeRef.current?.({ silent: true });
    const intervalId = window.setInterval(() => {
      void refreshWaiterQrChargeRef.current?.({ silent: true });
    }, 4500);

    return () => window.clearInterval(intervalId);
  }, [waiterChargeIntent?.id, waiterChargeIntent?.status, activeBranchScope?.id]);
  const cancelTableOrder = () => {
    if (!canEditSelectedTableOrder || !canCancelOrders) {
      setTableOrderMessage(tableOrderPermissionMessage);
      return;
    }

    if (!selectedTable || !activeOrderForSelectedTable) {
      return;
    }

    if (activeOrderForSelectedTable.phase !== "new") {
      setTableOrderMessage(
        "Solo puedes cancelar el pedido antes de que pase a cocina o a entrega."
      );
      return;
    }

    if (activeOrderForSelectedTable.stockDiscounted === true) {
      const consumos = (Array.isArray(activeOrderForSelectedTable.items)
        ? activeOrderForSelectedTable.items
        : []
      ).flatMap((item) => item.consumos || []);

      if (consumos.length > 0) {
        restoreIngredientStock(activeBranchScope, [], consumos);
      }
    }

    const now = new Date().toISOString();
    const cancelledOrder = {
      ...activeOrderForSelectedTable,
      phase: "cancelled",
      cancelledAt: now,
      updatedAt: now,
    };

    const nextState = {
      ...operationalOrders,
      mesasActivas: (operationalOrders.mesasActivas || []).filter(
        (mesa) => mesa.id !== activeOrderForSelectedTable.id
      ),
      pedidosHistoricos: [
        cancelledOrder,
        ...(operationalOrders.pedidosHistoricos || []).filter(
          (pedido) => pedido.id !== activeOrderForSelectedTable.id
        ),
      ],
    };

    persistOperationalOrders(nextState);
    setPanelMessage(
      `${selectedTable.label || `Mesa ${selectedTable.tableNumber}`} quedo con el pedido cancelado.`
    );
    closeTableModal();
  };
  const closeSelectedTableService = () => {
    if (
      !selectedTable ||
      !activeOrderForSelectedTable ||
      isSelectedTableAssignedToAnotherWaiter
    ) {
      setTableOrderMessage(
        isSelectedTableAssignedToAnotherWaiter
          ? tableOrderPermissionMessage
          : "Esta mesa no tiene un pedido activo para cerrar."
      );
      return;
    }

    if (!canModifyOrders) {
      setTableOrderMessage("Tu acceso no tiene permiso para cerrar mesas.");
      return;
    }

    if ((activeOrderForSelectedTable.items || []).some((item) => !item.delivered)) {
      setTableOrderMessage(
        "Todavia hay productos pendientes de entrega. Confirma todas las entregas antes de cerrar la mesa."
      );
      return;
    }

    const currentPayments = getPaymentOperations(activeBranchScope);
    const paymentState = resolveOperationalOrderPaymentState(
      activeOrderForSelectedTable,
      currentPayments,
      activeOrderForSelectedTable.paymentOperationId
    );

    if (!paymentState.isPaid) {
      setTableOrderMessage(
        "La cuenta sigue pendiente de pago. Cobra en efectivo, tarjeta o QR antes de cerrar la mesa."
      );
      return;
    }

    const tableLabel =
      selectedTable.label || `Mesa ${selectedTable.tableNumber}`;
    const confirmed = window.confirm(
      `Confirmas que ${tableLabel} recibio todo el pedido y que deseas cerrar la cuenta pagada?`
    );

    if (!confirmed) {
      return;
    }

    const now = new Date().toISOString();
    const closedOrder = {
      ...activeOrderForSelectedTable,
      phase: "closed",
      paymentStatus: "closed",
      closedAt: now,
      updatedAt: now,
      closedByUserId: user?.id || "",
      closedByName:
        currentUserProfile?.fullName || currentUserProfile?.email || "Mozo",
    };
    const nextState = {
      ...operationalOrders,
      mesasActivas: (operationalOrders.mesasActivas || []).filter(
        (mesa) => mesa.id !== activeOrderForSelectedTable.id
      ),
      pedidosHistoricos: [
        closedOrder,
        ...(operationalOrders.pedidosHistoricos || []).filter(
          (pedido) => pedido.id !== activeOrderForSelectedTable.id
        ),
      ],
    };

    persistOperationalOrders(nextState);

    if (paymentState.paymentOperation?.id) {
      persistPaymentOperations(
        currentPayments.map((payment) =>
          payment.id !== paymentState.paymentOperation.id
            ? payment
            : {
                ...payment,
                status: "closed",
                paidAt: payment.paidAt || activeOrderForSelectedTable.paidAt || now,
                closedAt: now,
                history: [
                  {
                    id: `history-${Date.now()}-waiter-close`,
                    label: "Operacion cerrada junto con la mesa desde el panel del mozo.",
                    at: now,
                  },
                  ...(Array.isArray(payment.history) ? payment.history : []),
                ].slice(0, 12),
              }
        )
      );
    }

    if (
      isSelectedTableAssignedToCurrentWaiter &&
      activeBranchScope?.id &&
      user?.id
    ) {
      releaseWaiterTableAssignment(activeBranchScope, selectedTable.id, user.id);
    }

    setPanelMessage(`${tableLabel} quedo cerrada con el pago verificado.`);
    closeTableModal();
  };

  const handleCompleteDeliveryAlert = (alert) => {
    if (!alert?.id) {
      return;
    }

    const currentOperationalState = getOperationalOrdersState(activeBranchScope);
    const order = (currentOperationalState.mesasActivas || []).find(
      (candidate) => candidate.id === alert.orderId
    );

    if (!order) {
      completeDeliveryAlert(activeBranchScope, alert.id, user?.id || "");
      setPanelMessage("El pedido ya no esta activo. El aviso fue cerrado.");
      return;
    }

    const requestedItemIds = new Set(
      (Array.isArray(alert.itemIds) ? alert.itemIds : [])
        .map((itemId) => String(itemId || "").trim())
        .filter(Boolean)
    );
    const itemIdsToDeliver = requestedItemIds.size
      ? requestedItemIds
      : new Set((order.items || []).filter((item) => !item.delivered).map((item) => item.id));

    if (!itemIdsToDeliver.size) {
      completeDeliveryAlert(activeBranchScope, alert.id, user?.id || "");
      setPanelMessage(`${alert.mesa || "El pedido"} ya figuraba como entregado.`);
      return;
    }

    const now = new Date().toISOString();
    const waiterName =
      currentUserProfile?.fullName || currentUserProfile?.email || "Mozo";
    const nextState = {
      ...currentOperationalState,
      mesasActivas: (currentOperationalState.mesasActivas || []).map((candidate) =>
        candidate.id !== order.id
          ? candidate
          : {
              ...candidate,
              deliveryMode: "waiter",
              assignedWaiterUserId: candidate.assignedWaiterUserId || user?.id || "",
              assignedWaiterName: candidate.assignedWaiterName || waiterName,
              updatedAt: now,
              items: (candidate.items || []).map((item) =>
                itemIdsToDeliver.has(item.id)
                  ? {
                      ...item,
                      delivered: true,
                      deliveryStatus: "delivered",
                      deliveredAt: item.deliveredAt || now,
                      deliveredBy: item.deliveredBy || user?.id || "",
                      deliveredByName: item.deliveredByName || waiterName,
                    }
                  : item
              ),
            }
      ),
    };

    persistOperationalOrders(nextState);
    completeDeliveryAlert(activeBranchScope, alert.id, user?.id || "");
    setPanelMessage(
      `${alert.summary || `Entrega de ${alert.mesa || "la mesa"}`} quedo registrada.`
    );
  };
  const handleCompleteVisibleServiceRequest = (request) => {
    if (!request?.id) {
      return;
    }

    completeWaiterServiceRequest(activeBranchScope, request.id);
    (waiterOps.deliveryAlerts || [])
      .filter((alert) => alert.orderId === request.id)
      .forEach((alert) => {
        markDeliveryAlertSeen(activeBranchScope, alert.id, user?.id || "");
      });
    setPanelMessage(`${request.mesa} ya quedo atendida desde tu panel.`);
  };
  const handleCompleteServiceRequest = (alert) => {
    const request = (waiterOps.serviceRequests || []).find(
      (item) => item.id === alert?.orderId
    );

    if (request) {
      handleCompleteVisibleServiceRequest(request);
      return;
    }

    markDeliveryAlertSeen(activeBranchScope, alert.id, user?.id || "");
    setPanelMessage(`${alert.mesa} ya quedo atendida desde tu panel.`);
  };
  const waiterChargeOrder = waiterChargeDialog?.order || null;
  const releasedWaiterPayments = getPaymentOperations(activeBranchScope).filter(
    (payment) => payment.cancellationScope === "local"
  );
  const lateWaiterPayments = releasedWaiterPayments.filter((payment) => payment.reconciliationRequired);
  const waiterChargePaymentState = waiterChargeOrder
    ? resolveOperationalOrderPaymentState(
        (operationalOrders.mesasActivas || []).find((order) => order.id === waiterChargeOrder.id) || waiterChargeOrder,
        getPaymentOperations(activeBranchScope),
        waiterChargeDialog?.paymentOperationId
      )
    : null;
  const waiterChargePayment = waiterChargePaymentState?.paymentOperation || null;
  const waiterChargeStatus = String(
    waiterChargeIntent?.status || waiterChargePayment?.status || ""
  ).toLowerCase();
  const waiterChargeCompleted = waiterChargePaymentState?.isPaid || ["approved", "paid", "accredited", "completed", "closed"].includes(
    waiterChargeStatus
  );
  const waiterChargeQrData =
    getInteroperableQrData(waiterChargeIntent) ||
    getInteroperableQrData(waiterChargePayment);

  if (loadingTables && loadingLogoUrl) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          backgroundColor: "#ffffff",
        }}
      >
        <style>{loadingLogoKeyframes}</style>
        <img
          src={loadingLogoUrl}
          alt="Logo de carga"
          style={{
            width: "auto",
            height: "auto",
            maxWidth: "35vw",
            maxHeight: "35vh",
            objectFit: "contain",
            display: "block",
            ...loadingLogoAnimation,
          }}
        />
      </div>
    );
  }

  return (
    <div className="waiter-session-page" style={styles.page}>
      <style>
        {`
          @keyframes waiterSalonTableCallPulse {
            0%, 100% {
              background: #dcfce7;
              border-color: #22c55e;
              color: #166534;
              box-shadow: 0 0 0 5px rgba(34,197,94,0.2), 0 14px 30px rgba(22,163,74,0.28);
            }
            50% {
              background: #fee2e2;
              border-color: #dc2626;
              color: #991b1b;
              box-shadow: 0 0 0 7px rgba(239,68,68,0.22), 0 17px 38px rgba(185,28,28,0.4);
            }
          }

          @keyframes waiterSalonTableReadyPulse {
            0%, 100% {
              background: #fef9c3;
              border-color: #eab308;
              color: #854d0e;
              box-shadow: 0 0 0 5px rgba(234,179,8,0.18), 0 14px 30px rgba(202,138,4,0.26);
            }
            50% {
              background: #fef08a;
              border-color: #ca8a04;
              color: #713f12;
              box-shadow: 0 0 0 8px rgba(234,179,8,0.24), 0 18px 38px rgba(161,98,7,0.38);
            }
          }

          .waiter-order-modal-card.waiter-order-modal-card-menu {
            width: min(1440px, calc(100vw - 24px)) !important;
            min-height: 92vh !important;
            height: 92vh !important;
            max-height: 92vh !important;
            padding: 16px 20px !important;
            box-sizing: border-box !important;
          }

          .waiter-order-modal-card-menu .waiter-order-customer-field-menu-hidden {
            display: none !important;
          }

          .waiter-order-modal-card-menu .waiter-order-modal-header {
            flex: 0 0 auto !important;
            min-height: 36px;
          }

          .waiter-order-modal-card-menu .waiter-order-modal-grid {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }

          .waiter-order-modal-card-menu .waiter-menu-workspace-panel-browse {
            display: flex !important;
            flex-direction: column !important;
            height: 100% !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }

          .waiter-order-modal-card-menu .waiter-product-result-list-expanded {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            max-height: none !important;
            overflow: auto !important;
          }

          @media (min-width: 960px) and (min-height: 680px) {
            .waiter-session-page {
              height: 100dvh;
              min-height: 0 !important;
              overflow: hidden;
              padding: 12px 24px !important;
              box-sizing: border-box;
            }

            .waiter-session-card {
              height: 100%;
              min-height: 0 !important;
              overflow: hidden;
              padding: 14px 20px !important;
              display: flex !important;
              flex-direction: column;
              box-sizing: border-box;
              max-width: none !important;
            }

            .waiter-branch-bar,
            .waiter-alerts-panel {
              flex: 0 0 auto;
            }

            .waiter-branch-bar {
              padding: 8px 14px !important;
            }

            .waiter-alerts-panel {
              padding: 10px 12px !important;
            }

            .waiter-salon-section {
              flex: 1 1 auto;
              min-height: 0;
              display: flex !important;
              flex-direction: column;
              gap: 8px !important;
              margin-top: 0 !important;
            }

            .waiter-salon-header,
            .waiter-room-tabs {
              flex: 0 0 auto;
            }

            .waiter-room-tabs {
              margin: 0 !important;
            }

            .waiter-salon-floor-scroll {
              flex: 1 1 auto;
              min-height: 0;
              display: flex;
              overflow: auto;
            }

            .waiter-salon-floor {
              flex: 1 1 auto;
              width: 100%;
              min-height: 320px !important;
              height: 100% !important;
            }
          }

          @media (max-width: 640px) {
            .waiter-session-page {
              align-items: stretch !important;
              justify-content: flex-start !important;
              padding: 10px !important;
              overflow-x: hidden;
            }

            .waiter-session-card {
              border-radius: 18px !important;
              padding: 12px !important;
              gap: 14px !important;
              box-shadow: 0 12px 34px rgba(15, 23, 42, 0.12) !important;
            }

            .waiter-branch-bar {
              align-items: stretch !important;
              padding: 10px !important;
              gap: 10px !important;
            }

            .waiter-branch-bar > div {
              width: 100%;
            }

            .waiter-branch-bar button {
              min-height: 42px;
              flex: 1 1 0;
            }

            .waiter-alerts-panel {
              padding: 10px !important;
              border-radius: 16px !important;
            }

            .waiter-salon-section {
              gap: 12px !important;
            }

            .waiter-salon-header {
              align-items: stretch !important;
              gap: 12px !important;
            }

            .waiter-salon-header > div {
              width: 100%;
            }

            .waiter-salon-header strong {
              font-size: 22px !important;
            }

            .waiter-salon-header button,
            .waiter-salon-header span {
              width: 100%;
              justify-content: center;
              text-align: center;
              box-sizing: border-box;
            }

            .waiter-room-tabs {
              gap: 8px !important;
              padding-bottom: 8px !important;
            }

            .waiter-room-tabs button {
              padding: 10px 12px !important;
            }

            .waiter-salon-floor {
              min-width: 760px;
              min-height: 500px !important;
              border-radius: 18px !important;
            }

            .waiter-salon-floor-scroll {
              width: 100%;
              overflow-x: auto;
              overflow-y: hidden;
              border-radius: 18px;
              -webkit-overflow-scrolling: touch;
            }

            .waiter-order-modal-overlay {
              align-items: stretch !important;
              padding: 8px !important;
            }

            .waiter-order-modal-card {
              max-height: calc(100dvh - 16px) !important;
              border-radius: 18px !important;
              padding: 12px !important;
              gap: 10px !important;
            }

            .waiter-order-modal-card h2 {
              font-size: 22px !important;
            }

            .waiter-order-modal-card input,
            .waiter-order-modal-card select,
            .waiter-order-modal-card button {
              max-width: 100%;
            }

            .waiter-order-modal-card [data-waiter-product-result] {
              grid-template-columns: 58px minmax(0, 1fr) !important;
              align-items: start !important;
            }

            .waiter-order-modal-card [data-waiter-product-price] {
              grid-column: 2;
              white-space: normal !important;
            }
          }
        `}
      </style>
      <div className="waiter-session-card" style={styles.card}>
        <div className="waiter-branch-bar" style={styles.branchBar}>
          <div style={styles.branchIdentity}>
            <span aria-hidden="true" style={styles.branchStatusDot} />
            <div style={styles.branchIdentityCopy}>
              <span style={styles.branchLabel}>Sucursal en servicio</span>
              <strong style={styles.branchName}>{currentBranch?.name || "Pendiente"}</strong>
            </div>
          </div>

          <div style={styles.branchActions}>
            {!isAndroidWaiterLauncher ? (
              <>
                <button
                  type="button"
                  onClick={() => setLauncherInstallTarget("android")}
                  style={styles.androidLauncherButton}
                >
                  Descargar launcher Android
                </button>
                <button
                  type="button"
                  onClick={() => setLauncherInstallTarget("ios")}
                  style={styles.androidLauncherButton}
                >
                  Instalar en iPhone
                </button>
              </>
            ) : null}
            <button type="button" onClick={handleWaiterSignOut} style={styles.signOutButton}>
              Cerrar sesion
            </button>
          </div>
        </div>

        {tablesError ? <div style={styles.errorBanner}>{tablesError}</div> : null}
        {panelMessage ? <div style={styles.successBanner}>{panelMessage}</div> : null}
        {canChargeOrders && lateWaiterPayments.length > 0 ? (
          <div role="alert" style={styles.waiterChargeCancelConfirmation}>
            <strong>Revisar cobros: se acredito un QR liberado</strong>
            {lateWaiterPayments.map((payment) => (
              <span key={payment.id}>
                {payment.mesa || "Cuenta"} - {payment.customerName || payment.orderId}: {formatCurrency(payment.grossAmount || payment.amount)}.
                Posible doble cobro. Revisar en Caja antes de un reintegro.
              </span>
            ))}
          </div>
        ) : null}

        {visibleAlerts.length > 0 ? (
          <section className="waiter-alerts-panel" style={styles.alertsPanel}>
            <div style={styles.alertsHeader}>
              <strong style={styles.alertsTitle}>Avisos del salon</strong>
              <span style={styles.alertsMeta}>
                {visibleAlerts.length} aviso{visibleAlerts.length === 1 ? "" : "s"} pendiente
              </span>
            </div>

            <div style={styles.alertList}>
              {visibleAlerts.map((alert) => (
                <article key={alert.id} style={styles.alertCard}>
                  <div style={styles.alertCardContent}>
                    <strong style={styles.alertCardTitle}>{alert.mesa}</strong>
                    <span style={styles.alertCardText}>
                      {alert.type === "service_request"
                        ? alert.summary || "Te estan llamando desde esta mesa."
                        : alert.summary
                          ? alert.summary
                        : alert.broadcast
                          ? "Pedido listo para entregar. Aviso abierto al equipo de salon."
                          : "Pedido listo para entregar para la mesa que estas atendiendo."}
                    </span>
                  </div>
                  <button
                    type="button"
                    style={styles.alertCardButton}
                    onClick={() =>
                      alert.type === "service_request"
                        ? handleCompleteServiceRequest(alert)
                        : handleCompleteDeliveryAlert(alert)
                    }
                  >
                    {alert.type === "service_request"
                      ? "Marcar atendido"
                      : "Marcar entregado"}
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {loadingTables ? (
          <div style={styles.emptyState}>Estamos cargando las mesas registradas.</div>
        ) : visibleTables.length === 0 ? (
          <div style={styles.emptyState}>
            Todavia no hay mesas cargadas para esta sucursal.
          </div>
        ) : (
          <section className="waiter-salon-section" style={styles.salonSection}>
            <div className="waiter-salon-header" style={styles.salonHeader}>
              <div style={styles.salonHeaderCopy}>
                <strong style={styles.salonTitle}>Plano del salon</strong>
                <span style={styles.salonReadonly}>
                  Vista operativa de {activeSalonRoom.name}. La distribucion solo la modifica el administrador.
                </span>
              </div>
              <div style={styles.salonHeaderActions}>
                <span style={styles.salonMeta}>
                  {activeSalonOccupiedCount}/{selectedSalonTables.length} ocupadas
                </span>
                <button
                  type="button"
                  style={styles.takeawayButton}
                  onClick={openTakeawayOrder}
                >
                  + Pedido para retirar
                </button>
              </div>
            </div>

            {salonRooms.length > 1 ? (
              <div className="waiter-room-tabs" style={styles.roomTabs}>
                {salonRooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    style={{
                      ...styles.roomTab,
                      ...(room.id === resolvedSalonRoomId ? styles.roomTabActive : {}),
                    }}
                    onClick={() => setSelectedSalonRoomId(room.id)}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="waiter-salon-floor-scroll">
              <div className="waiter-salon-floor" style={styles.salonFloor}>
                {activeSalonFixtures.map((fixture) => (
                  <div
                    key={fixture.id || `${fixture.type}-${fixture.x}-${fixture.y}`}
                    style={{
                      ...styles.salonFixture,
                      left: `${clampPercentage(fixture.x, 50)}%`,
                      top: `${clampPercentage(fixture.y, 50)}%`,
                      width: `${Math.max(6, Math.min(45, Number(fixture.width || 16)))}%`,
                      height: `${Math.max(5, Math.min(45, Number(fixture.height || 8)))}%`,
                    }}
                  >
                    {fixture.label || SALON_FIXTURE_LABELS[fixture.type] || "Sector"}
                  </div>
                ))}

                {selectedSalonTables.map((table, index) => {
                  const assignment = tableAssignmentsById.get(table.id) || null;
                  const order = findOperationalOrderForTable(
                    operationalOrders.mesasActivas,
                    table
                  );
                  const tableReservations = getSalonReservationsForTable(
                    operationalOrders.salonReservations || {},
                    table,
                    serviceRequestClock
                  );
                  const reservation = getSalonReservationForTable(
                    operationalOrders.salonReservations || {},
                    table,
                    serviceRequestClock
                  );
                  const nextReservation = tableReservations[0] || null;
                  const layout = resolveWaiterTableLayout(
                    operationalOrders.salonLayout?.[table.id],
                    index,
                    selectedSalonTables.length
                  );
                  const isOwn = assignment?.waiterUserId === user?.id;
                  const isOther = Boolean(assignment?.waiterUserId && !isOwn);
                  const occupied = Boolean(order || assignment);
                  const reserved = !occupied && Boolean(reservation);
                  const tableServiceRequests =
                    visibleServiceRequestsByTableId.get(table.id) || [];
                  const hasWaiterCall = tableServiceRequests.length > 0;
                  const hasEscalatedWaiterCall = tableServiceRequests.some((request) =>
                    isWaiterServiceRequestEscalated(request, serviceRequestClock)
                  );
                  const tableOrders = getOperationalOrdersForTable(
                    operationalOrders.mesasActivas,
                    table
                  );
                  const readyOrderForCurrentWaiter = tableOrders.find((candidate) => {
                    if (!isOrderReadyForDelivery(candidate)) {
                      return false;
                    }

                    const orderWaiterId =
                      candidate?.assignedWaiterUserId || assignment?.waiterUserId || "";

                    return !orderWaiterId || orderWaiterId === user?.id;
                  });
                  const hasReadyDeliveryAlert = visibleDeliveryReadyAlerts.some((alert) =>
                    alert?.tableId
                      ? alert.tableId === table.id
                      : normalizeTableLabel(alert?.mesa) ===
                        normalizeTableLabel(table.label || `Mesa ${table.tableNumber}`)
                  );
                  const hasReadyForDelivery = Boolean(
                    readyOrderForCurrentWaiter || hasReadyDeliveryAlert
                  );

                  return (
                    <button
                      type="button"
                      key={table.id}
                      style={{
                        ...styles.salonTable,
                        ...(occupied
                          ? styles.salonTableOccupied
                          : reserved
                            ? styles.salonTableReserved
                            : styles.salonTableFree),
                        ...(hasReadyForDelivery ? styles.salonTableReady : {}),
                        ...(isOwn ? styles.salonTableOwn : {}),
                        left: `${layout.x}%`,
                        top: `${layout.y}%`,
                        width: `${layout.width}px`,
                        height: `${layout.height}px`,
                        borderRadius: getTableBorderRadius(layout.shape),
                        ...(hasWaiterCall
                          ? {
                              animation:
                                "waiterSalonTableCallPulse 1.05s ease-in-out infinite",
                            }
                          : hasReadyForDelivery
                            ? {
                                animation:
                                  "waiterSalonTableReadyPulse 1.2s ease-in-out infinite",
                              }
                            : {}),
                      }}
                      onClick={() => handleSelectTable(table)}
                    >
                      <strong style={styles.salonTableLabel}>
                        {table.label || `Mesa ${table.tableNumber}`}
                      </strong>
                      {hasWaiterCall || hasReadyForDelivery ? (
                        <span
                          style={
                            hasWaiterCall
                              ? styles.salonTableCallBadge
                              : styles.salonTableReadyBadge
                          }
                        >
                          {hasWaiterCall
                            ? hasEscalatedWaiterCall
                              ? "URGENTE"
                              : "LLAMADO"
                            : "LISTO"}
                        </span>
                      ) : null}
                      <span style={styles.salonTableStatus}>
                        {hasWaiterCall
                          ? tableServiceRequests[0]?.summary || "Necesita atencion"
                          : hasReadyForDelivery
                            ? "Listo para entregar"
                          : isOwn
                          ? "Tu mesa"
                          : isOther
                            ? assignment.waiterName || "Ocupada"
                            : order
                              ? "Ocupada"
                              : reserved
                                ? "Bloqueada"
                                : nextReservation
                                  ? `Reserva ${formatReservationTime(
                                      nextReservation.reservedFor
                                    )}`
                                  : "Libre"}
                      </span>
                    </button>
                  );
                })}

                {!selectedSalonTables.length ? (
                  <div style={styles.salonEmpty}>
                    No hay mesas asignadas a este ambiente.
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        )}

        {launcherInstallTarget ? (
          <div
            style={styles.launcherOverlay}
            onClick={() => setLauncherInstallTarget("")}
          >
            <section
              style={styles.launcherDialog}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Launcher de Mozzo Mozo"
            >
              <div style={styles.launcherHeader}>
                <div>
                  <span style={styles.launcherEyebrow}>Mozzo Mozo</span>
                  <h2 style={styles.launcherTitle}>
                    {launcherInstallTarget === "ios"
                      ? "Instalar en iPhone"
                      : "Instalar launcher Android"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setLauncherInstallTarget("")}
                  style={styles.launcherCloseButton}
                >
                  Cerrar
                </button>
              </div>

              {launcherInstallTarget === "ios" ? (
                <div style={styles.launcherContent}>
                  <div style={styles.launcherQrCard}>
                    <div style={styles.launcherQrFrame}>
                      {waiterLauncherUrl ? (
                        <QRCode
                          value={waiterLauncherUrl}
                          size={196}
                          bgColor="#ffffff"
                          fgColor="#0f172a"
                        />
                      ) : null}
                    </div>
                    <a href={waiterLauncherUrl || undefined} style={styles.androidLauncherButton}>
                      Abrir Mozzo Mozo
                    </a>
                  </div>

                  <div style={styles.launcherInstructions}>
                    <strong style={styles.launcherInstructionsTitle}>
                      En iPhone se instala desde Safari
                    </strong>
                    <ol style={styles.launcherSteps}>
                      <li>Escanea este QR o abre el enlace desde Safari en el iPhone.</li>
                      <li>Toca Compartir y elige "Agregar a pantalla de inicio".</li>
                      <li>Confirma el nombre "Mozzo Mozo".</li>
                      <li>Abre el icono e inicia sesion con el correo y contrasena del mozo.</li>
                    </ol>
                    <div style={styles.launcherCallout}>
                      Queda como un icono independiente y abre directo al panel de mozo.
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.launcherContent}>
                  <div style={styles.launcherQrCard}>
                    <div style={styles.launcherQrFrame}>
                      {androidWaiterApkUrl ? (
                        <QRCode
                          value={androidWaiterApkUrl}
                          size={196}
                          bgColor="#ffffff"
                          fgColor="#0f172a"
                        />
                      ) : null}
                    </div>
                    <a
                      href={androidWaiterApkUrl || undefined}
                      download={androidWaiterApkFileName}
                      style={{
                        ...styles.androidLauncherButton,
                        ...(androidWaiterApkUrl ? {} : styles.launcherLinkDisabled),
                      }}
                      aria-disabled={!androidWaiterApkUrl}
                      onClick={(event) => {
                        if (!androidWaiterApkUrl) {
                          event.preventDefault();
                        }
                      }}
                    >
                      Descargar APK
                    </a>
                  </div>

                  <div style={styles.launcherInstructions}>
                    <strong style={styles.launcherInstructionsTitle}>
                      Para usarla en una tablet o celular
                    </strong>
                    <ol style={styles.launcherSteps}>
                      <li>Escanea este QR desde el dispositivo Android.</li>
                      <li>Descarga e instala la aplicacion "Mozzo Mozo".</li>
                      <li>Abrela e inicia sesion con el correo y contrasena del mozo.</li>
                      <li>Elige la sucursal si trabaja en mas de una.</li>
                    </ol>
                    <div style={styles.launcherCallout}>
                      Esta version abre el acceso de mozo de MozzoApp y permite usar la tablet en
                      vertical u horizontal. No bloquea el dispositivo como el launcher de kiosco.
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        ) : null}

        {isOrderEditorOpen ? (
          <div
            className="waiter-order-modal-overlay"
            style={styles.modalOverlay}
            onClick={closeTableModal}
          >
            <div
              className={`waiter-order-modal-card${
                orderWorkspaceTab === "menu" ? " waiter-order-modal-card-menu" : ""
              }`}
              style={{
                ...styles.modalCard,
                ...(orderWorkspaceTab === "menu" ? styles.modalCardMenuWorkspace : {}),
              }}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={
                orderTargetMode === "takeaway"
                  ? "Crear pedido para retirar"
                  : `Gestionar ${selectedTable?.label || `Mesa ${selectedTable?.tableNumber || ""}`}`
              }
              >
              <div className="waiter-order-modal-header" style={styles.modalHeader}>
                <div>
                  <div style={styles.badge}>
                    {orderTargetMode === "takeaway"
                      ? "Retiro"
                      : selectedTable?.tableNumber
                        ? `Mesa ${selectedTable.tableNumber}`
                        : selectedTable?.label || "Mesa"}
                  </div>
                  {orderTargetMode === "takeaway" ? (
                    <>
                      <h2 style={styles.modalTitle}>Pedido para retirar</h2>
                      <p style={styles.modalSubtitle}>
                        Carga el pedido completo y dejalo identificado con el nombre de quien lo retira.
                      </p>
                    </>
                  ) : null}
                  {!canEditSelectedTableOrder ? (
                    <div style={styles.permissionBanner}>
                      {tableOrderPermissionMessage}
                    </div>
                  ) : null}
                  {activeOrderSummary ? (
                    <div style={styles.orderLiveMeta}>
                      <span style={styles.orderLiveChip}>{activeOrderSummary.sourceLabel}</span>
                      <span style={styles.orderLiveChip}>{activeOrderSummary.phaseLabel}</span>
                      {activeOrderSummary.customerName ? (
                        <span style={styles.orderLiveChip}>
                          Comensal: {activeOrderSummary.customerName}
                        </span>
                      ) : null}
                      {activeOrderSummary.updatedAt ? (
                        <span style={styles.orderLiveText}>
                          Ultima actualizacion: {activeOrderSummary.updatedAt}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {orderTargetMode === "table" && selectedTableReservation ? (
                    <div style={styles.reservationCard}>
                      <strong>Mesa bloqueada por reserva</strong>
                      <span>
                        {selectedTableReservation.customerName || "Cliente"}
                        {selectedTableReservation.partySize
                          ? ` - ${selectedTableReservation.partySize} personas`
                          : ""}
                        {selectedTableReservation.reservedFor
                          ? ` - ${formatDateTime(selectedTableReservation.reservedFor)}`
                          : ""}
                      </span>
                      <span>
                        Bloqueo configurado desde {formatReservationTime(
                          getSalonReservationWindow(selectedTableReservation).startsAt
                        )} ({Number(selectedTableReservation.blockMinutesBefore || 120)} min antes).
                      </span>
                      {selectedTableReservation.notes ? (
                        <span>{selectedTableReservation.notes}</span>
                      ) : null}
                    </div>
                  ) : orderTargetMode === "table" && selectedTableNextReservation ? (
                    <div style={styles.reservationCard}>
                      <strong>Proxima reserva</strong>
                      <span>
                        {selectedTableNextReservation.customerName || "Cliente"}
                        {selectedTableNextReservation.partySize
                          ? ` - ${selectedTableNextReservation.partySize} personas`
                          : ""}
                        {selectedTableNextReservation.reservedFor
                          ? ` - ${formatDateTime(selectedTableNextReservation.reservedFor)}`
                          : ""}
                      </span>
                      <span>
                        La mesa se bloqueara desde {formatReservationTime(
                          getSalonReservationWindow(selectedTableNextReservation).startsAt
                        )}.
                      </span>
                      {selectedTableNextReservation.notes ? (
                        <span>{selectedTableNextReservation.notes}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <button type="button" onClick={closeTableModal} style={styles.secondaryButton}>
                  Cerrar
                </button>
              </div>

              {orderTargetMode === "table" && selectedTableServiceRequests.length > 0 ? (
                <section style={styles.tableCallPanel}>
                  <div style={styles.tableCallHeader}>
                    <div>
                      <span style={styles.tableCallEyebrow}>LLAMADO ACTIVO</span>
                      <strong style={styles.tableCallTitle}>
                        Esta mesa necesita atencion
                      </strong>
                    </div>
                    <span style={styles.tableCallCount}>
                      {selectedTableServiceRequests.length}
                    </span>
                  </div>
                  {selectedTableServiceRequests.map((request) => (
                    <article key={request.id} style={styles.tableCallRequest}>
                      <div style={styles.tableCallCopy}>
                        <strong>{request.summary || "Cliente llama al mozo."}</strong>
                        <span>
                          {isWaiterServiceRequestEscalated(
                            request,
                            serviceRequestClock
                          )
                            ? "Aviso escalado a todo el equipo"
                            : "Aviso asignado a vos"}
                        </span>
                      </div>
                      <button
                        type="button"
                        style={styles.tableCallButton}
                        onClick={() => handleCompleteVisibleServiceRequest(request)}
                      >
                        Marcar atendido
                      </button>
                    </article>
                  ))}
                </section>
              ) : null}

              {tableOrderMessage ? <div style={styles.successBanner}>{tableOrderMessage}</div> : null}

              {orderTargetMode === "table" && selectedTableOrders.length > 0 ? (
                <section style={styles.accountSection}>
                  <div style={styles.accountSectionHeader}>
                    <div style={styles.accountSectionCopy}>
                      <strong style={styles.accountSectionTitle}>Cuentas y comensales</strong>
                      <span style={styles.accountSectionHelp}>
                        Elegí una cuenta para verla, agregar productos o cobrarla por separado.
                      </span>
                    </div>
                    <button
                      type="button"
                      style={styles.newAccountButton}
                      onClick={() => setSelectedExistingOrderId(NEW_ORDER_ACCOUNT_ID)}
                      disabled={!canEditSelectedTableOrder}
                    >
                      + Nueva cuenta
                    </button>
                  </div>

                  {!canChargeOrders ? (
                    <div style={styles.chargePermissionBanner}>
                      {chargePermissionMessage}
                    </div>
                  ) : null}

                  <div style={styles.accountGrid}>
                    {selectedTableOrders.map((order, index) => {
                      const isSelected = activeOrderForSelectedTable?.id === order.id;
                      const paymentState = resolveOperationalOrderPaymentState(
                        order,
                        getPaymentOperations(activeBranchScope),
                        order?.paymentOperationId
                      );
                      const isPaid = paymentState.isPaid;
                      const pendingWaiterQrPayment =
                        paymentState.pendingDigitalOperation?.paymentRail ===
                        PAYMENT_RAILS.WAITER_QR;
                      const isDigital =
                        paymentState.hasPendingDigitalPayment ||
                        isOperationalOrderDigital(order);
                      const isChargeable = paymentState.canCollectCash;

                      return (
                        <article
                          key={order.id}
                          style={{
                            ...styles.accountCard,
                            ...(isSelected ? styles.accountCardActive : {}),
                          }}
                        >
                          <button
                            type="button"
                            style={styles.accountSelectButton}
                            onClick={() => setSelectedExistingOrderId(order.id)}
                          >
                            <span style={styles.accountName}>
                              {getOperationalCustomerName(order) || `Comensal ${index + 1}`}
                            </span>
                            <strong style={styles.accountAmount}>
                              {formatCurrency(getOperationalOrderTotal(order))}
                            </strong>
                            <span style={styles.accountDetail}>
                              {isPaid
                                ? "Pagado"
                                : pendingWaiterQrPayment
                                ? "QR pendiente de confirmacion"
                                : isDigital
                                ? "Pago digital"
                                : "Pendiente en caja"}
                            </span>
                          </button>

                          {isChargeable || pendingWaiterQrPayment ? (
                            <button
                              type="button"
                              style={{
                                ...styles.accountChargeButton,
                                ...(!canChargeOrders
                                  ? styles.accountChargeButtonRestricted
                                  : {}),
                              }}
                              onClick={() => chargeOperationalOrder(order)}
                              aria-disabled={!canChargeOrders}
                              title={
                                canChargeOrders
                                  ? "Cobrar esta cuenta"
                                  : "El cobro debe habilitarse desde los permisos del mozo"
                              }
                            >
                              {canChargeOrders
                                ? pendingWaiterQrPayment
                                  ? "Ver QR"
                                  : "Cobrar"
                                : "Cobro sin permiso"}
                            </button>
                          ) : (
                            <span style={styles.accountStatus}>
                              {isPaid ? "Cobrado" : isDigital ? "Digital" : "Sin saldo"}
                            </span>
                          )}
                          {pendingWaiterQrPayment && canChargeOrders &&
                           paymentState.pendingDigitalOperation?.paymentIntentId &&
                           getInteroperableQrData(paymentState.pendingDigitalOperation) ? (
                            <button
                              type="button"
                              style={{ ...styles.waiterChargeCancelButton, width: "100%" }}
                              onClick={() => chargeOperationalOrder(order, { cancelQr: true })}
                            >
                              Cambiar a efectivo
                            </button>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <label
                className={`waiter-order-customer-field${
                  orderWorkspaceTab === "menu" ? " waiter-order-customer-field-menu-hidden" : ""
                }`}
                style={styles.customerField}
              >
                <span style={styles.customerFieldLabel}>
                  {orderTargetMode === "takeaway"
                    ? "Nombre para retirar"
                    : "Nombre del comensal"}
                </span>
                <input
                  type="text"
                  style={styles.input}
                  value={orderCustomerName}
                  onChange={(event) => setOrderCustomerName(event.target.value)}
                  placeholder={
                    orderTargetMode === "takeaway"
                      ? "Ejemplo: Claudia"
                      : "Ejemplo: Comensal 1"
                  }
                  disabled={!canEditSelectedTableOrder}
                />
              </label>

              <div style={styles.orderWorkspaceTabs} role="tablist" aria-label="Gestion del pedido">
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderWorkspaceTab === "order"}
                  style={{
                    ...styles.orderWorkspaceTab,
                    ...(orderWorkspaceTab === "order" ? styles.orderWorkspaceTabActive : {}),
                  }}
                  onClick={() => setOrderWorkspaceTab("order")}
                >
                  <span>Pedido actual</span>
                  <span style={styles.orderWorkspaceCount}>{tableOrderItems.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderWorkspaceTab === "menu"}
                  style={{
                    ...styles.orderWorkspaceTab,
                    ...(orderWorkspaceTab === "menu" ? styles.orderWorkspaceTabActive : {}),
                  }}
                  onClick={() => setOrderWorkspaceTab("menu")}
                >
                  <span>Agregar productos</span>
                </button>
              </div>

              <div className="waiter-order-modal-grid" style={styles.modalGrid}>
                <section
                  style={{
                    ...styles.modalPanel,
                    ...(orderWorkspaceTab !== "order" ? styles.hiddenWorkspacePanel : {}),
                  }}
                >
                  <div style={styles.sectionHeader}>
                    <strong style={styles.sectionTitle}>Pedido actual</strong>
                    <span style={styles.sectionMeta}>
                      {activeOrderSummary?.customerName
                        ? activeOrderSummary.customerName
                        : `${tableOrderItems.length} item${tableOrderItems.length === 1 ? "" : "s"}`}
                    </span>
                  </div>

                  {tableOrderItems.length > 0 ? (
                    <div style={styles.orderItemList}>
                      {tableOrderItems.map((item) => (
                        <div key={item.id} style={styles.orderItemRow}>
                          <div style={styles.orderItemInfo}>
                            <strong>{item.nombre}</strong>
                            {Array.isArray(item.selectionDetails) &&
                            item.selectionDetails.length > 0 ? (
                              <span style={styles.orderItemMeta}>
                                {item.selectionDetails.join(" · ")}
                              </span>
                            ) : null}
                            {item.unitPrice ? (
                              <span style={styles.orderItemMeta}>
                                {formatCurrency(Number(item.unitPrice || 0) * Math.max(1, Number(item.quantity || 1)))}
                              </span>
                            ) : null}
                          </div>
                          <div style={styles.orderItemActions}>
                            {item.sourceProductId ? (
                              <button
                                type="button"
                                style={styles.editButton}
                                onClick={() => editTableOrderItem(item)}
                                disabled={!canEditSelectedTableOrder}
                              >
                                Editar
                              </button>
                            ) : null}
                            {item.sourceProductId ? (
                              <div style={styles.qtyControls}>
                                <button
                                  type="button"
                                  style={styles.qtyButton}
                                  onClick={() => updateDraftItemQuantity(item.id, -1)}
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  -
                                </button>
                                <span style={styles.qtyValue}>{Math.max(1, Number(item.quantity || 1))}</span>
                                <button
                                  type="button"
                                  style={styles.qtyButton}
                                  onClick={() => updateDraftItemQuantity(item.id, 1)}
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  +
                                </button>
                              </div>
                            ) : null}
                            <button
                              type="button"
                              style={styles.removeButton}
                              onClick={() => removeTableOrderItem(item.id)}
                              disabled={!canEditSelectedTableOrder}
                            >
                              Quitar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyState}>
                      Esta mesa todavia no tiene items cargados. Puedes tomar el pedido manualmente.
                    </div>
                  )}
                </section>

                <section
                  className={`waiter-menu-workspace-panel${
                    !selectedMenuProduct ? " waiter-menu-workspace-panel-browse" : ""
                  }`}
                  style={{
                    ...styles.modalPanel,
                    ...(orderWorkspaceTab === "menu" && !selectedMenuProduct
                      ? styles.menuWorkspacePanel
                      : {}),
                    ...(orderWorkspaceTab !== "menu" ? styles.hiddenWorkspacePanel : {}),
                  }}
                >
                  <div style={styles.sectionHeader}>
                    <strong style={styles.sectionTitle}>Agregar al pedido</strong>
                    <span style={styles.sectionMeta}>Carta de la sucursal</span>
                  </div>

                  {waiterAvailableCombos.length > 0 ? (
                    <div style={styles.quickSection}>
                      <div style={styles.quickSectionHeader}>
                        <strong style={styles.quickSectionTitle}>Combos</strong>
                        <span style={styles.quickSectionMeta}>
                          {waiterAvailableCombos.length} disponible{waiterAvailableCombos.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div style={styles.quickCardList}>
                        {waiterAvailableCombos.map((combo) => (
                          <button
                            key={combo.id}
                            type="button"
                            style={styles.quickCardButton}
                            onClick={() => addComboToTableOrder(combo)}
                            disabled={!canEditSelectedTableOrder}
                          >
                            <div style={styles.quickCardInfo}>
                              <strong>{combo.title}</strong>
                              <span style={styles.quickCardText}>
                                {combo.pricingSummary || combo.hook || "Combo activo"}
                              </span>
                            </div>
                            <span style={styles.quickCardAction}>Agregar</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {waiterAvailablePromotions.length > 0 ? (
                    <div style={styles.quickSection}>
                      <div style={styles.quickSectionHeader}>
                        <strong style={styles.quickSectionTitle}>Promociones</strong>
                        <span style={styles.quickSectionMeta}>
                          {waiterAvailablePromotions.length} activa{waiterAvailablePromotions.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div style={styles.quickCardList}>
                        {waiterAvailablePromotions.map((promotion) => (
                          <button
                            key={promotion.id}
                            type="button"
                            style={styles.quickCardButton}
                            onClick={() => applyPromotionFilter(promotion)}
                            disabled={!canEditSelectedTableOrder}
                          >
                            <div style={styles.quickCardInfo}>
                              <strong>{promotion.title}</strong>
                              <span style={styles.quickCardText}>
                                {promotion.pricingSummary || promotion.hook || "Promo activa"}
                              </span>
                            </div>
                            <span style={styles.quickCardAction}>Usar</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div style={styles.inlineFilters}>
                    <select
                      value={tableOrderCategoryId}
                      onChange={(event) => setTableOrderCategoryId(event.target.value)}
                      style={styles.input}
                      disabled={!canEditSelectedTableOrder}
                    >
                      <option value="">Todas las categorias</option>
                      {menuCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.nombre}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={tableOrderSearch}
                      onChange={(event) => setTableOrderSearch(event.target.value)}
                      placeholder="Buscar plato"
                      style={styles.input}
                      disabled={!canEditSelectedTableOrder}
                    />
                  </div>

                  {selectedMenuProduct ? (
                    <div style={styles.waiterProductDetailCard}>
                      <div style={styles.waiterProductDetailHeader}>
                        <div style={styles.waiterProductDetailTitleWrap}>
                          <strong style={styles.waiterProductDetailTitle}>
                            {selectedMenuProduct.nombre}
                          </strong>
                          <span style={styles.waiterProductDetailSubtitle}>
                            {selectedMenuProduct.subcategoria || "Producto"}
                          </span>
                        </div>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={closeWaiterProductDetail}
                        >
                          Cerrar
                        </button>
                      </div>

                      <div style={styles.waiterSummaryRow}>
                        <span style={styles.waiterSummaryChip}>
                          {selectedMenuProduct.ingredients?.length || 0} ingredientes
                        </span>
                        <span style={styles.waiterSummaryChip}>
                          {selectedMenuProductSummary?.removedUnits || 0} quitados
                        </span>
                        <span style={styles.waiterSummaryChip}>
                          {selectedMenuProductSummary?.extraUnits || 0} extras
                        </span>
                      </div>

                      {selectedMenuProduct.ingredients?.length > 0 ? (
                        <div style={styles.waiterIngredientList}>
                          {selectedMenuProduct.ingredients.map((ingredient) => {
                            const currentQuantity = Number(
                              draftSelection?.[ingredient.id] ??
                                ingredient.baseQuantity ??
                                0
                            );
                            const baseQuantity = Number(ingredient.baseQuantity || 0);

                            return (
                              <div key={ingredient.id} style={styles.waiterIngredientRow}>
                                <div style={styles.waiterIngredientInfo}>
                                  <div style={styles.waiterIngredientNameRow}>
                                    <strong style={styles.waiterIngredientName}>
                                      {ingredient.name}
                                    </strong>
                                    <span style={styles.waiterIngredientState}>
                                      {getWaiterIngredientStateLabel(
                                        ingredient,
                                        currentQuantity
                                      )}
                                    </span>
                                  </div>
                                  <div style={styles.waiterIngredientMetaRow}>
                                    <span style={styles.waiterIngredientMeta}>
                                      Base: {baseQuantity}
                                    </span>
                                    <span style={styles.waiterIngredientMeta}>
                                      {ingredient.type === "required"
                                        ? "Ingrediente base"
                                        : "Ingrediente opcional"}
                                    </span>
                                    <span style={styles.waiterIngredientMeta}>
                                      {ingredient.canRemove
                                        ? "Se puede quitar"
                                        : "No se puede quitar"}
                                    </span>
                                    <span style={styles.waiterIngredientMeta}>
                                      {ingredient.canAddMore
                                        ? `Se puede agregar hasta ${Number(
                                            ingredient.maxExtraQuantity ||
                                              DEFAULT_MAX_EXTRA_QUANTITY
                                          )} extra`
                                        : "No se ofrece como extra"}
                                    </span>
                                    {Number(ingredient.price || 0) > 0 ? (
                                      <span style={styles.waiterIngredientMeta}>
                                        Extra: {formatCurrency(Number(ingredient.price || 0))}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                                <div style={styles.waiterIngredientControls}>
                                  {ingredient.canRemove || ingredient.canAddMore ? (
                                    <>
                                      <button
                                        type="button"
                                        style={styles.qtyButton}
                                        onClick={() =>
                                          updateWaiterIngredientQuantity(ingredient, -1)
                                        }
                                        disabled={
                                          !canEditSelectedTableOrder ||
                                          currentQuantity <=
                                          (ingredient.canRemove
                                            ? 0
                                            : Number(ingredient.baseQuantity || 0))
                                        }
                                      >
                                        -
                                      </button>
                                      <span style={styles.qtyValue}>{currentQuantity}</span>
                                      <button
                                        type="button"
                                        style={styles.qtyButton}
                                        onClick={() =>
                                          updateWaiterIngredientQuantity(ingredient, 1)
                                        }
                                        disabled={
                                          !canEditSelectedTableOrder ||
                                          currentQuantity >=
                                          (ingredient.canAddMore
                                            ? Number(ingredient.baseQuantity || 0) +
                                              Math.max(
                                                1,
                                                Number(
                                                  ingredient.maxExtraQuantity ||
                                                    DEFAULT_MAX_EXTRA_QUANTITY
                                                )
                                              )
                                            : Number(ingredient.baseQuantity || 0))
                                        }
                                      >
                                        +
                                      </button>
                                    </>
                                  ) : (
                                    <span style={styles.waiterFixedChip}>Fijo</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={styles.emptyState}>
                          Este plato no tiene ingredientes cargados para personalizar.
                        </div>
                      )}

                      {selectedEmpanadaSaleOptions.length > 0 ? (
                        <div style={styles.waiterLinkedChoiceWrap}>
                          <div style={styles.waiterLinkedChoiceHeader}>
                            <div>
                              <strong style={styles.waiterLinkedChoiceTitle}>
                                Elegi la presentacion
                              </strong>
                              <p style={styles.waiterLinkedChoiceText}>
                                Selecciona si el comensal pide unidad, media docena o
                                docena.
                              </p>
                            </div>
                            <span style={styles.waiterSummaryChip}>Obligatorio</span>
                          </div>

                          <div style={styles.waiterLinkedChoiceList}>
                            {selectedEmpanadaSaleOptions.map((option) => {
                              const isActive =
                                selectedEmpanadaSaleOption?.id === option.id;

                              return (
                                <button
                                  key={`waiter-empanada-sale:${option.id}`}
                                  type="button"
                                  style={{
                                    ...styles.waiterLinkedChoiceButton,
                                    ...(isActive
                                      ? styles.waiterLinkedChoiceButtonActive
                                      : {}),
                                  }}
                                  onClick={() =>
                                    setDraftEmpanadaSaleOptionId(option.id)
                                  }
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  <div>
                                    <strong style={styles.waiterLinkedChoiceProductName}>
                                      {option.label}
                                    </strong>
                                    <p style={styles.waiterLinkedChoiceProductDescription}>
                                      {Number(option.unitCount || 1)}{" "}
                                      {Number(option.unitCount || 1) === 1
                                        ? "unidad"
                                        : "unidades"}
                                    </p>
                                  </div>
                                  <span style={styles.waiterLinkedChoicePrice}>
                                    {formatCurrency(Number(option.price || 0))}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {selectedEmpanadaCookingOptions.length > 0 ? (
                        <div style={styles.waiterLinkedChoiceWrap}>
                          <div style={styles.waiterLinkedChoiceHeader}>
                            <div>
                              <strong style={styles.waiterLinkedChoiceTitle}>
                                Elegi la coccion
                              </strong>
                              <p style={styles.waiterLinkedChoiceText}>
                                Si hay mas de una activa, elegi como sale la
                                empanada para cocina.
                              </p>
                            </div>
                            <span style={styles.waiterSummaryChip}>
                              {selectedEmpanadaCookingOptions.length > 1
                                ? "Obligatorio"
                                : "Automatico"}
                            </span>
                          </div>

                          <div style={styles.waiterLinkedChoiceList}>
                            {selectedEmpanadaCookingOptions.map((option) => {
                              const isActive =
                                selectedEmpanadaCookingOption?.id === option.id;

                              return (
                                <button
                                  key={`waiter-empanada-cooking:${option.id}`}
                                  type="button"
                                  style={{
                                    ...styles.waiterLinkedChoiceButton,
                                    ...(isActive
                                      ? styles.waiterLinkedChoiceButtonActive
                                      : {}),
                                  }}
                                  onClick={() =>
                                    setDraftEmpanadaCookingOptionId(option.id)
                                  }
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  <div>
                                    <strong style={styles.waiterLinkedChoiceProductName}>
                                      {option.label}
                                    </strong>
                                    <p style={styles.waiterLinkedChoiceProductDescription}>
                                      Se envia como detalle a cocina.
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {selectedPizzaSizeOptions.length > 0 ? (
                        <div style={styles.waiterLinkedChoiceWrap}>
                          <div style={styles.waiterLinkedChoiceHeader}>
                            <div>
                              <strong style={styles.waiterLinkedChoiceTitle}>
                                Elegi el tamano
                              </strong>
                              <p style={styles.waiterLinkedChoiceText}>
                                Primero define si es grande, familiar, chica u otra
                                presentacion; despues elegi una modalidad habilitada.
                              </p>
                            </div>
                            <span style={styles.waiterSummaryChip}>Obligatorio</span>
                          </div>

                          <div style={styles.waiterLinkedChoiceList}>
                            {selectedPizzaSizeOptions.map((option) => {
                              const isActive = selectedPizzaSizeOption?.id === option.id;

                              return (
                                <button
                                  key={`waiter-pizza-size:${option.id}`}
                                  type="button"
                                  style={{
                                    ...styles.waiterLinkedChoiceButton,
                                    ...(isActive
                                      ? styles.waiterLinkedChoiceButtonActive
                                      : {}),
                                  }}
                                  onClick={() => {
                                    setDraftPizzaSizeOptionId(option.id);
                                    setDraftPizzaPortionMode(getDefaultPizzaSaleMode(option));
                                    setDraftPizzaHalfProductId("");
                                  }}
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  <div>
                                    <strong style={styles.waiterLinkedChoiceProductName}>
                                      {option.label}
                                    </strong>
                                    <p style={styles.waiterLinkedChoiceProductDescription}>
                                      {getPizzaSizePriceSummary(option, formatCurrency)}
                                    </p>
                                  </div>
                                  <span style={styles.waiterLinkedChoicePrice}>
                                    {formatCurrency(getPizzaStartingPrice(option))}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {selectedPizzaSizeOption && <PizzaSaleModeSelector option={selectedPizzaSizeOption}
                        value={draftPizzaPortionMode} disabled={!canEditSelectedTableOrder}
                        canCombine={selectedPizzaHalfOptions.length > 0} onChange={(mode) => {
                          setDraftPizzaPortionMode(mode); setDraftPizzaHalfProductId("");
                        }} />}
                      {draftPizzaPortionMode === "half_and_half" && selectedPizzaHalfOptions.length > 0 ? (
                        <div style={styles.waiterLinkedChoiceWrap}>
                          <div style={styles.waiterLinkedChoiceHeader}>
                            <div>
                              <strong style={styles.waiterLinkedChoiceTitle}>
                                Hacer mitad y mitad
                              </strong>
                              <p style={styles.waiterLinkedChoiceText}>
                                Combina esta pizza con otra del mismo tamano. Se cobra
                                media pizza de cada sabor.
                              </p>
                            </div>
                            <span style={styles.waiterSummaryChip}>Opcional</span>
                          </div>

                          <div style={styles.waiterLinkedChoiceList}>

                            {selectedPizzaHalfOptions.map((option) => {
                              const isActive = draftPizzaHalfProductId === option.id;
                              const optionPricing = getPizzaHalfAndHalfPricing(
                                selectedMenuProduct,
                                option,
                                { sizeOptionId: selectedPizzaSizeOption?.id || "" }
                              );
                              const optionExtraPrice = optionPricing.extraUnitPrice;

                              return (
                                <button
                                  key={`waiter-pizza-half:${option.id}`}
                                  type="button"
                                  style={{
                                    ...styles.waiterLinkedChoiceButton,
                                    ...(isActive
                                      ? styles.waiterLinkedChoiceButtonActive
                                      : {}),
                                  }}
                                  onClick={() => setDraftPizzaHalfProductId(option.id)}
                                  disabled={!canEditSelectedTableOrder}
                                >
                                  <div>
                                    <strong style={styles.waiterLinkedChoiceProductName}>
                                      1/2 {getPizzaProductName(option)}
                                    </strong>
                                    {option.descripcion || option.description ? (
                                      <p style={styles.waiterLinkedChoiceProductDescription}>
                                        {option.descripcion || option.description}
                                      </p>
                                    ) : (
                                      <p style={styles.waiterLinkedChoiceProductDescription}>
                                        Total mitad y mitad:{" "}
                                        {formatCurrency(optionPricing.totalUnitPrice)}
                                      </p>
                                    )}
                                  </div>
                                  <span style={styles.waiterLinkedChoicePrice}>
                                    {optionExtraPrice > 0
                                      ? `+ ${formatCurrency(optionExtraPrice)}`
                                      : "Sin adicional"}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      {beverageRules.length > 0 ? <div style={styles.waiterLinkedChoiceWrap}>
                        <strong style={styles.waiterLinkedChoiceTitle}>Elegi la variedad de bebida</strong>
                        <p style={styles.waiterLinkedChoiceText}>Obligatorio. Para variedades distintas, agrega cada una por separado.</p>
                        <div style={styles.waiterLinkedChoiceList}>
                          {beverageOptions.map((option) => <button key={option.id} type="button"
                            aria-pressed={selectedBeverageOption?.id === option.id}
                            disabled={!canEditSelectedTableOrder} onClick={() => setDraftBeverageIngredientId(option.targetIngredientId)}
                            style={{ ...styles.waiterLinkedChoiceButton,
                              ...(selectedBeverageOption?.id === option.id ? styles.waiterLinkedChoiceButtonActive : {}) }}>
                            <strong>{option.name}</strong><span>{option.consumptionMl} ml por vaso · Incluido</span>
                          </button>)}
                        </div>
                        {!selectedBeverageOption ? <p role="status">Selecciona una variedad disponible antes de agregar.</p> : null}
                      </div> : null}
                      {selectedMenuProductLinkedRule ? (
                        <div style={styles.waiterLinkedChoiceWrap}>
                          <div style={styles.waiterLinkedChoiceHeader}>
                            <div>
                              <strong style={styles.waiterLinkedChoiceTitle}>
                                {selectedMenuProductLinkedTitle ||
                                  `Elegi ${
                                    selectedLinkedOptions[0]?.subcategoria
                                      ? `una ${selectedLinkedOptions[0].subcategoria.toLowerCase()}`
                                      : "una opcion asociada"
                                  }`}
                              </strong>
                              <p style={styles.waiterLinkedChoiceText}>
                                Puedes elegir una opcion o dejar Nada.
                              </p>
                            </div>
                            <span style={styles.waiterSummaryChip}>Opcional</span>
                          </div>

                          <div style={styles.waiterLinkedChoiceList}>
                            <button
                              type="button"
                              style={{
                                ...styles.waiterLinkedChoiceButton,
                                ...(!draftLinkedProductId
                                  ? styles.waiterLinkedChoiceButtonActive
                                  : {}),
                              }}
                              onClick={() => setDraftLinkedProductId("")}
                              disabled={!canEditSelectedTableOrder}
                            >
                              <div>
                                <strong style={styles.waiterLinkedChoiceProductName}>
                                  Nada
                                </strong>
                                <p style={styles.waiterLinkedChoiceProductDescription}>
                                  Sin combinacion
                                </p>
                              </div>
                              <span style={styles.waiterLinkedChoicePrice}>
                                Sin adicional
                              </span>
                            </button>

                              {selectedLinkedOptions.map((option) => {
                                const isActive = draftLinkedProductId === option.id;

                                return (
                                  <button
                                    key={option.id}
                                    type="button"
                                    style={{
                                      ...styles.waiterLinkedChoiceButton,
                                      ...(isActive
                                        ? styles.waiterLinkedChoiceButtonActive
                                        : {}),
                                    }}
                                    onClick={() => setDraftLinkedProductId(option.id)}
                                    disabled={!canEditSelectedTableOrder}
                                  >
                                    <div>
                                      <strong style={styles.waiterLinkedChoiceProductName}>
                                        {option.nombre}
                                      </strong>
                                      {option.descripcion || option.description ? (
                                        <p style={styles.waiterLinkedChoiceProductDescription}>
                                          {option.descripcion || option.description}
                                        </p>
                                      ) : null}
                                    </div>
                                    <span style={styles.waiterLinkedChoicePrice}>
                                      {formatCurrency(resolveMenuProductPrice(option))}
                                    </span>
                                  </button>
                                );
                              })}
                          </div>
                          {selectedLinkedOptions.length === 0 ? (
                            <div style={styles.emptyState}>
                              No hay otras opciones disponibles en esta combinacion.
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <div style={styles.waiterDetailActions}>
                        <div style={styles.waiterDetailTools}>
                          <div style={styles.waiterDetailQtyWrap}>
                            <span style={styles.waiterDetailQtyLabel}>Cantidad</span>
                            <div style={styles.qtyControls}>
                              <button
                                type="button"
                                style={styles.qtyButton}
                                onClick={() =>
                                  setDraftProductQuantity((prev) => Math.max(1, prev - 1))
                                }
                                disabled={!canEditSelectedTableOrder || draftProductQuantity <= 1}
                              >
                                -
                              </button>
                              <span style={styles.qtyValue}>{draftProductQuantity}</span>
                              <button
                                type="button"
                                style={styles.qtyButton}
                                onClick={() => setDraftProductQuantity((prev) => prev + 1)}
                                disabled={!canEditSelectedTableOrder}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div style={styles.waiterDetailAmountWrap}>
                            <span style={styles.waiterDetailQtyLabel}>Total del plato</span>
                            <strong style={styles.waiterDetailAmountValue}>
                              {formatCurrency(
                                selectedMenuProductUnitPrice * draftProductQuantity
                              )}
                            </strong>
                          </div>
                        </div>

                        <div style={styles.waiterDetailButtonRow}>
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={resetWaiterProductCustomization}
                            disabled={!canEditSelectedTableOrder}
                          >
                            Volver a normal
                          </button>
                          <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={addSelectedProductToTableOrder}
                            disabled={!canEditSelectedTableOrder || (beverageRules.length > 0 && !selectedBeverageOption)}
                          >
                            Agregar al pedido
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div
                    className={`waiter-product-result-list${
                      orderWorkspaceTab === "menu" && !selectedMenuProduct
                        ? " waiter-product-result-list-expanded"
                        : ""
                    }`}
                    style={{
                      ...styles.productResultList,
                      ...(orderWorkspaceTab === "menu" && !selectedMenuProduct
                        ? styles.productResultListExpanded
                        : {}),
                    }}
                  >
                    {waiterMenuProducts.length > 0 ? (
                      waiterMenuProducts.map((product) => {
                        const imageUrl = product.imageUrl || product.fotoUrl || "";

                        return (
                          <button
                            key={product.id}
                            type="button"
                            data-waiter-product-result="true"
                            style={styles.productResultButton}
                            onClick={() => openWaiterProductDetail(product)}
                            disabled={!canEditSelectedTableOrder}
                          >
                              <CatalogImage
                                src={imageUrl}
                                alt=""
                                style={styles.productResultImage}
                              />
                            <div style={styles.productResultInfo}>
                              <strong>{product.nombre}</strong>
                              <span style={styles.productResultMeta}>
                                {product.subcategoria || "Menú"}
                              </span>
                            </div>
                            <span
                              data-waiter-product-price="true"
                              style={styles.productResultPrice}
                            >
                              {productHasPizzaSizeOptions(product) ||
                              productHasEmpanadaSaleOptions(product)
                                ? "Desde "
                                : ""}
                              {formatCurrency(resolveMenuProductPrice(product))}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div style={styles.emptyState}>
                        No encontramos platos para ese filtro en la carta de esta sucursal.
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <div style={styles.modalFooter}>
                <strong style={styles.modalTotal}>
                  Total estimado:{" "}
                  {formatCurrency(tableOrderPricing.finalTotal)}
                </strong>
                <div style={styles.modalFooterActions}>
                  {orderTargetMode === "table" && isSelectedTableAssignedToCurrentWaiter ? (
                    <button
                      type="button"
                      onClick={releaseSelectedTable}
                      style={styles.releaseTableButton}
                    >
                      Liberar mesa
                    </button>
                  ) : null}
                  {orderTargetMode === "table" &&
                  activeOrderForSelectedTable &&
                  !isSelectedTableAssignedToAnotherWaiter ? (
                    <button
                      type="button"
                      onClick={closeSelectedTableService}
                      style={styles.closeTableButton}
                      disabled={!canModifyOrders}
                    >
                      Cerrar mesa
                    </button>
                  ) : null}
                  {orderTargetMode === "table" && activeOrderForSelectedTable ? (
                    <button
                      type="button"
                      onClick={cancelTableOrder}
                      style={styles.dangerButton}
                      disabled={!canEditSelectedTableOrder || !canCancelOrders}
                    >
                      Cancelar pedido
                    </button>
                  ) : null}
                  <button type="button" onClick={closeTableModal} style={styles.secondaryButton}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={saveTableOrderChanges}
                    style={styles.primaryButton}
                    disabled={!canEditSelectedTableOrder}
                  >
                    {orderTargetMode === "takeaway"
                      ? "Crear pedido para retirar"
                      : activeOrderForSelectedTable
                      ? "Guardar esta cuenta"
                      : "Crear cuenta"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {waiterChargeOrder ? (
          <div
            style={{ ...styles.modalOverlay, zIndex: 1250 }}
            onClick={closeWaiterChargeDialog}
          >
            <section
              style={styles.waiterChargeDialog}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Cobrar cuenta del comensal"
            >
              <div style={styles.waiterChargeHeader}>
                <div>
                  <span style={styles.waiterChargeEyebrow}>Cobro individual</span>
                  <h2 style={styles.waiterChargeTitle}>
                    {getOperationalCustomerName(waiterChargeOrder) || "Comensal"}
                  </h2>
                  <span style={styles.waiterChargeTable}>
                    {waiterChargeOrder.mesa || "Pedido de salon"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={closeWaiterChargeDialog}
                  style={styles.launcherCloseButton}
                  disabled={waiterChargeBusy}
                >
                  Cerrar
                </button>
              </div>

              <div style={styles.waiterChargeSummary}>
                <span>Total a cobrar</span>
                <strong>{formatCurrency(getOperationalOrderTotal(waiterChargeOrder))}</strong>
              </div>

              {waiterChargeMessage ? (
                <div
                  style={{
                    ...styles.waiterChargeMessage,
                    ...(waiterChargeCompleted ? styles.waiterChargeMessageSuccess : {}),
                  }}
                >
                  {waiterChargeMessage}
                </div>
              ) : null}
              {releasedWaiterPayments.some((payment) => payment.orderId === waiterChargeOrder?.id) ? (
                <div role="note" style={styles.waiterChargeCancelConfirmation}>
                  Hay un QR anterior liberado solo en MozzoApp. No lo uses nuevamente: si se acredita, habra que revisar el cobro en Caja.
                </div>
              ) : null}

              {!waiterChargeIntent && !waiterChargeCompleted ? (
                <div style={styles.waiterChargeMethods}>
                  <button
                    type="button"
                    style={{ ...styles.waiterChargeMethod, ...styles.waiterChargeCashMethod }}
                    onClick={() => confirmWaiterLocalCharge("counter")}
                    disabled={waiterChargeBusy || !canChargeOrders}
                  >
                    <strong>Efectivo</strong>
                    <span>Registrar el dinero recibido en caja.</span>
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.waiterChargeMethod, ...styles.waiterChargeQrMethod }}
                    onClick={createWaiterQrCharge}
                    disabled={waiterChargeBusy || !canChargeOrders}
                  >
                    <strong>QR interoperable</strong>
                    <span>Generar el QR presencial y esperar la confirmacion real del pago.</span>
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.waiterChargeMethod,
                      ...styles.waiterChargePointMethod,
                    }}
                    onClick={() => confirmWaiterLocalCharge("card")}
                    disabled={waiterChargeBusy || !canChargeOrders}
                    title="Registra una tarjeta que ya fue aprobada en la terminal externa del restaurante."
                  >
                    <strong>Tarjeta</strong>
                    <span>Registrar un pago ya aprobado en la terminal del restaurante.</span>
                  </button>
                </div>
              ) : null}

              {waiterChargeIntent && !waiterChargeCompleted && !waiterQrCancelConfirmation ? (
                <div style={styles.waiterChargeQrPanel}>
                  <div style={styles.waiterChargeQrFrame}>
                    {waiterChargeQrData ? (
                      <QRCode
                        value={waiterChargeQrData}
                        size={220}
                        bgColor="#ffffff"
                        fgColor="#0f172a"
                      />
                    ) : (
                      <span>No se pudo preparar el QR.</span>
                    )}
                  </div>
                  <div style={styles.waiterChargeQrCopy}>
                    <strong>Escanea con una billetera compatible</strong>
                    <span>
                      Es el mismo QR presencial interoperable del kiosk. La cuenta queda pendiente hasta que Mercado Pago confirme la acreditacion.
                    </span>
                    <button
                      type="button"
                      style={styles.waiterChargeRefreshButton}
                      onClick={() => refreshWaiterQrCharge({ silent: false })}
                      disabled={waiterChargeBusy || waiterChargeRefreshing}
                    >
                      {waiterChargeBusy || waiterChargeRefreshing ? "Revisando pago..." : "Actualizar estado"}
                    </button>
                    <button
                      type="button"
                      style={styles.waiterChargeCancelButton}
                      onClick={requestWaiterQrCancellation}
                      disabled={waiterChargeBusy}
                    >
                      Cancelar QR
                    </button>
                  </div>
                </div>
              ) : null}

              {waiterQrCancelConfirmation && waiterChargeIntent && !waiterChargeCompleted ? (
                <div style={styles.waiterChargeCancelConfirmation} role="alertdialog" aria-labelledby="waiter-cancel-qr-title">
                  <strong id="waiter-cancel-qr-title">{waiterQrCancelConfirmation === "local" ? "Liberar el cobro en MozzoApp?" : "Cancelar este QR?"}</strong>
                  <span>
                    {waiterQrCancelConfirmation === "local"
                      ? "Podras cobrar en efectivo o generar otro QR sin cambiar el pedido. Esto NO anula el QR anterior en Mercado Pago: podria acreditarse despues y producir un doble cobro. La liberacion quedara registrada a tu nombre."
                      : "Se intentara anular el QR en Mercado Pago. El pedido seguira en su estado actual. Tambien puedes liberar el cobro solo en MozzoApp para cambiar de medio sin esperar a Mercado Pago."}
                  </span>
                  <div style={styles.waiterChargeCancelActions}>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => setWaiterQrCancelConfirmation(false)}
                      disabled={waiterChargeBusy}
                      autoFocus
                    >
                      Mantener QR
                    </button>
                    <button
                      type="button"
                      style={styles.waiterChargeCancelConfirmButton}
                      onClick={() => cancelWaiterQrCharge({ releaseLocally: waiterQrCancelConfirmation === "local" })}
                      disabled={waiterChargeBusy}
                    >
                      {waiterQrCancelConfirmation === "local"
                        ? waiterChargeBusy ? "Liberando cobro..." : "Confirmar y liberar cobro"
                        : waiterChargeBusy ? "Cancelando QR..." : "Si, cancelar QR"}
                    </button>
                    {waiterQrCancelConfirmation !== "local" ? (
                      <button type="button" style={styles.secondaryButton}
                        disabled={waiterChargeBusy} onClick={() => setWaiterQrCancelConfirmation("local")}>
                        Liberar cobro en MozzoApp
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {waiterChargeCompleted ? (
                <div style={styles.waiterChargeCompleted}>
                  Cobro confirmado. Esta cuenta ya no se puede volver a cobrar.
                </div>
              ) : null}

              <div style={styles.waiterChargeFooter}>
                <button
                  type="button"
                  onClick={closeWaiterChargeDialog}
                  style={styles.secondaryButton}
                  disabled={waiterChargeBusy}
                >
                  {waiterChargeCompleted
                    ? "Listo"
                    : waiterChargeIntent
                      ? "Dejar QR activo"
                      : "Volver"}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = {
  salonHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  takeawayButton: {
    border: "none",
    backgroundColor: "#ff6414",
    color: "#ffffff",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(255, 100, 20, 0.2)",
  },
  accountSection: {
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    borderRadius: "18px",
    padding: "16px",
    display: "grid",
    gap: "14px",
  },
  accountSectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  accountSectionCopy: {
    display: "grid",
    gap: "3px",
  },
  accountSectionTitle: {
    fontSize: "17px",
    color: "#0f172a",
  },
  accountSectionHelp: {
    fontSize: "13px",
    color: "#64748b",
  },
  newAccountButton: {
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    borderRadius: "999px",
    padding: "9px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },
  accountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "10px",
  },
  accountCard: {
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "10px",
    display: "grid",
    gap: "8px",
  },
  accountCardActive: {
    borderColor: "#ff6414",
    boxShadow: "0 8px 20px rgba(255, 100, 20, 0.12)",
  },
  accountSelectButton: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    textAlign: "left",
    display: "grid",
    gap: "4px",
    cursor: "pointer",
  },
  accountName: {
    fontWeight: 800,
    color: "#0f172a",
  },
  accountAmount: {
    fontSize: "18px",
    color: "#0f172a",
  },
  accountDetail: {
    fontSize: "12px",
    color: "#64748b",
  },
  accountChargeButton: {
    border: "none",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderRadius: "12px",
    padding: "9px 12px",
    fontWeight: 800,
    cursor: "pointer",
  },
  accountChargeButtonRestricted: {
    backgroundColor: "#94a3b8",
    cursor: "not-allowed",
  },
  chargePermissionBanner: {
    padding: "10px 12px",
    borderRadius: "12px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  accountStatus: {
    fontSize: "12px",
    fontWeight: 800,
    color: "#64748b",
  },
  customerField: {
    display: "grid",
    gap: "6px",
    maxWidth: "420px",
  },
  customerFieldLabel: {
    fontWeight: 800,
    color: "#0f172a",
  },
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background:
      "linear-gradient(160deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 52%, rgba(226,232,240,1) 100%)",
  },
  card: {
    width: "100%",
    maxWidth: "1180px",
    backgroundColor: "#ffffff",
    borderRadius: "24px",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    boxShadow: "0 22px 60px rgba(15, 23, 42, 0.12)",
    padding: "32px",
    display: "grid",
    gap: "22px",
  },
  branchBar: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "14px 16px",
    borderRadius: "18px",
    border: "1px solid #dbe5ef",
    background: "linear-gradient(135deg, #f8fafc 0%, #f0fdf4 100%)",
  },
  branchIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  branchStatusDot: {
    width: "10px",
    height: "10px",
    flex: "0 0 10px",
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    boxShadow: "0 0 0 5px rgba(34, 197, 94, 0.13)",
  },
  branchIdentityCopy: {
    display: "grid",
    gap: "2px",
    minWidth: 0,
  },
  branchLabel: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    fontWeight: 800,
  },
  branchName: {
    color: "#0f172a",
    fontSize: "18px",
    lineHeight: 1.2,
    overflowWrap: "anywhere",
  },
  signOutButton: {
    border: "1px solid #dbe5ef",
    backgroundColor: "rgba(255, 255, 255, 0.88)",
    color: "#475569",
    borderRadius: "999px",
    padding: "9px 14px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  branchActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  androidLauncherButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    borderRadius: "999px",
    padding: "9px 14px",
    fontSize: "13px",
    fontWeight: 800,
    cursor: "pointer",
    textDecoration: "none",
  },
  badge: {
    display: "inline-flex",
    width: "fit-content",
    padding: "8px 14px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft)",
    color: "var(--brand-primary)",
    fontWeight: 700,
    marginBottom: "14px",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 700,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 800,
    cursor: "pointer",
  },
  releaseTableButton: {
    border: "1px solid #fcd34d",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 800,
    cursor: "pointer",
  },
  closeTableButton: {
    border: "1px solid #22c55e",
    backgroundColor: "#ecfdf5",
    color: "#166534",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 800,
    cursor: "pointer",
  },
  errorBanner: {
    borderRadius: "14px",
    padding: "14px 16px",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  successBanner: {
    borderRadius: "14px",
    padding: "14px 16px",
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  alertsPanel: {
    borderRadius: "20px",
    padding: "18px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    display: "grid",
    gap: "14px",
  },
  alertsHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  alertsTitle: {
    color: "#9a3412",
    fontSize: "16px",
  },
  alertsMeta: {
    color: "#c2410c",
    fontSize: "13px",
    fontWeight: 700,
  },
  alertList: {
    display: "grid",
    gap: "10px",
  },
  alertCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "14px 16px",
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(251, 146, 60, 0.35)",
  },
  alertCardContent: {
    display: "grid",
    gap: "4px",
  },
  alertCardTitle: {
    color: "#7c2d12",
  },
  alertCardText: {
    color: "#9a3412",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  alertCardButton: {
    border: "1px solid var(--brand-primary)",
    borderRadius: "999px",
    padding: "11px 15px",
    backgroundColor: "var(--brand-primary)",
    color: "var(--brand-secondary)",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    borderRadius: "18px",
    padding: "26px",
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    textAlign: "center",
    fontWeight: 600,
  },
  salonSection: {
    display: "grid",
    gap: "18px",
    marginTop: "8px",
  },
  salonHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: "16px",
    flexWrap: "wrap",
  },
  salonHeaderCopy: {
    display: "grid",
    gap: "5px",
  },
  salonTitle: {
    color: "#0f172a",
    fontSize: "28px",
  },
  salonReadonly: {
    color: "#64748b",
    lineHeight: 1.5,
  },
  salonMeta: {
    padding: "10px 14px",
    borderRadius: "999px",
    backgroundColor: "#ecfdf5",
    color: "#166534",
    fontWeight: 800,
  },
  roomTabs: {
    display: "flex",
    gap: "10px",
    overflowX: "auto",
    paddingBottom: "4px",
  },
  roomTab: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "12px 16px",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  roomTabActive: {
    borderColor: "#f97316",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    boxShadow: "0 10px 22px rgba(249, 115, 22, 0.14)",
  },
  salonFloor: {
    position: "relative",
    minHeight: "600px",
    overflow: "hidden",
    border: "1px solid #dbe4ef",
    borderRadius: "28px",
    background:
      "radial-gradient(circle at top right, rgba(255,247,237,0.8), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  },
  salonFixture: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    display: "grid",
    placeItems: "center",
    padding: "8px",
    boxSizing: "border-box",
    border: "1px dashed #94a3b8",
    borderRadius: "18px",
    backgroundColor: "rgba(241, 245, 249, 0.92)",
    color: "#475569",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    pointerEvents: "none",
    zIndex: 1,
  },
  salonTable: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    padding: "8px",
    boxSizing: "border-box",
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)",
    transition: "box-shadow 0.18s ease, transform 0.18s ease",
    zIndex: 2,
  },
  salonTableFree: {
    backgroundColor: "#eef2f7",
    border: "2px solid #cbd5e1",
    color: "#334155",
  },
  salonTableOccupied: {
    backgroundColor: "#dcfce7",
    border: "2px solid #22c55e",
    color: "#166534",
  },
  salonTableReserved: {
    backgroundColor: "#ffedd5",
    border: "2px solid #f59e0b",
    color: "#9a3412",
  },
  salonTableReady: {
    backgroundColor: "#fef9c3",
    border: "2px solid #eab308",
    color: "#854d0e",
  },
  salonTableOwn: {
    boxShadow:
      "0 0 0 5px rgba(34, 197, 94, 0.16), 0 14px 28px rgba(15, 23, 42, 0.14)",
  },
  salonTableCallBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "19px",
    maxWidth: "calc(100% - 8px)",
    padding: "0 7px",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    color: "#b91c1c",
    boxShadow: "0 4px 10px rgba(127,29,29,0.2)",
    fontSize: "9px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  salonTableReadyBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "19px",
    maxWidth: "calc(100% - 8px)",
    padding: "0 7px",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    color: "#a16207",
    boxShadow: "0 4px 10px rgba(133,77,14,0.18)",
    fontSize: "9px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  salonTableLabel: {
    maxWidth: "100%",
    overflow: "hidden",
    color: "inherit",
    fontSize: "15px",
    fontWeight: 900,
    lineHeight: 1.1,
    textAlign: "center",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  salonTableStatus: {
    maxWidth: "100%",
    overflow: "hidden",
    color: "inherit",
    fontSize: "11px",
    fontWeight: 800,
    textAlign: "center",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  salonEmpty: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    color: "#64748b",
    fontWeight: 700,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.58)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    zIndex: 1000,
  },
  launcherOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "18px",
    backgroundColor: "rgba(15, 23, 42, 0.6)",
  },
  launcherDialog: {
    width: "min(720px, 100%)",
    display: "grid",
    gap: "22px",
    padding: "24px",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.3)",
  },
  waiterChargeDialog: {
    width: "min(620px, 100%)",
    maxHeight: "calc(100dvh - 24px)",
    boxSizing: "border-box",
    overflowY: "auto",
    display: "grid",
    gap: "18px",
    padding: "24px",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.32)",
  },
  waiterChargeHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
  },
  waiterChargeEyebrow: {
    display: "block",
    color: "#c2410c",
    fontSize: "11px",
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  waiterChargeTitle: {
    margin: "5px 0 2px",
    color: "#0f172a",
    fontSize: "25px",
    lineHeight: 1.1,
  },
  waiterChargeTable: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
  },
  waiterChargeSummary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "15px 17px",
    borderRadius: "16px",
    background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    color: "#9a3412",
    fontSize: "14px",
    fontWeight: 800,
  },
  waiterChargeMessage: {
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1e3a8a",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  waiterChargeMessageSuccess: {
    backgroundColor: "#ecfdf5",
    borderColor: "#86efac",
    color: "#166534",
  },
  waiterChargeMethods: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
  },
  waiterChargeMethod: {
    minHeight: "142px",
    display: "grid",
    alignContent: "space-between",
    gap: "10px",
    padding: "16px",
    borderRadius: "18px",
    textAlign: "left",
    cursor: "pointer",
  },
  waiterChargeCashMethod: {
    border: "1px solid #86efac",
    backgroundColor: "#f0fdf4",
    color: "#166534",
  },
  waiterChargeQrMethod: {
    border: "1px solid #93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1e3a8a",
  },
  waiterChargePointMethod: {
    border: "1px solid #c4b5fd",
    backgroundColor: "#f5f3ff",
    color: "#5b21b6",
  },
  waiterChargeMethodDisabled: {
    cursor: "not-allowed",
    opacity: 0.52,
    filter: "grayscale(0.28)",
  },
  waiterChargeQrPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 236px), 1fr))",
    alignItems: "center",
    gap: "20px",
    padding: "18px",
    border: "1px solid #bfdbfe",
    borderRadius: "20px",
    background: "linear-gradient(135deg, #f8fbff 0%, #eff6ff 100%)",
  },
  waiterChargeQrFrame: {
    display: "grid",
    placeItems: "center",
    minWidth: 0,
    maxWidth: "100%",
    minHeight: "236px",
    padding: "8px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbeafe",
  },
  waiterChargeQrCopy: {
    display: "grid",
    gap: "10px",
    color: "#1e3a8a",
    fontSize: "14px",
    lineHeight: 1.45,
  },
  waiterChargeRefreshButton: {
    width: "fit-content",
    border: "none",
    borderRadius: "12px",
    padding: "10px 13px",
    backgroundColor: "#1d4ed8",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  waiterChargeCancelButton: {
    width: "fit-content",
    border: "1px solid #fecaca",
    borderRadius: "12px",
    padding: "10px 13px",
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    fontWeight: 800,
    cursor: "pointer",
  },
  waiterChargeCancelConfirmation: {
    display: "grid",
    gap: "10px",
    padding: "15px 16px",
    borderRadius: "16px",
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    fontSize: "14px",
    lineHeight: 1.45,
  },
  waiterChargeCancelActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  waiterChargeCancelConfirmButton: {
    border: "none",
    borderRadius: "999px",
    padding: "11px 15px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  waiterChargeCompleted: {
    padding: "15px 16px",
    borderRadius: "16px",
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
    fontSize: "14px",
    fontWeight: 800,
  },
  waiterChargeFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },
  launcherHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
  },
  launcherEyebrow: {
    display: "block",
    color: "#c2410c",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
  },
  launcherTitle: {
    margin: "5px 0 0",
    color: "#0f172a",
    fontSize: "26px",
    lineHeight: 1.1,
  },
  launcherCloseButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "999px",
    padding: "9px 13px",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontWeight: 800,
    cursor: "pointer",
  },
  launcherContent: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    alignItems: "stretch",
    gap: "20px",
  },
  launcherQrCard: {
    display: "grid",
    justifyItems: "center",
    alignContent: "center",
    gap: "14px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fffaf5",
  },
  launcherQrFrame: {
    display: "grid",
    placeItems: "center",
    minWidth: "212px",
    minHeight: "212px",
    padding: "8px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
  },
  launcherInstructions: {
    display: "grid",
    alignContent: "start",
    gap: "14px",
    padding: "6px 0",
  },
  launcherInstructionsTitle: {
    color: "#0f172a",
    fontSize: "17px",
  },
  launcherSteps: {
    margin: 0,
    paddingLeft: "20px",
    display: "grid",
    gap: "9px",
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  launcherCallout: {
    padding: "13px 14px",
    borderRadius: "14px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1e3a8a",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  launcherLinkDisabled: {
    pointerEvents: "none",
    opacity: 0.55,
  },
  modalCard: {
    width: "100%",
    maxWidth: "1180px",
    maxHeight: "92dvh",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    borderRadius: "24px",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  modalCardMenuWorkspace: {
    width: "min(1440px, calc(100vw - 24px))",
    maxWidth: "calc(100vw - 24px)",
    height: "calc(100dvh - 24px)",
    maxHeight: "calc(100dvh - 24px)",
    boxSizing: "border-box",
  },
  tableCallPanel: {
    display: "grid",
    gap: "10px",
    padding: "14px",
    border: "1px solid #fca5a5",
    borderRadius: "16px",
    background: "linear-gradient(145deg, #fff7ed 0%, #fef2f2 100%)",
    boxShadow: "0 12px 28px rgba(185,28,28,0.12)",
  },
  tableCallHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  tableCallEyebrow: {
    display: "block",
    color: "#dc2626",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.1em",
  },
  tableCallTitle: {
    display: "block",
    marginTop: "3px",
    color: "#7f1d1d",
    fontSize: "16px",
  },
  tableCallCount: {
    display: "grid",
    placeItems: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
  },
  tableCallRequest: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "10px",
    borderTop: "1px solid rgba(248,113,113,0.3)",
    flexWrap: "wrap",
  },
  tableCallCopy: {
    display: "grid",
    gap: "3px",
    color: "#7f1d1d",
    fontSize: "13px",
  },
  tableCallButton: {
    minHeight: "40px",
    padding: "0 14px",
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: 900,
    cursor: "pointer",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  modalTitle: {
    margin: "8px 0 0",
    fontSize: "28px",
    color: "#0f172a",
  },
  modalSubtitle: {
    margin: "10px 0 0",
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "720px",
  },
  permissionBanner: {
    marginTop: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
    fontSize: "13px",
    fontWeight: 700,
    maxWidth: "720px",
  },
  orderLiveMeta: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "12px",
  },
  orderLiveChip: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "#ecfeff",
    color: "#155e75",
    fontSize: "12px",
    fontWeight: 800,
  },
  orderLiveText: {
    color: "#475569",
    fontSize: "12px",
    fontWeight: 600,
  },
  reservationCard: {
    display: "grid",
    gap: "4px",
    marginTop: "12px",
    padding: "12px 14px",
    border: "1px solid #f59e0b",
    borderRadius: "16px",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
  },
  orderWorkspaceTabs: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
    padding: "4px",
    backgroundColor: "#edf3f9",
    border: "1px solid #d7e1ed",
    borderRadius: "16px",
    flexShrink: 0,
  },
  orderWorkspaceTab: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "transparent",
    color: "#5e6f87",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  orderWorkspaceTabActive: {
    backgroundColor: "#ffffff",
    color: "#0b1b38",
    boxShadow: "0 8px 24px rgba(15, 34, 58, 0.10)",
  },
  orderWorkspaceCount: {
    display: "inline-flex",
    minWidth: "24px",
    height: "24px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    backgroundColor: "#e8f1ff",
    color: "#175be8",
    fontSize: "12px",
  },
  hiddenWorkspacePanel: {
    display: "none",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gridTemplateRows: "minmax(0, 1fr)",
    gap: "18px",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
  },
  modalPanel: {
    display: "grid",
    alignContent: "start",
    gap: "14px",
    padding: "18px",
    borderRadius: "20px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    minWidth: 0,
    minHeight: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
    overflowY: "auto",
    overscrollBehaviorY: "contain",
    WebkitOverflowScrolling: "touch",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  sectionTitle: {
    color: "#0f172a",
  },
  sectionMeta: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
  },
  quickSection: {
    display: "grid",
    gap: "10px",
    marginBottom: "14px",
  },
  quickSectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  quickSectionTitle: {
    color: "#0f172a",
    fontSize: "14px",
  },
  quickSectionMeta: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
  },
  quickCardList: {
    display: "grid",
    gap: "8px",
  },
  waiterProductDetailCard: {
    display: "grid",
    gap: "14px",
    marginTop: "14px",
    marginBottom: "14px",
    padding: "16px",
    borderRadius: "18px",
    border: "1px solid #cfe1ff",
    background:
      "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(239,246,255,0.95) 100%)",
  },
  waiterProductDetailHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  waiterProductDetailTitleWrap: {
    display: "grid",
    gap: "4px",
  },
  waiterProductDetailTitle: {
    color: "#0f172a",
    fontSize: "22px",
    lineHeight: 1.1,
  },
  waiterProductDetailSubtitle: {
    color: "#64748b",
    fontSize: "14px",
  },
  waiterSummaryRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  waiterSummaryChip: {
    borderRadius: "999px",
    padding: "7px 12px",
    backgroundColor: "#e2e8f0",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 800,
  },
  waiterIngredientList: {
    display: "grid",
    gap: "10px",
  },
  waiterIngredientRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "14px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe3ee",
  },
  waiterIngredientInfo: {
    display: "grid",
    gap: "8px",
    minWidth: 0,
    flex: "1 1 320px",
  },
  waiterIngredientNameRow: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  waiterIngredientName: {
    color: "#0f172a",
    fontSize: "16px",
  },
  waiterIngredientState: {
    color: "#475569",
    fontSize: "12px",
    fontWeight: 700,
  },
  waiterIngredientMetaRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  waiterIngredientMeta: {
    color: "#64748b",
    fontSize: "12px",
  },
  waiterIngredientControls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  waiterFixedChip: {
    borderRadius: "999px",
    padding: "8px 12px",
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontSize: "12px",
    fontWeight: 800,
  },
  waiterLinkedChoiceWrap: {
    display: "grid",
    gap: "12px",
    paddingTop: "4px",
  },
  waiterLinkedChoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  waiterLinkedChoiceTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  waiterLinkedChoiceText: {
    marginTop: "4px",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.45,
  },
  waiterLinkedChoiceList: {
    display: "grid",
    gap: "10px",
  },
  waiterLinkedChoiceButton: {
    width: "100%",
    borderRadius: "16px",
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    textAlign: "left",
    cursor: "pointer",
  },
  waiterLinkedChoiceButtonActive: {
    borderColor: "var(--brand-primary)",
    boxShadow: "0 0 0 1px var(--brand-primary) inset",
    backgroundColor: "rgba(59,130,246,0.06)",
  },
  waiterLinkedChoiceProductName: {
    color: "#0f172a",
    fontSize: "15px",
  },
  waiterLinkedChoiceProductDescription: {
    marginTop: "4px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  waiterLinkedChoicePrice: {
    color: "#0f172a",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  waiterDetailActions: {
    display: "grid",
    gap: "12px",
  },
  waiterDetailTools: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  waiterDetailQtyWrap: {
    display: "grid",
    gap: "8px",
  },
  waiterDetailQtyLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  waiterDetailAmountWrap: {
    display: "grid",
    gap: "6px",
    justifyItems: "end",
  },
  waiterDetailAmountValue: {
    color: "var(--brand-primary)",
    fontSize: "22px",
    lineHeight: 1,
  },
  waiterDetailButtonRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  quickCardButton: {
    width: "100%",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    borderRadius: "14px",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    textAlign: "left",
    cursor: "pointer",
  },
  quickCardInfo: {
    display: "grid",
    gap: "4px",
  },
  quickCardText: {
    fontSize: "12px",
    color: "#64748b",
  },
  quickCardAction: {
    fontSize: "12px",
    fontWeight: 800,
    color: "var(--brand-primary)",
  },
  orderItemList: {
    display: "grid",
    gap: "10px",
  },
  orderItemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe3ee",
    flexWrap: "wrap",
  },
  orderItemInfo: {
    display: "grid",
    gap: "4px",
  },
  orderItemMeta: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
  },
  orderItemActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  qtyControls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  qtyButton: {
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  qtyValue: {
    minWidth: "18px",
    textAlign: "center",
    fontWeight: 800,
    color: "#0f172a",
  },
  removeButton: {
    border: "1px solid #fecaca",
    backgroundColor: "#fff5f5",
    color: "#b91c1c",
    borderRadius: "999px",
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  editButton: {
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: "999px",
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  inlineFilters: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 220px) minmax(0, 1fr)",
    gap: "10px",
  },
  input: {
    minHeight: "44px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    padding: "0 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
  },
  productResultList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "10px",
    maxHeight: "360px",
    overflow: "auto",
  },
  menuWorkspacePanel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    // Match modalPanel's longhand so React restores scrolling after browsing.
    overflowY: "hidden",
  },
  productResultListExpanded: {
    flex: "1 1 0",
    height: "100%",
    minHeight: 0,
    maxHeight: "none",
    alignContent: "start",
    paddingRight: "4px",
  },
  productResultButton: {
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "10px",
    display: "grid",
    gridTemplateColumns: "76px minmax(0, 1fr) auto",
    gap: "12px",
    alignItems: "center",
    textAlign: "left",
    cursor: "pointer",
  },
  productResultImage: {
    width: "76px",
    height: "76px",
    borderRadius: "12px",
    objectFit: "cover",
    backgroundColor: "#f1f5f9",
  },
  productResultFallback: {
    width: "76px",
    height: "76px",
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    backgroundColor: "#f1f5f9",
    color: "#64748b",
    fontWeight: 800,
  },
  productResultInfo: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
  },
  productResultMeta: {
    color: "#64748b",
    fontSize: "13px",
  },
  productResultPrice: {
    color: "#0f172a",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
    flexShrink: 0,
  },
  modalTotal: {
    color: "#0f172a",
    fontSize: "18px",
  },
  modalFooterActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  primaryButton: {
    border: "1px solid var(--brand-primary)",
    backgroundColor: "var(--brand-primary)",
    color: "var(--brand-secondary)",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 700,
    cursor: "pointer",
  },
  tableCard: {
    appearance: "none",
    aspectRatio: "1 / 1",
    borderRadius: "22px",
    background:
      "linear-gradient(160deg, rgba(15,23,42,1) 0%, rgba(30,41,59,1) 100%)",
    color: "#f8fafc",
    padding: "20px",
    border: "1px solid rgba(148,163,184,0.24)",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "left",
    cursor: "pointer",
  },
  tableCardAssigned: {
    background:
      "linear-gradient(160deg, rgba(2,132,199,1) 0%, rgba(14,116,144,1) 100%)",
    boxShadow: "0 16px 32px rgba(14, 116, 144, 0.24)",
  },
  tableCardBusy: {
    background:
      "linear-gradient(160deg, rgba(120,53,15,1) 0%, rgba(146,64,14,1) 100%)",
    boxShadow: "0 16px 32px rgba(146, 64, 14, 0.22)",
  },
  tableOverline: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#93c5fd",
    fontWeight: 700,
  },
  tableTitle: {
    fontSize: "28px",
    lineHeight: 1.05,
  },
  tableFoot: {
    fontSize: "13px",
    fontWeight: 700,
    color: "rgba(226,232,240,0.92)",
  },
};

export function PanelMozoStandaloneDemo() {
  const demoAuth = useMemo(() => buildStandaloneWaiterDemoAuth(), []);
  return <PanelMozo authOverride={demoAuth} />;
}

export default PanelMozo;
